/**
 * ECH dynamic fill — port of the monolith's ECH_DYN contract:
 *   ECH_DYN / ECH_CTA_PAIRS (index.html L10804–10924)
 *   __echSrcOf / __echIsSeed / __echSrcOk / __echDynFor (L10930–10933)
 *   __echLevel (L11200–11211) · __echScopeUrl (L11364–11376)
 *   __echDynVal (L11534–11658) · __ctaUrlFor (L11336–11363)
 *   __socIsOff / __socSetOff (L11833–11840)
 *   __echDynFill (L11841–11886) · __echFillAll (L11776–11820)
 *
 * THE BUG THIS FIXES (audit row 19). In the repo, fill happened only inside the
 * open Inspector, only on an explicit button click, and only for the selected
 * block — so the canvas sat on the blocks' shipped placeholders until each one
 * was clicked. Jack, 2026-08-19: "logo still isn't auto pulling from the first
 * tab… this should auto happen before I select an individual block."
 * `echFillAll()` walks the WHOLE document, so picking the division / focus /
 * community on Step 1 populates every dynamic block at once.
 *
 * This module is PURE — no fetch, no DOM. Everything the resolver needs arrives
 * in an `EchCtx`, which `useEchDyn.ts` assembles from the worker feeds. That is
 * the one structural departure from the monolith, which reads `$("divisionCode")`
 * and `window.__commSel` straight out of the page; the values and the precedence
 * order are unchanged.
 */
import type { CommunityPackResponse, PlanInfo, HomesiteInfo } from "../types/api";
import { echNameOf, echMeta, echByName, echTypes, echAmenLib, echPickMode } from "./echRegistry";
import { ECH_IFFIELD_ON, ECH_IFFIELD_OFF } from "./echIfField";
import { echCityState } from "./cityState";
import type { DataSource } from "./guardrails";
import { contactRewrite, withScheme, echStripUnresolved } from "./echContact";
import { contactLookup, contactTreatmentHtml, contactAltGaps, contactTreated, CONTACT_IMAGES_BEGIN, CONTACT_IMAGES_END } from "./echContactTreatment";
import { cuLookup, cuState, type CuState } from "./echContactChoices";
/* `homeKey` is the repo's canonical STABLE home identifier — "<code>|h|<addr>|
   <price>", no index in it — and it is what the picker already puts in its own
   `picks.homes`. Imported rather than re-spelled here so the read side of
   `__homePick` and the write side can never drift apart. dynamicFill reaches only
   types/api + email/core, so this adds no cycle. */
import { homeKey, type Pack, type FillLevel } from "./dynamicFill";
import { orderHomesites } from "./echOrder";
import { bynderCardUrl } from "./echBynderMatch";
import { echExpandListingHtml } from "./echExpand";
import { htmlRowNumbers, rowApplyExtraColls, EXTRA_COLLS } from "./echRows";
import { AMEN_MIN, amenApply, amenFromAirtable, amenPairs, amenPicked } from "./echAmen";

/** one entry in ECH_DYN: a bare source string, or a source with constraints */
export interface DynSource {
  src: string;
  /** which `echLevel()` values this field may fill at — null means all of them */
  levels?: string[] | null;
  /**
   * The feed OFFERS this value rather than owning it — approved copy the requester
   * is expected to edit. A seed stays editable and is only written while the field
   * is untouched (empty, or still the block's shipped default). Anything else the
   * feed writes is authoritative. (monolith 2026-08-19)
   */
  seed?: boolean;
  /**
   * The block MAY declare this field. An `opt` entry fills only when the block's
   * declared fields carry the key; on a block that does not declare it the fill
   * skips it entirely, so the map can be wired ahead of a Content Builder edit
   * without writing data a template has no token for. Added 2026-09-18 for the
   * Featured card price (pilot call: collection-level cards ship with no price
   * because ECH_Featured1CTA/2CTA declare none) — the app side is ready the day
   * Layne publishes the `Price` field.
   */
  opt?: boolean;
}
type DynEntry = string | DynSource;

/* which LABEL field decides each URL field's shape, per block. Keys verified
   against each block's own guardrail (monolith L10802–10813). */
export const ECH_CTA_PAIRS: Record<string, Record<string, string>> = {
  ECH_2CTA: { btn1Url: "btn1Label", btn2Url: "btn2Label" },
  ECH_1CTA: { btnUrl: "btnLabel" },
  ECH_GOTY1CTA: { CtaUrl: "CtaLabel" },
  ECH_GOInvite1CTA: { CtaUrl: "CtaLabel" },
  ECH_GOInvite2CTA: { cta1Url: "cta1Label", cta2Url: "cta2Label" },
  ECH_GOTY2CTA: { cta1Url: "cta1Label", cta2Url: "cta2Label" },
  /* the Featured Plan blocks carry buttons too — their labels resolve against the
     PLAN first (see ctaUrlFor), then fall back to the community answers.
     ECH_Featured1CTA WAS MISSING (2026-08-20), while this comment claimed both of
     them: its one URL field is named `FloorplanUrl` rather than `btnUrl`, so it read
     as a plain data field and stayed pinned to `plan.self.url` whatever the button
     said. Pick "Schedule a Tour" on that block and the button went to the floorplan
     page — a label that lies about its destination. Its own guardrail confirms the
     field is the button's href, not just a datum: "The floorplan URL fills the image
     link, the Outlook (VML) button link, and the standard button link at once."
     Same shape as ECH_Featured2CTA's `btn1Url`, which the block labels "Button 1 URL
     (also used for the image link)" and which has been label-driven all along. The
     default label "View Floorplan" still resolves to `plan.self.url`, so nothing
     changes until someone picks a different CTA. */
  ECH_Featured1CTA: { FloorplanUrl: "BtnLabel" },
  ECH_Featured2CTA: { btn1Url: "btn1Label", btn2Url: "btn2Label" },
};

/* ═══════════════════════════════════════════════════════════════════════════════
   THE FEATURE LEVEL DRIVES THE FILL — ONE TABLE, THREE LEVELS. 2026-08-20.
   ═══════════════════════════════════════════════════════════════════════════════

   JACK, ON THE FEATURED PLAN BLOCK'S DYNAMIC PANE: "i do want this to be dynamic
   where it can be used for communities, floor plan or homesites… should be dynamic."

   THE BUG. The stepped wizard has offered three FEATURE LEVELS since it was ported
   (DynFeaturePicker's `dynp-lvlseg`: Community / Floorplans / Homesites, the
   monolith's `window.__dyn.secLevel`, index.html L10257) and it hands the chosen one
   back on `DynPickResult.level`. The ECH fill THREW IT AWAY: `ECH_DYN`'s Featured
   entries mapped every field to `plan.self.*` and nothing else, so picking Homesites
   or Community stored a pick the resolver had no source for and the canvas did not
   move. That is the "two renderings of the same data disagree" fault this codebase
   has a standing rule about — the selected-cards panel showed the chosen home while
   the email still showed a floorplan.

   THE SHAPE OF THE FIX, AND WHY IT IS ONE TABLE AND NOT A SECOND MAPPER. Exactly one
   place is allowed to say "at level X, field Y comes from source Z" — this one. Two
   consumers read it and neither restates it:

     · `echDynPatch` / `echDynFor(name, feat)` — the FIELD fill;
     · `ctaUrlFor` — the BUTTON, whose destination is chosen by its LABEL and whose
       label-to-source mapping obviously has to move with the level (a "View Home"
       button on a Featured home must point at the home, not at a floorplan).

   `ECH_DYN`'s two Featured rows are the `plans` row OF THIS TABLE, BY REFERENCE —
   not a copy. A copy is a second thing to keep in step with Content Builder, and
   this file has already watched `ECH_CHOICES` and `ECH_CTA_PAIRS` drift apart twice.
   The test `ECH_DYN.ECH_Featured1CTA === ECH_FEAT.plans.fields.ECH_Featured1CTA`
   pins it.

   WHY THE THREE LEVELS DECLARE DIFFERENT NUMBERS OF FIELDS. THE MAP IS THE CONTRACT
   (see `ECH_DYN`'s header): a field the level omits is left exactly as the requester
   or the template left it, because `echDynPatch` only walks `Object.keys(map)`. That
   is the whole mechanism behind the community row below, and it is deliberate — a
   community has no beds, no baths and no square footage, so the honest answers are
   "don't touch it", never a number and never "".

   NOT COVERED HERE, ON PURPOSE: `ECH_ListingTable`. It has exactly one level — its
   ten rows ARE homesites — so it stays in `ECH_DYN` and `echDynFor` falls back to
   that map at every level. Nothing writes a level on it. (The monolith's listing
   section DOES support all three via `__dynRowsForLevel`, but its rows are a
   generated pipe-delimited string a hand-written renderer parses; Layne's block
   declares 70 fixed `home<N>*` tokens, so a comms/plans listing would be a different
   token set, not a different source. Separate piece of work.) */

/** the three FEATURE LEVELS, spelled exactly as the wizard's own `FillLevel` */
export type EchFeat = FillLevel; /* "comms" | "plans" | "homes" */

/** one level's whole contract: what to call it, what it needs picked, where it reads */
export interface EchFeatSpec {
  /** the pane's noun and the section head's word — the monolith's `__dynCardTitle`
   *  vocabulary (Featured Communities / Featured Floorplans / Featured Homes) */
  label: string;
  /**
   * which stored pick this level consumes: `__planPick`, `__homePick`, or neither.
   * `""` for comms — Step 1 already chose the community, so there is nothing left to
   * pick and no pick key to write. The Inspector reads this rather than deciding for
   * itself which key a level uses.
   */
  pick: "plan" | "home" | "";
  /** blockName → field → source. THE mapping. */
  fields: Record<string, Record<string, DynEntry>>;
  /**
   * WHAT A FEATURED BLOCK'S CTA LABEL RESOLVES AGAINST AT THIS LEVEL. `ctaUrlFor`
   * knows three intents off the label — the thing's own page, its tour, its included
   * features — and only the SOURCE changes with the level. `""` means "this level has
   * no such source": the button then falls through to the community-level answers
   * (the community page, `/schedule-tour`, `#join-interest-list`) exactly as it does
   * on a non-Featured block, rather than inventing a link. A homesite carries no
   * Matterport tour and no Everything's Included page in `/sfmc-content`, and a
   * community carries neither — hence the empties.
   */
  cta: { url: string; tour: string; ei: string };
}

/* the FLOORPLAN level's two rows — today's shipped behaviour, unchanged. Extracted
   into a named const only so `ECH_DYN` below can hold the SAME OBJECT. */
/* ── A FEATURED FLOORPLAN FILLS AT EVERY LEVEL (2026-08-24, Jack: "how come we're not
   auto populating … should auto pull in one per community if there's more than one
   community or area of interest level or higher … if it's just one community should pull
   in a max of 3") ─────────────────────────────────────────────────────────────────────
   These seven fields were gated to `levels: ["community"]`, so `echSrcOk` skipped every
   one of them the moment Step 1 named a second community (which resolves to `market`) or
   set the focus to Area of Interest / Market / Division — and the card shipped Layne's
   "Plan Name · 1,234 sq ft · 3 beds · 2.5 baths" seed. The gate is now every scope the
   form can produce; WHICH plan each card shows is decided in `echPlanScope` below, not
   here, because that is a per-card question and this is a per-field one. */
const PLAN_LEVELS = ["community", "aoi", "market", "division"];
const FEAT1_PLANS: Record<string, DynEntry> = {
  PlanName: { src: "plan.self.name", levels: PLAN_LEVELS },
  PlanImage: { src: "plan.self.image", levels: PLAN_LEVELS },
  PlanImageAlt: { src: "plan.self.alt", levels: PLAN_LEVELS },
  Beds: { src: "plan.self.beds", levels: PLAN_LEVELS },
  Baths: { src: "plan.self.baths", levels: PLAN_LEVELS },
  SqFt: { src: "plan.self.sqft", levels: PLAN_LEVELS },
  Price: { src: "plan.self.price", levels: PLAN_LEVELS, opt: true },
  FloorplanUrl: { src: "plan.self.url", levels: PLAN_LEVELS },
};
const FEAT2_PLANS: Record<string, DynEntry> = {
  PlanName: { src: "plan.self.name", levels: PLAN_LEVELS },
  PlanImage: { src: "plan.self.image", levels: PLAN_LEVELS },
  PlanImageAlt: { src: "plan.self.alt", levels: PLAN_LEVELS },
  Beds: { src: "plan.self.beds", levels: PLAN_LEVELS },
  Baths: { src: "plan.self.baths", levels: PLAN_LEVELS },
  SqFt: { src: "plan.self.sqft", levels: PLAN_LEVELS },
  Price: { src: "plan.self.price", levels: PLAN_LEVELS, opt: true },
  btn1Url: "community.pageurl",
  btn2Url: "community.pageurl",
};

/* the HOMESITE level. THE SAME DECLARED FIELDS, A DIFFERENT SOURCE OBJECT — Layne's
   markup does not change, so a Featured HOME still fills PlanName / PlanImage /
   Beds / Baths / SqFt / the URL. `home.self.*` resolves through the very same
   `echHomeIn` path the Listing table uses, and `self` means "whichever Featured block
   this is" exactly as it does for plans.

   `PlanName` ← `home.self.name`, WHICH IS THE ADDRESS. A homesite's identity is WHERE
   IT IS: two homes in one community routinely share a plan, so the plan name cannot
   distinguish them, and the monolith agrees — its Featured-card title at homes level
   is `hm.addr||hm.plan||"Home"` (index.html `__dynItemsForLevel`), and the picker
   card is `hm.addr||hm.plan||"Home"` too (`echDynPick.groupedCards`). The plan name is
   still on the card implicitly, through the beds/baths/sqft trio. `home.self.name`
   falls back to the plan name when a row has no street address (a real case in the
   feed) and to "" when it has neither — never to a made-up "Home", because a fill
   that invents a label is a fill that lies in a live send.

   `PlanImageAlt` ← the same `home.self.name`. Zero invention: the alt text for a
   photograph of 202 Oak is "202 Oak". (`plan.self.alt` appends " floorplan" because a
   plan image IS a floorplan drawing; a home image is a photo of the home.) */
/* ── THE HOMES FIELDS REACH EVERY SCOPE TOO (2026-09-11) ─────────────────────────────
   The thirteen fields below were still gated to `levels: ["community"]` — the exact gate
   the PLAN fields lost on 2026-08-24 (see PLAN_LEVELS above). So a homes card on any send
   that resolves to market/aoi/division — two communities picked, Market focus, an Area
   of Interest — was skipped by `echSrcOk` and shipped Layne's seed. Lauren's one homesite
   card under a ten-home listing (Teams, 2026-09-11 1:54 PM) is the single-community half
   of this; the multi-community half never filled at all. WHICH home each card shows is
   `echHomeCardFallback`'s job (round-robin across the communities), not this gate's. */
const FEAT1_HOMES: Record<string, DynEntry> = {
  PlanName: { src: "home.self.name", levels: PLAN_LEVELS },
  PlanImage: { src: "home.self.img", levels: PLAN_LEVELS },
  PlanImageAlt: { src: "home.self.name", levels: PLAN_LEVELS },
  Beds: { src: "home.self.beds", levels: PLAN_LEVELS },
  Baths: { src: "home.self.baths", levels: PLAN_LEVELS },
  SqFt: { src: "home.self.sqft", levels: PLAN_LEVELS },
  Price: { src: "home.self.price", levels: PLAN_LEVELS, opt: true },
  FloorplanUrl: { src: "home.self.url", levels: PLAN_LEVELS },
  /* Next Gen logo: Yes when the homesite is a Next Gen home (Sitecore flag) — see echYesNo */
  ShowNextGen: { src: "home.self.nextgen", levels: PLAN_LEVELS, opt: true },
};
const FEAT2_HOMES: Record<string, DynEntry> = {
  PlanName: { src: "home.self.name", levels: PLAN_LEVELS },
  PlanImage: { src: "home.self.img", levels: PLAN_LEVELS },
  PlanImageAlt: { src: "home.self.name", levels: PLAN_LEVELS },
  Beds: { src: "home.self.beds", levels: PLAN_LEVELS },
  Baths: { src: "home.self.baths", levels: PLAN_LEVELS },
  SqFt: { src: "home.self.sqft", levels: PLAN_LEVELS },
  Price: { src: "home.self.price", levels: PLAN_LEVELS, opt: true },
  ShowNextGen: { src: "home.self.nextgen", levels: PLAN_LEVELS, opt: true },
  /* label-driven through ECH_CTA_PAIRS, same as at plans level — the src here is the
     honest documentation of where the button goes and the fallback if the pair is
     ever removed */
  btn1Url: "community.pageurl",
  btn2Url: "community.pageurl",
};

/* the COMMUNITY level. THREE FIELDS FILL AND THREE DELIBERATELY DO NOT.
   name / hero photo / community page all exist on the community itself — `hero` is a
   real field on `/sfmc-content`'s `CommunityInfo` and is what the wizard already
   paints its community cards with (DynFeaturePicker: `pk.C.community.hero`).

   BEDS, BATHS AND SQFT ARE ABSENT, AND THAT IS THE POINT. A community has no bed
   count. Writing "" would blank three cells in a layout whose own guardrail says the
   "spec block structure is locked: square feet, line break, beds, line break, baths.
   Do not add, remove, or reorder lines" — an empty spec line is a visibly broken
   card. Writing a plausible number would put "3 beds" on a Featured COMMUNITY card,
   which is simply false, and this codebase's standing rule is that a demo/sample
   value you have not observed is never invented. So the map omits them, `echDynPatch`
   never touches them, and they stay at whatever the template or the requester left —
   which the requester can see and edit, because ECH fields stay on screen in Dynamic
   mode. Jack's own call on this shape: "do not invent values."

   `PlanImageAlt` ← `community.name`, for the same zero-invention reason as the homes
   row: the alt text for a photo of Elevon is "Elevon". */
/* the three spec lines become the community's own facts — city/state, from-price,
   available-home count (Jack, 2026-08-21: "should adapt to each level"; the content
   is his approved AOI-mockup card line for line). The unit labels clear via the
   `none` source — they are declared editable by the block's own guardrail — and
   `echFeatRestore` puts the template's "sq ft / beds / baths" back on a switch to
   the other levels. Every value is a real feed number: nothing is invented. */
/** the six group-written fields below — the per-field loop must skip them so a
 *  half-answerable pack can never ship "Lavon, TX · — · —" on a locked layout */
export const COMMS_LINE_KEYS = new Set(["SqFt", "SqFtLabel", "Beds", "BedsLabel", "Baths", "BathsLabel"]);
const COMMS_LINES: Record<string, DynEntry> = {
  SqFt: { src: "community.citystate", levels: ["community"] },
  SqFtLabel: { src: "none", levels: ["community"] },
  Beds: { src: "community.fromprice", levels: ["community"] },
  BedsLabel: { src: "none", levels: ["community"] },
  Baths: { src: "community.availcount", levels: ["community"] },
  BathsLabel: { src: "none", levels: ["community"] },
};
const FEAT1_COMMS: Record<string, DynEntry> = {
  PlanName: { src: "community.name", levels: ["community"] },
  PlanImage: { src: "community.hero", levels: ["community"] },
  PlanImageAlt: { src: "community.name", levels: ["community"] },
  FloorplanUrl: { src: "community.pageurl", levels: ["community"] },
  ...COMMS_LINES,
};
const FEAT2_COMMS: Record<string, DynEntry> = {
  PlanName: { src: "community.name", levels: ["community"] },
  PlanImage: { src: "community.hero", levels: ["community"] },
  PlanImageAlt: { src: "community.name", levels: ["community"] },
  btn1Url: "community.pageurl",
  btn2Url: "community.pageurl",
  ...COMMS_LINES,
};

export const ECH_FEAT: Record<EchFeat, EchFeatSpec> = {
  plans: {
    label: "Floorplans",
    pick: "plan",
    fields: { ECH_Featured1CTA: FEAT1_PLANS, ECH_Featured2CTA: FEAT2_PLANS },
    cta: { url: "plan.self.url", tour: "plan.self.tour", ei: "plan.self.ei" },
  },
  homes: {
    label: "Homes",
    pick: "home",
    fields: { ECH_Featured1CTA: FEAT1_HOMES, ECH_Featured2CTA: FEAT2_HOMES },
    /* ── "SCHEDULE A TOUR" ON A HOMESITE GOES TO THAT HOMESITE'S TOUR PAGE (2026-09-08) ──
       Lauren, Teams: "the homesite link is populated which is great but the schedule a
       tour takes them to the division page… can we link to the schedule a tour page for
       the specific homesite?" It fell through to the community-level answer, and at
       market scope that is the division page. lennar.com serves a tour form at
       <homesite url>/schedule-tour — verified live on
       …/arbor-collection/litchfield-ii/25086616025/schedule-tour ("Touring Sweetgrass
       at Summers Corner · Request a tour"), the same form the community page offers,
       reached from the homesite's own path. `home.self.tour` derives it; a home whose
       URL is not a lennar.com listing has no tour page and falls through as before.
       Everything's Included stays empty — a homesite has none of its own. */
    cta: { url: "home.self.url", tour: "home.self.tour", ei: "" },
  },
  comms: {
    label: "Communities",
    pick: "",
    fields: { ECH_Featured1CTA: FEAT1_COMMS, ECH_Featured2CTA: FEAT2_COMMS },
    /* every intent falls through to the community-level answers, which is exactly
       right for a Featured Community card: the community page, and its
       /schedule-tour and #join-interest-list suffixes */
    cta: { url: "", tour: "", ei: "" },
  },
};

/**
 * THE NEXT GEN LOGO ON A FEATURED CARD (2026-10-29, Layne + Jack).
 *
 * Both Featured blocks wrap the logo row in <!--IFFIELD:ShowNextGen--> and declare a
 * yes/no `ShowNextGen` field. At HOMES level the fill answers it from the homesite's
 * own Next Gen flag (`HomesiteInfo.nextGen`, Sitecore), so a Next Gen home shows the
 * logo with nobody touching it. Plans and communities have no such flag, so there it
 * is the author's tick, and `echFeatRestore` puts the block's default back when a card
 * moves off homes level unless the author set it themselves.
 *
 * The answer is spelled the way the BLOCK spells it — the first declared option that
 * reads as yes / no — so the panel's buttons light up whatever words Layne picks.
 * "Yes" / "No" when the block declares no options.
 */
export function echYesNo(m: DynBlock | undefined, k: string, on: boolean): string {
  const rule = m ? (echMeta(m.type)?.declared || {})[k] as { options?: readonly unknown[] } | undefined : undefined;
  const re = on ? ECH_IFFIELD_ON : ECH_IFFIELD_OFF;
  const hit = (rule && rule.options ? rule.options : []).map((o) => String(o)).find((o) => re.test(o.trim()));
  return hit != null ? hit : on ? "Yes" : "No";
}

/** the block-data key holding this block's FEATURE LEVEL — one of `EchFeat` */
export const ECH_FEAT_KEY = "__featLevel";

/**
 * A FIELD THIS LEVEL DOESN'T SOURCE, BUT ANOTHER LEVEL DOES, GOES BACK TO THE
 * TEMPLATE'S OWN VALUE. 2026-08-20 — found by driving the real path in Chrome, not by
 * reading the code.
 *
 * THE DEFECT. `ECH_FEAT.comms` deliberately omits Beds / Baths / SqFt: a community has
 * no bed count, so the fill writes nothing there. On a FRESH block that is exactly
 * right — the fields hold the template's shipped "3 / 2.5 / 1,234" and the requester
 * edits them. But switch a block Floorplans → Homesites → Community and "nothing
 * written" means the HOME's numbers stay on the card: the Featured Plan block read
 * "Village - Timber Ridge · 1,837 sq ft · 3 beds · 2 baths" — the community's name over
 * 2273 Linden Pass's specs. A stale value from the previous level, presented as this
 * level's answer.
 *
 * This is the same fault `echDynPatch`'s "DYNAMIC IS AUTHORITATIVE, INCLUDING WHEN IT
 * RESOLVES TO NOTHING" comment exists to prevent (switch Step 1 from Market to
 * Community and the header still said "Atlanta"), one axis over.
 *
 * WHY THE SHIPPED DEFAULT AND NOT "". Blanking is the usual honest answer, and here it
 * is not available: ECH_Featured1CTA's own guardrail says the "spec block structure is
 * locked: square feet, line break, beds, line break, baths. Do not add, remove, or
 * reorder lines", so an empty spec line is a visibly broken card in a live send. And
 * inventing a number is out of the question. The template's own declared default is the
 * one value that is neither invented nor stale — it is what a brand-new block of this
 * type says, and it is what the requester sees in the panel and can edit.
 *
 * THE SET IS DERIVED FROM THE ONE TABLE, never listed by hand: every field ANY level
 * sources for this block, minus the fields THIS level sources. So adding a field to one
 * level automatically makes it restorable at the others, and nothing has to be kept in
 * step. A block the table does not cover (the Listing table, the Logo, …) has an empty
 * union and this is a no-op.
 *
 * A FIELD THE USER TOOK OVER IS LEFT ALONE (`socIsOff`), same as in the fill: if they
 * typed the square footage themselves, a level switch is not licence to overwrite it.
 */
export function echFeatRestore(m: DynBlock, name: string, feat: string): Record<string, string> {
  const out: Record<string, string> = {};
  const mine = ECH_FEAT[feat as EchFeat]?.fields[name];
  if (!mine) return out;                       /* the table doesn't cover this block */
  const declared = echMeta(m.type)?.declared || {};
  const seen: Record<string, 1> = {};
  (Object.keys(ECH_FEAT) as EchFeat[]).forEach((l) => {
    const map = ECH_FEAT[l].fields[name];
    if (!map) return;
    Object.keys(map).forEach((k) => {
      if (mine[k] !== undefined || seen[k]) return;   /* this level owns it, or done */
      seen[k] = 1;
      if (socIsOff(m, k)) return;                     /* the requester took it over */
      const def = String(declared[k]?.default ?? "");
      if (String(m.data[k] == null ? "" : m.data[k]) !== def) out[k] = def;
    });
  });
  return out;
}

/**
 * THIS BLOCK'S FEATURE LEVEL, AND IT FALLS BACK RATHER THAN BLANKING.
 *
 * Stored as a STABLE STRING, not an index — the `__planPick` precedent, and for the
 * same reason: an index is a promise about an array that the next refactor breaks,
 * whereas "homes" means "homes" forever. Anything unreadable (a hand-edited document,
 * a level a future build renames) resolves to the BLOCK'S OWN DEFAULT rather than to
 * nothing, so a Featured card can never end up with no map and no fill.
 *
 * The default is `echPickMode`'s answer, not a second copy of that rule: a block
 * declaring `home1Homesite` is a homes block, one declaring `PlanName` is a plans
 * block. That keeps the Listing table at homes and the Featured blocks at plans —
 * the monolith's own `secLevel` defaults, `{fp:"plans", lst:"homes"}` (index.html
 * L10257).
 */
export function echFeatOf(m: DynBlock | null | undefined): EchFeat {
  const raw = String((m && m.data && m.data[ECH_FEAT_KEY]) || "").trim();
  if (raw === "plans" || raw === "homes" || raw === "comms") return raw;
  return echPickMode(echMeta(String(m?.type || ""))?.declared || {}) === "rows" ? "homes" : "plans";
}
/** the pane's noun for a level — "Floorplans" / "Homes" / "Communities" */
export const echFeatLabel = (feat: string): string => ECH_FEAT[feat as EchFeat]?.label || "Floorplans";
/** which stored pick a level consumes, or "" when it needs none (comms) */
export const echFeatPickKind = (feat: string): "plan" | "home" | "" =>
  ECH_FEAT[feat as EchFeat]?.pick ?? "";

/**
 * THE MAP IS THE CONTRACT. A field not listed here stays hand-edited in both
 * modes. Ported verbatim from L10814–10924 — same keys, same `levels`, same
 * omissions. The keys are read off each block's own guardrail, not guessed: the
 * Grand Opening blocks use CtaUrl / cta1Url / cta2Url, not btnUrl.
 *
 * THE TWO FEATURED ROWS LIVE IN `ECH_FEAT` (above) and are referenced here, not
 * duplicated — `ECH_DYN.ECH_Featured1CTA` and `ECH_FEAT.plans.fields.ECH_Featured1CTA`
 * are the same object. This map is therefore still "the default level's contract",
 * which is what every existing caller of `echDynFor(name)` means by it.
 */
export const ECH_DYN: Record<string, Record<string, DynEntry>> = {
  /* `cityState` arrived when Layne revised the live Header block (2026-08-21 —
     Jack: "you added city and state in and it's not dynamic"). New SFMC fields
     auto-appear in the panel; this row is the wiring that makes them FED. Seeded:
     Layne ships the literal "City, State" placeholder as the default, so the
     feed must overwrite it while untouched but hand-edits win. */
  /* `community` stays a NON-seed fed field (2026-08-24). Marking it seed also makes
     the panel treat it as editable-with-a-hint, which is a different decision from
     the render fix Jack asked for — the placeholder he saw on the form's preview rail
     is handled where it lives, in `railBlankOk`. */
  ECH_Logo: { community: "community.name", cityState: { src: "community.citystate", seed: true }, phone: "community.phone" },
  /* URL FIELDS DEFAULT TO A REAL PAGE (per Jack). Every one of these ships with
     the placeholder "https://" — a dead link if nobody edits it.
     `community.pageurl` resolves scope-aware: one community → its own lennar.com
     page, anything broader → that market's page. Labels stay manual; only the
     destination is filled. Switch the block to Manual to override. */
  ECH_2CTA: { btn1Url: "community.pageurl", btn2Url: "community.pageurl" },
  ECH_1CTA: { btnUrl: "community.pageurl" },
  /* ── THE GRAND OPENING FAMILY NAMES ITS OWN PLACE (2026-08-28) ─────────────────
     An audit of every block found 19 unfed fields here, and half of them were facts
     this app already answers on OTHER blocks in the same email: `CommunityName` is
     `community.name` (it fills the Header), and `AddressLine1`/`AddressLine2` are the
     identical pair `ECH_GetDirections` fills from `echCommInfo`. Two blocks, the same
     two facts, one fed and one not — so a GO invite shipped Layne's placeholders next
     to a correctly filled Get Directions block.

     WHAT STAYS MANUAL, and why (Jack's ruling, 2026-08-28):
      · `Day` / `Date` / `Time` — an EVENT date is not the deploy date. A GO invite
        normally goes out days before the event, so seeding these from the request's
        deploy date would be wrong by default on most sends, and a wrong date that
        looks filled in is worse than a blank one. The intake row's Event Information
        column is free text today; when Layne splits it into three structured fields
        these get a real source.
      · `BodyCopy` — the requester's words, like every other body block.
      · `Headline` on the Thank You — same.
     The address pair carries `levels:["community"]` for the reason Get Directions
     does: a market or division send has no street, and the block's layout is locked,
     so blanking those lines would ship an address with holes in it. */
  ECH_GOTY1CTA: { CtaUrl: "community.pageurl", CommunityName: "community.name" },
  /* the two-button Thank You (Layne 10-29) — same sources, both buttons */
  ECH_GOTY2CTA: { cta1Url: "community.pageurl", cta2Url: "community.pageurl", CommunityName: "community.name" },
  ECH_GOInvite1CTA: {
    CtaUrl: "community.pageurl",
    CommunityName: "community.name",
    MasterplanName: { src: "community.masterplan", levels: ["community"] },
    AddressLine1: { src: "community.address1", levels: ["community"] },
    AddressLine2: { src: "community.address2", levels: ["community"] },
  },
  ECH_GOInvite2CTA: {
    cta1Url: "community.pageurl", cta2Url: "community.pageurl",
    CommunityName: "community.name",
    MasterplanName: { src: "community.masterplan", levels: ["community"] },
    AddressLine1: { src: "community.address1", levels: ["community"] },
    AddressLine2: { src: "community.address2", levels: ["community"] },
  },
  /* SOCIAL ICONS ARE THE DIVISION'S OWN (per Jack + the Fast Track deck). All five
     fields ship as "https://" and the block's own guardrail says they "must be set
     before sending" — so today every send either carries five dead icons or someone
     pastes them by hand 48 divisions over. /sfmc-social-links has the real
     per-division URLs, with the national LNR set as the fallback. A channel a
     division doesn't have resolves empty, and the block's instruction is to remove
     that icon. */
  ECH_SocialIcons: {
    facebookUrl: "social.fb", xUrl: "social.x", instagramUrl: "social.ig",
    pinterestUrl: "social.pin", youtubeUrl: "social.yt",
  },
  /* Get Directions is community-level by nature — a division or a market has no
     street address. Those fields declare `levels`, so at any other focus they are
     left alone rather than filled with nothing: the block's own note says the
     address layout is locked and lines can't be added or removed, so blanking them
     would ship an address block with empty lines. */
  ECH_GetDirections: {
    Community: "community.name",
    Phone: "community.phone",
    AddressLine1: { src: "community.address1", levels: ["community"] },
    AddressLine2: { src: "community.address2", levels: ["community"] },
    DirectionsUrl: { src: "community.directions", levels: ["community"] },
  },
  /* ── THE PHOTO IS MANUAL; ITS LINK IS NOT (2026-08-28) ────────────────────────
     `ECH_MainImage` used to sit in `ECH_MANUAL_ONLY`, which reads as "a photo has no
     feed" — true — but that map is enforced in `echDynOn`, i.e. it turns the WHOLE
     BLOCK off. So this row was dead code and the hero's click destination could never
     be filled: every button in the email got `community.pageurl` automatically while
     the hero image shipped the literal placeholder `https://`, a dead link unless
     somebody typed one.
     The exemption belongs at the FIELD, and it already exists there — this map IS the
     field-level contract. `imgSrc` and `imgAlt` have no row here, so the fill can
     never touch them however dynamic the block is, and a Bynder pick stays the only
     thing that writes the photo. See `ECH_MANUAL_ONLY` below, now empty. */
  ECH_MainImage: { imgUrl: "community.pageurl" },
  /* the same block at 600px — configured differently from its own sibling for no
     stated reason until now (audit finding 5) */
  ECH_MainImage600: { imgUrl: "community.pageurl" },
  /* ECH_SecondaryImage declares imgSrc1/imgAlt1/imgSrc2/imgAlt2 and NO url field, so
     it has nothing this app can honestly fill. Deliberately absent rather than
     force-fed: two photos are the requester's choice, and inventing a feed for an alt
     text is how the Bynder migration note ended up in an alt attribute. */
  /* the community's name under the hero. Seeded: Layne ships the literal "Community
     Name" as the default, so it is the panel's grey hint and the feed writes over it
     while untouched — the `cityState` rule, applied to the block that arrived after
     it. */
  ECH_CommunitySubHead: { subhead: { src: "community.name", seed: true } },
  /* ── THE SECTION HEADER IS THE TEMPLATE'S, NOT THE SEND'S (Jack, 2026-08-28) ───
     `sectionTitle` and `linkLabel` were the last two unruled fields in the audit.
     Both are template furniture rather than per-send content — the title says what
     the list below it IS, and "View All" is the approved wording of the one link that
     block has (its own guardrail note warns about colliding with it). So both are
     FIXED: fed from the block's own authored default, listed in `ECH_FIXED` below,
     and read-only in the panel. The one thing that varies is real: when the block
     underneath is the amenity grid, "Explore Homes" is simply wrong, so the title
     follows the block it heads. */
  ECH_TableHeader: {
    linkUrl: "community.pageurl",
    sectionTitle: "table.title",
    linkLabel: "table.viewall",
  },
  /* FEATURED FLOORPLAN — the plan's own specs and photo, from SFMC. `plan.self.*`
     resolves per Featured block, so two of them show two plans. The labels
     (BedsLabel/BathsLabel/SqFtLabel) stay manual — they're wording, not data.
     BtnLabel likewise: it is wording, so switching the block to Homes level fills the
     home but leaves "View Floorplan" for the requester to reword. The DESTINATION does
     follow the level (ECH_FEAT[…].cta, read by ctaUrlFor).
     THE SAME OBJECTS as `ECH_FEAT.plans.fields`, not copies — see that table. */
  ECH_Featured1CTA: FEAT1_PLANS,
  ECH_Featured2CTA: FEAT2_PLANS,
  /* ⛔ ECH_CopyBlock.BodyCopy ← {src:"initiative.details", seed:true} is
     DELIBERATELY ABSENT, exactly as it is commented out in the monolith (Jack,
     2026-08-19: "remove the auto descriptions for now"). The plumbing was right;
     the FIELD is a marketing brief, not email copy — on a National Sales Event
     initiative it returned internal strategy prose long enough to trip the block's
     own limit. Re-enabling needs a precise worker route reading the AIM Initiatives
     row by initiative number AND field id (see the audit's "Needs a backend route"
     §3), plus either a type filter or a different field. The `seed` mechanism below
     is built and unused, same as the monolith. */
};

/**
 * ── A FEED THE BLOCK DECLARES FOR ITSELF (Layne's Contact Us ask, 2026-09-02) ───────
 *
 * Every entry in `ECH_DYN` above is a block THIS REPO knows by name. Layne asked for the
 * other shape: "read a data extension keyed on the author's division selection and
 * populate the block's declared fields from the matching row" — phrased generically on
 * purpose, so a data-driven block needs a header declaration and not a code change here.
 *
 * So a block whose `@GUARDRAILS` header declares `dataSource: { externalKey, primaryKey }`
 * gets a feed map DERIVED from its fields: every field carrying `fromColumn` becomes a
 * `de:<externalKey>:<column>` source, resolved by `echDynVal` from the row the hook
 * fetched for the current division. Same fed-field behaviour as everything else — the
 * panel shows it editable, `takeover` claims it on the first keystroke, clearing hands
 * it back.
 *
 * `dynMap()` is the ONE lookup the rest of this file uses; the four places that used to
 * read `ECH_DYN[...]` directly go through it, so a declared feed and a coded feed are
 * indistinguishable downstream. A coded entry wins if both exist — none does today.
 *
 * WHY NO LEVELS: a DE keyed on division is the same row at community, masterplan and
 * market scope. The division is the key, not the pick.
 */
const DE_SRC = /^de:([^:]+):(.+)$/;
export function echDeclaredDyn(name: string): Record<string, DynEntry> | null {
  const meta = echByName(name);
  const full = meta && meta.full;
  const ds = full && (full as { dataSource?: DataSource | null }).dataSource;
  if (!ds || !ds.externalKey) return null;
  const fields = (full && full.fields) || {};
  const out: Record<string, DynEntry> = {};
  for (const k of Object.keys(fields)) {
    const col = String((fields[k] as { fromColumn?: string }).fromColumn || "").trim();
    if (col) out[k] = "de:" + ds.externalKey + ":" + col;
  }
  return Object.keys(out).length ? out : null;
}
/** the block's feed map, coded or declared — the only way this file looks one up */
export const dynMap = (name: string): Record<string, DynEntry> | null =>
  ECH_DYN[name] || echDeclaredDyn(name);
/** every distinct data source any registered block declares — what the hook fetches */
export function echAllDataSources(): DataSource[] {
  const seen: Record<string, DataSource> = {};
  for (const t of echTypes()) {
    const ds = echDataSource(echNameOf(t));
    if (ds && !seen[ds.externalKey]) seen[ds.externalKey] = ds;
  }
  return Object.values(seen);
}
/** the block's declared data source, if it has one */
export function echDataSource(name: string): DataSource | null {
  const meta = echByName(name);
  const ds = meta && meta.full && (meta.full as { dataSource?: DataSource | null }).dataSource;
  return ds && ds.externalKey ? { externalKey: String(ds.externalKey), primaryKey: String(ds.primaryKey || "Division_Code") } : null;
}

/* LISTING TABLE — 10 rows × 7 fields, generated rather than typed out. Every row
   comes from a real available homesite, so the block that used to need a
   spreadsheet import fills itself. Collection names stay manual: a collection is
   not a property of a homesite. Community-level only — a market send has no lot
   list. (monolith L10908–10924) */
/* the Listing table at FLOORPLANS level: one row per plan — plan number in the
   homesite slot (the column header reads "Homesite / Plan" and a plan's number is
   its honest identifier), from-price in the price column, View → the plan's page.
   Registered in ECH_FEAT below so the pane's existing level control actually
   changes what fills (Jack, 2026-08-21: "should adapt to each level"). */
const LST_PLANS: Record<string, DynEntry> = {};
for (let n = 1; n <= 10; n++) {
  LST_PLANS["home" + n + "Homesite"] = { src: "plan." + n + ".num", levels: ["community"] };
  LST_PLANS["home" + n + "Plan"] = { src: "plan." + n + ".name", levels: ["community"] };
  LST_PLANS["home" + n + "SqFt"] = { src: "plan." + n + ".sqft", levels: ["community"] };
  LST_PLANS["home" + n + "Beds"] = { src: "plan." + n + ".beds", levels: ["community"] };
  LST_PLANS["home" + n + "Baths"] = { src: "plan." + n + ".baths", levels: ["community"] };
  LST_PLANS["home" + n + "Price"] = { src: "plan." + n + ".price", levels: ["community"] };
  LST_PLANS["home" + n + "Url"] = { src: "plan." + n + ".url", levels: ["community"] };
}

(() => {
  const m: Record<string, DynEntry> = {};
  for (let n = 1; n <= 10; n++) {
    m["home" + n + "Homesite"] = { src: "home." + n + ".homesite", levels: ["community"] };
    m["home" + n + "Plan"] = { src: "home." + n + ".plan", levels: ["community"] };
    m["home" + n + "SqFt"] = { src: "home." + n + ".sqft", levels: ["community"] };
    m["home" + n + "Beds"] = { src: "home." + n + ".beds", levels: ["community"] };
    m["home" + n + "Baths"] = { src: "home." + n + ".baths", levels: ["community"] };
    m["home" + n + "Price"] = { src: "home." + n + ".price", levels: ["community"] };
    m["home" + n + "Url"] = { src: "home." + n + ".url", levels: ["community"] };
  }
  ECH_DYN.ECH_ListingTable = m;
  /* the level table points at the SAME objects — homes is the default map, plans is
     the generated plan-row map above; comms has no honest 4-cell row and deliberately
     falls back to homes */
  ECH_FEAT.homes.fields.ECH_ListingTable = m;
  ECH_FEAT.plans.fields.ECH_ListingTable = LST_PLANS;
})();

/** normalise a map entry to {src, levels, seed} (monolith __echSrcOf) */
export function echSrcOf(v: DynEntry): Required<DynSource> {
  return v && typeof v === "object"
    ? { src: v.src, levels: v.levels || null, seed: !!v.seed, opt: !!v.opt }
    : { src: String(v), levels: null, seed: false, opt: false };
}
export const echIsSeed = (v: DynEntry) => echSrcOf(v).seed;

/* ── A SEED'S AUTHORED DEFAULT IS A HINT, NOT CONTENT (Jack, 2026-08-21) ────────
   "i'd like it to start blank like the other" — and then, on being told Layne
   could blank the default in Content Builder: "how would the city, state show as
   the label but not fill in text, as what you said might confuse her." So the
   line is drawn HERE, not in SFMC: Layne keeps authoring a human-readable default
   ("City, State") exactly as she does today, and the app treats it as the field's
   grey placeholder rather than a starting value. The block enters every document
   with "" in the field — panel blank, canvas blank, export blank — and the feed
   seeds it the moment Step 1 can. Only SEED-mapped fields get this treatment: a
   non-seed default (a headline, a CTA label) is real shipped copy and unaffected. */

/** the authored default for a SEED field — the panel's grey hint — else "" */
export function echSeedHint(type: string, k: string): string {
  const map = dynMap(echNameOf(type));
  if (!map || !map[k] || !echIsSeed(map[k])) return "";
  const meta = echMeta(type);
  const f = meta && meta.full && meta.full.fields && meta.full.fields[k];
  return String((f && f.default) ?? "");
}

/**
 * THE MASTERPLAN LEADS THE HEADER (Jack, 2026-08-21: "should state master plan
 * first so like Granary Park - 50s"). Airtable's collection vocabulary is
 * "<collection> - <masterplan>" ("50s - Holstein Crossing", "Village - Timber
 * Ridge") — right for Step 1's pick list, backwards for an email heading. Split
 * at the FIRST " - " and swap; a name without the separator passes through.
 */
export function echCollLabel(name: unknown): string {
  const s = String(name || "").trim();
  /* the separator as Airtable actually types it: a dash with a space on AT LEAST
     one side ("50s - Granary Park", "24ft Townhomes- Shearwater"). A dash with no
     space on either side is part of a word ("St-Augustine") and never splits.
     Output always normalises to a clean " - ". */
  const m = /\s+-\s*|\s*-\s+/.exec(s);
  /* ── THE TAG COMES OFF EVEN WITHOUT A SEPARATOR (2026-08-22, second pass) ──────
     The first fix only stripped it on the swap path, so a MASTERPLAN heading —
     which has no "50s - " prefix — went back to the email reading "Edenbrooke at
     Hyland Trail | 55+". Caught by driving the re-pick live rather than by a test.
     One rule now: the audience tag is Airtable's own marker and never appears in a
     customer-facing heading, separator or not. */
  if (!m) return echNameCore(s);
  const left = s.slice(0, m.index).trim();
  /* ── THE AUDIENCE TAG IS INTERNAL — IT DOES NOT GO IN AN EMAIL HEADING (2026-08-22)
     Airtable spells the collection "50s - Edenbrooke at Hyland Trail | 55+", so the
     swap produced "Edenbrooke at Hyland Trail | 55+ - 50s" as a customer-facing
     table header. The tag is Airtable's own audience marker (`| 55+`), not part of
     the community's name in SFMC or in the URL, so it comes off the heading. The
     separator and everything else stay exactly as they were. */
  const right = echNameCore(s.slice(m.index + m[0].length));
  if (!left || !right) return s;
  return right + " - " + left;
}

/**
 * THE PARENT COMMUNITY BEHIND A COLLECTION NAME. 2026-08-22.
 *
 * A section heading names the COMMUNITY, not the collection — a masterplan's 50s and
 * 60s are one place to a reader. Cutting `echCollLabel`'s output at its " - " gets
 * that right for Airtable's own convention ("50s - Granary Park" → "Granary Park")
 * and WRONG for the rows that break it: "Hardwick Farms - Townhome Collection" was
 * swapped to "Townhome Collection - Hardwick Farms" and then headed a live section as
 * "Townhome Collection", which names a product line and not a community.
 *
 * So the side that carries a COLLECTION MARKER is the collection and the other side
 * is the parent. When both sides or neither look like a marker, nothing is inferred —
 * the label's first segment stands, which is the previous behaviour.
 */
const COLL_MARK = /^(?:[0-9]+s|[0-9]+(?:ft|')(?:\s|$)|the\s)|(?:townhome|townhomes|collection|series|village|signature|estates|manor|duplex|paired|cottages|villas|carriage)\b/i;
export function echParentCommunity(name: unknown): string {
  const label = echCollLabel(name);
  const m = /\s-\s/.exec(label);
  if (!m) return label.trim();
  const a = label.slice(0, m.index).trim();
  const b = label.slice(m.index + m[0].length).trim();
  const am = COLL_MARK.test(a);
  const bm = COLL_MARK.test(b);
  if (am && !bm) return b;
  if (bm && !am) return a;
  return a;
}

/** blank every seed field still carrying its authored default — mutates in place.
    Called from the block factory (App makeBlock), the Form preview's pvBlock, and
    the manifest rebuild, so a block holding the authored text can only mean the
    requester TYPED it (which flips `__socOff` and is theirs to keep). */
export function echSeedStrip(m: { type: string; data: Record<string, unknown> }): void {
  const map = dynMap(echNameOf(m.type));
  if (!map || !m.data) return;
  for (const k of Object.keys(map)) {
    if (!echIsSeed(map[k])) continue;
    const hint = echSeedHint(m.type, k);
    if (hint && String(m.data[k] ?? "") === hint) m.data[k] = "";
  }
}
export function echSrcOk(v: DynEntry, lvl: string): boolean {
  const e = echSrcOf(v);
  return !e.levels || e.levels.indexOf(lvl) > -1;
}
/**
 * The field map for a block, AT A FEATURE LEVEL.
 *
 * `feat` omitted → the default map, byte-identical to what every existing caller got
 * before 2026-08-20. A level the table covers for this block wins; a block the table
 * does not cover (the Listing table, the Logo, Get Directions, …) falls back to
 * `ECH_DYN` at every level, which is how "one level only" is expressed — by absence,
 * not by a special case here.
 */
export const echDynFor = (name: string, feat?: string) =>
  (feat ? ECH_FEAT[feat as EchFeat]?.fields[name] : undefined) || dynMap(name) || null;
/** true when the block has a feed behind it at all */
export const echHasFeed = (name: string) => !!dynMap(name);

/**
 * DYNAMIC-ONLY BLOCKS (monolith `ECH_DYN_ONLY` L11177, per Jack: "social would
 * only be dynamic so remove the toggle"). Nothing about a division's social
 * handles is a per-send decision — the URLs are the division's, full stop. So
 * there is no Manual mode to offer and NO STRIP to render; the only choice is
 * which icons to include.
 */
export const ECH_DYN_ONLY: Record<string, 1> = { ECH_SocialIcons: 1 };
export const echDynOnly = (name: string) => !!ECH_DYN_ONLY[name];

/**
 * ALWAYS-DYNAMIC BLOCKS — no mode, no strip, and editing stays normal (2026-08-22,
 * per Jack: "for at least the listing table and featured home let's just have it be
 * fully dynamic — when adding a community/home you pull from dynamic but can just
 * edit like regular").
 *
 * These are the two blocks whose content IS inventory: the listing table's 10 rows
 * and a Featured card's plan/home. Manual mode on either one was never "a second way
 * to fill it" — it was "freeze all 70 cells and retype them", which is why fixing one
 * stale price cost the whole block its live fill. So the mode goes away:
 *
 *   · rows/cards arrive filled from whatever Step 1 resolved
 *   · adding a home or choosing a plan pulls from the feed
 *   · typing over any value still works — `socIsOff` already marks that field as the
 *     requester's and the fill skips it, which is the mechanism that made a mode
 *     unnecessary in the first place (see `echDynPatch`)
 *
 * Unlike ECH_DYN_ONLY this does NOT restrict what can be edited; it only removes the
 * choice of HOW the block gets filled. That distinction is the whole point.
 */
/* ── AND NOW EVERY FED BLOCK — THE STRIP IS GONE (2026-08-27) ────────────────────
   Jack, after reviewing the Header: "perfect do that for the rest of the blocks."
   So this stopped being an allowlist and became the RULE: a block with a feed is
   always dynamic. The two exceptions keep their own lists, and they are exceptions
   about the KIND of content, not about a mode — `ECH_DYN_ONLY` (a division's social
   handles are never a per-send choice) and `ECH_MANUAL_ONLY` (a photo has no feed).
   Everything else fills itself and is editable field by field.

   Written as a rule rather than seven more names on purpose: the next block Layne
   ships with a feed would otherwise arrive with a stray Manual/Dynamic strip that no
   other block has, which is exactly how the family look erodes. The map below stays
   only as documentation of the three blocks that got here first.

   Jack, on the Header panel's Manual/Dynamic strip: "i want to remove these and
   just auto dynamically pull at the start with the ability to change/edit afterwards…
   do logo first so i can confirm you're doing it right."

   The strip was always a worse version of what the fill already does. It offered two
   answers to a question with one: the feed knows the community, the requester may
   disagree about a specific field, and `socIsOff` has recorded that per FIELD since the
   listing table went modeless. A block-level mode on top of a field-level takeover buys
   nothing and costs a control that has to be found and understood — and while it sat in
   "Manual" the panel quietly stopped filling fields nobody had touched.

   So the Header now behaves the way the listing table and Featured cards already do:
   fields arrive filled from Step 1, and typing over any one of them claims THAT field
   for the requester and the feed leaves it alone forever after. Nothing becomes
   read-only; the only thing removed is the choice of how the block gets filled.

   ECH_Logo first, on purpose — three fields, all fed, and the block is in all seven
   templates, so it is the cheapest place to confirm the pattern before the rest follow. */
export const ECH_ALWAYS_DYN: Record<string, 1> = {
  ECH_ListingTable: 1,
  ECH_Featured1CTA: 1,
  ECH_Featured2CTA: 1,
  ECH_Logo: 1,
};
/**
 * A block with a feed is always dynamic. The list above is history, not the test —
 * `echHasFeed` is, so a fed block Layne adds tomorrow behaves like every other one
 * without anyone remembering to add it here (2026-08-27).
 *
 * The two exceptions come first and are about content, not mode: dynamic-only has no
 * manual answer to offer, manual-only has no feed to offer. Both already render no
 * strip; this keeps them out of the always-dynamic FILL as well, which is what
 * `echDynOn` reads.
 */
/* `echAlwaysDyn` is declared AFTER `echManualOnly` below — see the note there. */

/**
 * MANUAL-ONLY BLOCKS — the mirror of `ECH_DYN_ONLY` (2026-08-20, per Jack:
 * "remove the dynamic option for main image").
 *
 * A dynamic mode has to be able to answer "filled from WHAT?" A hero photo has no
 * such answer: there is no community-level image field in the feed, the photo comes
 * out of Bynder by hand, and the panel's real control is "Replace photo". Offering
 * Dynamic next to it either does nothing or quietly overwrites the photo somebody
 * just chose — the dead-control problem the greyed-out Dynamic already taught us.
 *
 * THE RULE THIS ENCODES, not just the one block: a Manual/Dynamic strip is drawn
 * only where BOTH modes are real. Dynamic-only → no strip (social handles are the
 * division's). Manual-only → no strip (a photo is a person's choice). A strip means
 * the block genuinely has two answers.
 */
/**
 * Blocks with NOTHING a feed can answer — no strip, no fill, ever.
 *
 * EMPTY SINCE 2026-08-28, and that is the fix, not an oversight. It held
 * `ECH_MainImage` on the grounds that a photo has no feed, which is true of the
 * PHOTO and false of the block: the hero also carries `imgUrl`, and this map is
 * enforced in `echDynOn`, so listing the block killed the link's feed too and the
 * hero shipped a dead `https://` on every send (audit finding 2).
 *
 * The honest boundary is the field, and `ECH_DYN` already draws it: a field with no
 * row there is never written by the fill. Kept — and kept enforced in `echDynOn` —
 * because the case is real: the moment Layne ships a block whose EVERY field is a
 * photo or a hand-typed label, listing it here is still how it avoids a strip and a
 * pointless dynamic note.
 */
export const ECH_MANUAL_ONLY: Record<string, 1> = {};
export const echManualOnly = (name: string) => !!ECH_MANUAL_ONLY[name];

/**
 * ── FIELDS THE TEMPLATE OWNS (2026-08-28) ─────────────────────────────────────
 *
 * A fed field is normally editable — that is the whole point of the modeless panel
 * (2026-08-27: "auto dynamically pull at the start with the ability to change/edit
 * afterwards"). These are the exception: they are FURNITURE, not content, so they
 * fill from the template and the panel shows them read-only rather than inviting 47
 * divisions to rename the same section 47 ways.
 *
 * Jack's ruling on `ECH_TableHeader`, 2026-08-28: the title says what the list below
 * it is, and the link label is the approved wording of that block's one link.
 *
 * A field listed here MUST also have an `ECH_DYN` row — the value has to come from
 * somewhere, and "read-only" with no feed is just a field nobody can fill.
 */
export const ECH_FIXED: Record<string, Record<string, 1>> = {
  ECH_TableHeader: { sectionTitle: 1, linkLabel: 1 },
};
/**
 * Fixed by the CODE table above, or by the block's own DECLARATION: a field that says
 * `authorEditable: false` is shown read-only and never asked for, whatever its type
 * (Layne, BLOCKER-REQUIRED-BY-TYPE-20260902: "a field an author cannot type into
 * should never be something they are asked to fill"). Her 17 DE-fed Contact Us fields
 * declare exactly that — the division's row fills them, the panel shows what will
 * render, and the author is not handed an empty box for a photo they cannot supply.
 */
/**
 * ── THE SECTION TITLE IS A CHOICE, NOT A TYPEBOX (2026-09-18) ──────────────────
 * RMT pilot call, item 4: "the headers can't be changed." The header they meant is
 * this one — `ECH_TableHeader.sectionTitle`, locked read-only on 2026-08-28. Jack's
 * ruling today ("just this one for now"): open it as a DROPDOWN of approved titles,
 * never free text — 47 divisions typing 47 spellings was the reason it was locked.
 * The template's own answer (`table.title`: Layne's authored default, or "Amenities"
 * over the amenity grid) is always the first option and the default; picking any
 * other option is a takeover (`__socOff`) so the feed stops rewriting it, and
 * picking the template's answer again hands it back.
 *
 * The wording below is a PROPOSAL awaiting Layne/Justin — edit this list, nothing
 * else, to change what the divisions can pick.
 */
export const SECTION_TITLE_OPTIONS: readonly string[] = [
  "Explore Homes",
  "Available Homes",
  "Quick Move-In Homes",
  "Featured Homes",
  "Explore Floorplans",
  "Featured Floorplans",
  "New Homes",
  "Now Selling",
  "Amenities",
];
/** the dropdown's options: the template's answer first, then the approved list, no repeats */
export function echTitleChoices(auto: string, current?: string): string[] {
  const out: string[] = [];
  const push = (v: string) => { const t = String(v || "").trim(); if (t && !out.includes(t)) out.push(t); };
  push(auto);
  SECTION_TITLE_OPTIONS.forEach(push);
  /* a value already on the block (an older document, a Content Builder default we
     do not list) stays selectable rather than silently snapping to something else */
  if (current) push(current);
  return out;
}

export const echFixed = (name: string, k: string): boolean => {
  if (ECH_FIXED[name] && ECH_FIXED[name][k]) return true;
  const meta = echByName(name);
  const f = meta && meta.full && meta.full.fields && (meta.full.fields as Record<string, { authorEditable?: boolean }>)[k];
  return !!f && f.authorEditable === false;
};

/**
 * A block with a feed is always dynamic. The `ECH_ALWAYS_DYN` map above is history,
 * not the test — `echHasFeed` is, so a fed block Layne adds tomorrow behaves like every
 * other one without anyone remembering to list it (2026-08-27, Jack: "perfect do that
 * for the rest of the blocks").
 *
 * The two exceptions come first and are about CONTENT, not mode: dynamic-only has no
 * manual answer to offer, manual-only has no feed to offer. Declared here rather than
 * beside the map so it sits below both exception lists — a const arrow that reads a
 * `const` declared later would be a temporal-dead-zone error the moment anything calls
 * it during module init.
 */
export const echAlwaysDyn = (name: string) =>
  !!name && !echDynOnly(name) && !echManualOnly(name) && echHasFeed(name);

/* ── the level ──────────────────────────────────────────────────────────────
   Dynamic isn't one thing — a send can be scoped to a DIVISION, a MARKET or a
   COMMUNITY, and the header should say whichever one it is. THE LEVEL IS THE
   INITIATIVE REQUEST FOCUS (per Jack): Step 1 already asks what this send
   targets, and a second toggle in the Inspector could only agree with it or
   contradict it. So there is no level control in the panel at all. Falls back UP
   the chain, never down. */
export function echLevel(focus: string, pickCount: number, sharedParent?: boolean): string {
  const f = String(focus || "");
  if (f === "Division") return "division";
  if (f === "Market") return "market";
  if (f === "Area of Interest") return "aoi";
  /* THE TWO LITERALS THE MONOLITH ACCEPTS, AND NOTHING ELSE (code review #17). This
     used to also take "Community/Masterplan" and a catch-all /community|masterplan/i
     — dead branches (requestData.ts only ever emits "Community") that silently
     widened the one function deciding every field's scope, and the regex would have
     fired on any unrelated focus string containing the word. Monolith L11204. */
  if (f === "Community" || f === "Masterplan") {
    /* ── SEVERAL COLLECTIONS OF ONE MASTERPLAN ARE STILL ONE PLACE (2026-08-27) ────
       Jack: "could not know like if i select multiple collections from the same mpc
       that it would say on community/mpc level?"
       The old rule was right about its reason and too blunt about its test: several
       communities in one send have no community-level truth, so it fell back UP to
       market. But `Village - Timber Ridge` + `50' & 60' - Timber Ridge` are not
       several communities — they are two collections of Timber Ridge, and the
       masterplan IS the single answer to "which place is this send about". Falling
       back to market threw that away and put the market's name and phone in a send
       about one masterplan.
       `sharedParent` is `echSharedParent()` having found exactly one masterplan every
       pick sits under (by pageUrl path, the only parent link either feed carries). The
       caller resolves that masterplan as the effective pick, so community-level fields
       have a real source — see useEchDyn's `soleName`. Without that adoption this
       would only blank fields, which is why the signal is passed in rather than
       guessed here. */
    if (pickCount > 1) return sharedParent ? "community" : "market";
    return "community";
  }
  return "market"; /* the form's own default */
}

/**
 * HAS STEP 1 ACTUALLY BEEN ANSWERED AT THIS LEVEL?
 *
 * THE BUG THIS EXISTS FOR (Jack, from the running build, 2026-08-19). His Get
 * Directions panel with the masterplan "Aloravita" picked showed Community =
 * "Aloravita", Address Line 2 = "Peoria, AZ" and Phone = "480-345-0077" — all
 * filled — while Address Line 1 sat empty under the note "Pick a community on
 * Step 1 to fill this." He had. The note was the only thing wrong.
 *
 * The panel could only ask "did the feed return anything?" (`dv`), and answered
 * every empty value with the same "you haven't picked yet" sentence. Those are
 * two different states and only one of them is the requester's to fix:
 *   · nothing picked at this level → "Pick a … on Step 1" is correct and useful;
 *   · picked, resolved, and the SOURCE simply has no value → the requester can
 *     do nothing about it, and telling them to pick again is a dead end.
 *
 * Verified against live Airtable via /refdata?div=AZH (2026-08-19): the
 * Masterplans row for Aloravita carries `{city:"Peoria", state:"AZ"}` and NO
 * `address` — which is why city/state/phone answered and the street address did
 * not. 330 of ~900 masterplans are in the same position (see
 * `community.directions` below, which already accommodates it). So the value is
 * genuinely absent, not mis-resolved, and NOTHING is invented to fill it: the
 * field stays empty and the note stops blaming Step 1.
 *
 * The monolith has the same single-sentence behaviour (index.html L13363), so
 * this is a DELIBERATE deviation from the spec rather than a missed port.
 */
export function echScopeReady(lvl: string, ctx: EchCtx): boolean {
  if (lvl === "division") return !!String(ctx.divisionCode || "").trim();
  if (lvl === "market") return !!(ctx.market && String(ctx.market.name || "").trim());
  if (lvl === "aoi") return !!String(ctx.aoiLabel || "").trim();
  return (ctx.picks || []).length > 0;
}

/** one Step-1 community/masterplan pick, with whatever Airtable knows about it */
export interface EchPick {
  name: string;
  kind: "CM" | "MP";
  code?: string;
  pageUrl?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  market?: string;
  /** the community's Airtable Amenities multi-select, pipe-joined (audit row 17) */
  amenities?: string;
  /**
   * Set by `echBorrowAddr` when this pick's street/city/state/zip were taken from
   * one of its COLLECTIONS because its own row has none — the collection's name, so
   * the panel can say whose address it is. Absent on every pick that owns its
   * address, which is how `CommInfo.addrFrom` stays honest.
   */
  addrFrom?: string;
  /**
   * Set by `echBorrowAmenities` when this masterplan's amenities were unioned from
   * its COLLECTIONS because the Masterplans table carries no Amenities field. The
   * collection names, so a panel can say whose amenities these are — the same
   * honesty contract as `addrFrom`.
   */
  amenitiesFrom?: string[];
}

/**
 * One addressed row from /refdata's `addr` bag — the donor shape for
 * `echBorrowAddr`. A pick is one of these plus a kind; kept separate because a
 * donor is never a Step-1 selection and must not be mistaken for one.
 */
export interface AddrRow {
  code?: string;
  pageUrl?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  amenities?: string;
  /** ── refdata SCHEMA 8 (2026-08-22) ──────────────────────────────────────────
   *  The community's own Area of Interest and Submarket, straight off the Airtable
   *  Communities table. Jack: "why can't you pull area of interest, it's in airtable
   *  under each community you can leverage" — he was right; the field existed all
   *  along and /refdata simply never asked for it, which is what made the AOI tier
   *  look blocked. 2,153 of 3,095 community rows carry one (measured live).
   *  `aoi` CAN BE A COMMA LIST ("Harrah, East OKC"), so match by split, not equals. */
  aoi?: string;
  submarket?: string;
  /**
   * The row's market, exactly as /refdata sends it — a raw Airtable string that can
   * be a pipe list and can carry a suffix ("ORLANDO - NORTH|…"), which is why every
   * reader normalises on the pipe and then on a spaced hyphen, rather than using it
   * straight. Declared here 2026-08-27 because `echSharedParent` builds a PICK out of
   * an addr row and a pick carries `market`; `useEchDyn` had been reading this field
   * off an untyped bag all along, so this documents what was already arriving.
   */
  market?: string;
}

/**
 * THE ROUTE ASKED AND FAILED — not the same as `null` (2026-09-09).
 *
 * A sentinel rather than a boolean beside the map, so the three states live in ONE
 * value and no consumer can read the row without also seeing that it failed. Not a
 * plain `"failed"`: the value shares a slot with a row object, and a namespaced string
 * cannot be mistaken for a column value in a log or a snapshot.
 */
export const DE_FAILED = "de:failed" as const;

/** what one `deRows` entry can be — see `EchCtx.deRows` for what each state means */
export type DeRowState = Record<string, string> | null | typeof DE_FAILED;

/** the route failed for this source; treat it exactly like not-yet-fetched, never like no-row */
export const deFailed = (v: DeRowState | undefined): v is typeof DE_FAILED => v === DE_FAILED;

/** everything the resolver may read. Assembled by useEchDyn from the feeds. */
export interface EchCtx {
  /** Step 1's Initiative Request Focus — the ONLY input to echLevel() */
  focus: string;
  divisionCode: string;
  /** Step 1's community + masterplan picks, in order */
  picks: EchPick[];
  /**
   * ── THE ONE MASTERPLAN EVERY PICK IS A COLLECTION OF (2026-08-27) ────────────
   * Set by useEchDyn from `echSharedParent` when there are SEVERAL picks and every
   * one of them sits one path segment under the same masterplan. Absent for a
   * single pick, for picks under different masterplans, and whenever a pick carries
   * no pageUrl — the only parent link either feed has.
   *
   * It is the IDENTITY of the send, and only that: the name, address, page and
   * market come from here, while the INVENTORY still comes from `collPacks`, which
   * is built from the picks themselves (each chosen collection is its own group).
   * That split is the whole design — a send about two collections of Timber Ridge
   * is one place with two collections in it.
   *
   * Deliberately NOT used to load `pack`: `pack` gates the plan/home resolvers, and
   * a masterplan's own /sfmc-content pack has no plans in it (see the comment on
   * `collections` in useEchDyn), so adopting it would replace a null — which falls
   * through to the scope list — with an empty pack that answers "". Identity here,
   * inventory from the collections, packs untouched.
   */
  parent?: EchPick;
  /**
   * ── THE MASTERPLAN ABOVE A SINGLE PICK (2026-08-28) ───────────────────────────
   * `parent` above is for SEVERAL picks that turn out to be collections of one
   * masterplan, and it is adopted as the send's identity. This is the other case: ONE
   * community pick that happens to sit under a masterplan, where the community is
   * still the send and the masterplan is a second, different fact — which is exactly
   * what the Grand Opening blocks ask for ("<CommunityName> in <MasterplanName>").
   * A name, not a pick, because nothing resolves ADDRESS or PAGE from it: the
   * community answers those itself. Empty unless `echParentName` is certain.
   */
  parentName?: string;
  /** the AOI as a buyer would recognise it (the option's LABEL, not its code) */
  aoiLabel?: string;
  /**
   * Step 1's initiative. Carried so a fill re-runs when it changes (part of row
   * 19's contract) even though NOTHING resolves from it today — the one
   * `initiative.*` source is the parked seed row. It becomes live the moment that
   * row is uncommented and the precise worker route is deployed.
   */
  initiative?: string;
  /** market name + state for a market-level send */
  market?: { name: string; state?: string; phone?: string } | null;
  /** the division's own row (/sfmc-division-content) — the phone fallback for
   *  EVERY level, because a division phone is always answerable */
  division?: { code: string; name: string; phone?: string } | null;
  /** per-division social handles, keyed by division code, LNR = national */
  social?: Record<string, Record<string, string | undefined>> | null;
  /** the single picked community's /sfmc-content pack (null unless exactly one) */
  pack?: CommunityPackResponse | null;
  /**
   * A MASTERPLAN'S CHILD COLLECTIONS' PACKS, in `echCollectionsOf` order
   * (2026-08-20, Jack: "full plan should pull in all related collections").
   *
   * A masterplan's own `/sfmc-content` pack carries no plans — the floorplans live on
   * its collections (`Village - Timber Ridge`, `50' & 60' - Timber Ridge`, …), which
   * is why the wizard's Floorplans step rendered zero cards for Arlington Heights and
   * again for Timber Ridge. These are the packs those plans come from, and they are
   * the group headings the picker draws.
   *
   * EMPTY FOR A PLAIN COMMUNITY, which is what keeps this a superset: `echPacks`
   * below then returns exactly `[pack]` and every resolution is byte-identical to
   * what it was. Assembled by `useEchDyn` (one `/sfmc-content` per collection, in
   * parallel, behind the same `live` guard as the sole pack).
   */
  collPacks?: Pack[] | null;
  /**
   * THE PACKS THE REQUESTER LOADED IN THE WIZARD — 2026-08-20, Jack: "i want the
   * right panel to have the other view where i don't even need a community — i click
   * the + button and can select the info myself."
   *
   * `pack` and `collPacks` above are both derived from STEP 1: the one community the
   * form scoped the send to, plus (for a masterplan) its child collections. That made
   * Step 1 a precondition for the Dynamic pane — pick nothing there, or set the focus
   * to Market, and a floorplan pick had no pack to resolve against, so `echDynVal`
   * returned "" and the canvas never moved.
   *
   * The MONOLITH has no such precondition: its `window.__lvPackCache` holds whatever
   * the WIZARD loaded, not whatever Step 1 said, and its community step lists the
   * whole division (verified in Chrome 2026-08-20 on the live form — focus "Market",
   * no community, and the pane still drew "Floorplans · 0 selected  Choose ›" over a
   * community grid of all 50 ATL communities). This field is that cache: every pack
   * the wizard has fetched this session, plus any pack re-fetched on load for a pick
   * a saved document already carried.
   *
   * IT IS A SUPERSET, NEVER AN OVERRIDE. `echPacks` appends it AFTER the Step-1 packs
   * and dedupes by code, and `echDynVal` still prefers `ctx.pack` for the POSITIONAL
   * fallback — so with Step 1 scoped to one community every resolution is
   * byte-identical to what it was before this field existed.
   */
  pickPacks?: Pack[] | null;
  /**
   * The division's OWN Contact Us markup, assembled by /sfmc-isc (audit row 24).
   * "" once the route has answered with nothing usable, undefined while it has
   * not answered at all — the two are not the same and `echIscPatch` only writes
   * on a real one.
   */
  iscHtml?: string;
  /**
   * ── DATA-EXTENSION ROWS, BY EXTERNAL KEY (2026-09-02) ───────────────────────────
   * One entry per `dataSource` any registered block declares: the row for the current
   * division, columns LOWERCASED (the API's casing), every cell "" rather than absent.
   *
   * ── THREE STATES, NOT TWO (2026-09-09, and this was a live defect) ──────────────
   * `null` used to mean both "the route answered and this division has no row" AND
   * "the fetch threw, or came back `!ok`" — `useEchDyn` collapsed them with a
   * `.catch(() => null)`. The API had always distinguished them (`ok` and `found` are
   * separate fields, and its own comment says "`found:false` is a real answer … not an
   * error"); the client threw the distinction away.
   *
   * Two consequences, one live and one that was about to be built. Live: during a
   * gateway outage every division read as no-row, so every Contact Us block rendered
   * the `none` treatment with an unsubstituted `{{ContactUrl}}` and nothing anywhere
   * said why. About to be built: Layne's ask to BLOCK a send on a no-row division
   * (`BUG-CONTACTUS-TOKENS-NOT-RENDERED-20260909`) is only safe if a no-row answer and
   * a broken lookup are different values — otherwise an outage becomes a company-wide
   * send freeze. She had named that risk from memory; the code agreed with her.
   *
   * So: `undefined` (key absent) = not fetched yet · `DE_FAILED` = asked and the route
   * failed · `null` = asked, answered, this division genuinely has no row · a row
   * object = the row. `pending` still covers the in-flight gap as it does for `pack`.
   * A gate may block on `null`. It must never block on `DE_FAILED` or `undefined`.
   */
  deRows?: Record<string, DeRowState>;
  /**
   * A feed is still in flight. Dynamic is authoritative INCLUDING when it resolves
   * to nothing — but never while a fetch is pending, or a fresh panel would blank
   * real copy for the second it takes the pack to arrive.
   */
  pending?: boolean;
}

export const EMPTY_CTX: EchCtx = { focus: "", divisionCode: "", picks: [] };

/* ── the community, whichever source has it (monolith __echCommInfo) ─────────
   THE WELCOME HOME CENTER WINS (per Jack). Airtable's street address is the
   community's, which for a masterplan can be a gate or a future phase; SFMC
   carries the WHC address, which is where a customer actually drives and what the
   sales team expects on a Get Directions block. So the pack is preferred whenever
   it has a real address AND a city. Airtable stays the fallback: it covers
   masterplans and communities whose pack leaves the WHC blank. */
export interface CommInfo {
  name: string; phone: string; addr: string; city: string; state: string; zip: string;
  code: string; market: string; whc: boolean;
  /** set only when `addr` was BORROWED — the collection it came from */
  addrFrom?: string;
  /**
   * Set when the row named a city but NO street and nothing else could supply one, so
   * `addr` is genuinely empty rather than merely unread (2026-08-31). The panel can ask
   * for a street instead of silently shipping half an address.
   */
  addrMissing?: boolean;
}

/* ── A MASTERPLAN WITH NO STREET BORROWS ITS COLLECTION'S (Jack, 2026-08-20) ──
   The rule above — an MPC reads its OWN row and never a sub-collection's — holds
   when the row HAS an address. It does not hold when the row has none, which is
   the common case: measured against the live /refdata feed on 2026-08-20,
   **1,729 of 3,086 community rows and 567 of 897 masterplan rows carry no address
   at all**, so Get Directions shipped a blank street on more than half of
   everything. The 2026-08-19 pass answered this by fixing the SENTENCE under the
   empty field ("Nothing on file…"); Jack's answer on 08-20 is to fill it.
   Aloravita is the shape of it: no bare "Aloravita" row and no Aloravita
   masterplan row, while `Signature - Aloravita` (25630 N 77th Drive) and
   `Destiny - Aloravita` (25942 N 77th Drive) both have one — the same street,
   because collections of one masterplan share an entrance.
   The only masterplan→collection link either feed carries is the pageUrl PATH: a
   collection of masterplan X sits at `/<mpc-path>/<collection-slug>`, exactly one
   segment deeper. So the match is on the path, never a substring — "/aloravita/"
   must not be satisfied by "aloravita-heights", the masterplan's OWN page (nothing
   past the slug) is not a collection of itself, and another masterplan's collection
   in the same division-scoped bag is not a candidate at all.
   Ordered by `code`, not by bag order: the community number is the stable Airtable
   identity a human quotes, where a slug is marketing copy that gets re-cut. That
   also makes the answer deterministic whatever order the feed arrives in.
   Called by `useEchDyn`, which folds the result into the pick — so `echCommInfo`
   below needs no special case, it just carries `addrFrom` through. */
/** one child collection of a masterplan — its refdata identity plus its row */
export interface EchCollection {
  /** the Airtable record id it is keyed by in `addr.communities` */
  rid: string;
  /** the community code — the stable identity a human quotes, and the sort key */
  code: string;
  /** the collection's NAME, from the `ids.communities` map (the group heading) */
  name: string;
  row: AddrRow;
}

/**
 * THE ONE MASTERPLAN → COLLECTIONS MATCHER. Extracted from `echBorrowAddr`
 * 2026-08-20 so the address borrow and the FLOORPLAN union ask the same question of
 * the same feed. Two copies of this rule drifting apart is a failure this codebase
 * has already had twice (`ECH_CHOICES` vs `ECH_CTA_PAIRS`) — the borrow's own
 * matcher is subtle enough (path, not substring; exactly one segment deeper; a name
 * slug only as a fallback) that a second hand-rolled version would have differed
 * within a week.
 *
 * WHY THE PATH IS THE LINK. Neither `/refdata` bag carries a parent pointer: `addr.
 * masterplans` rows are often just `{city,state}` and `addr.communities` rows carry
 * `{address,city,state,zip,pageUrl,code,market}`. The only relationship either one
 * expresses is the pageUrl PATH — a collection of masterplan X sits at
 * `/<mpc-path>/<collection-slug>`, exactly one segment deeper. Verified on Aloravita
 * (AZH): `.../peoria/aloravita/signature` code 3357 and `.../peoria/aloravita/destiny`
 * code 3358, with no bare "Aloravita" community row at all.
 *
 * THE THREE EXCLUSIONS ARE LOAD-BEARING, and each one is a real row in the live bag:
 *   · the masterplan's OWN page (nothing past the slug) is not a collection of itself;
 *   · a page two or more segments deeper is a floorplan page, not a collection;
 *   · a SUBSTRING match would let "aloravita-heights" answer for "aloravita".
 *
 * THE SORT IS ON `code`, ASCENDING, NUMERIC WHERE BOTH SIDES ARE NUMBERS, with the
 * string code and then the record id as tie-breakers. Why the code: the community
 * number is the stable Airtable identity a human quotes, where a slug is marketing
 * copy that gets re-cut — and a fixed key makes the answer deterministic whatever
 * order the feed happens to arrive in (the bag is an object, so its key order is the
 * worker's, not ours). The address borrow depends on this being stable: it takes the
 * FIRST addressed collection, so an unstable order would move a street between page
 * loads.
 *
 * Returns [] — never a guess — for a community pick, for a masterplan with no
 * collections, and for a name slug too short to match safely.
 */
/**
 * ── A COMMUNITY NAME AS SFMC AND THE URL SPELL IT (2026-08-22) ──────────────────
 *
 * Airtable's display names carry an AUDIENCE TAG that nothing else uses:
 *   Airtable   "Edenbrooke at Hyland Trail | 55+"
 *   Airtable   "50s - Edenbrooke at Hyland Trail | 55+"   (its collection)
 *   SFMC       "Edenbrooke at Hyland Trail 50s"
 *   the URL    ".../edenbrooke-at-hyland-trail/edenbrooke-at-hyland-trail-50s"
 *
 * Measured live in Jack's own tab (JAX, masterplan pick): `/sfmc-communities?q=`
 * with the tag returns ZERO; without it, both collections come back with real
 * inventory (07344 = 11 plans / 8 homes, 07345). The tag also slugged into
 * `edenbrooke-at-hyland-trail-55`, a path segment that does not exist, which is why
 * the masterplan roll-up found no collections and the listing table sat on Layne's
 * placeholder rows.
 *
 * So: one place that strips the tag, and one place that lists the spellings worth
 * trying. Both are pure string work — no feed, no cache, safe to call anywhere.
 */
export function echNameCore(raw: unknown): string {
  return String(raw == null ? "" : raw)
    .split("|")[0]                       /* the audience tag, and anything after it */
    .replace(/\([^)]*\)/g, " ")          /* "(55+)" spelt the other way */
    .replace(/\s+/g, " ")
    .trim();
}
/**
 * The spellings to try against SFMC, most specific first. A collection named
 * "50s - X | 55+" is "X 50s" over there, so the leading size marker moves to the
 * end; the bare parent is the last resort, because it may return the SIBLINGS too
 * (which is right for a masterplan and wrong for one collection — the caller
 * decides, this only offers).
 */
export function echNameVariants(raw: unknown): string[] {
  const core = echNameCore(raw);
  const out: string[] = [];
  const push = (v: string) => { const t = v.trim(); if (t && out.indexOf(t) < 0) out.push(t); };
  push(String(raw == null ? "" : raw).trim());
  push(core);
  /* "50s - Edenbrooke at Hyland Trail" → "Edenbrooke at Hyland Trail 50s" */
  const lead = /^([0-9]{2,3}['’]?s|[0-9]{2,3}['’]?\s*FE|[A-Za-z]+)\s*-\s*(.+)$/.exec(core);
  if (lead) { push(lead[2] + " " + lead[1]); push(lead[2]); }
  return out;
}

/**
 * ── WHICH MASTERPLAN ARE ALL THESE PICKS COLLECTIONS OF? (2026-08-27) ──────────
 *
 * The inverse of `echCollectionsOf`, and it has to use the same rule, because the rule
 * is the only one there is: "the only masterplan→collection link either feed carries is
 * the pageUrl PATH — a collection of masterplan X sits at `/<mpc-path>/<collection-slug>`,
 * exactly one segment deeper" (see the note above `echBorrowAddr`).
 *
 * WHY IT EXISTS. Picking `Village - Timber Ridge` and `50' & 60' - Timber Ridge` used to
 * drop the send to MARKET level, because the old test was simply "more than one pick".
 * Those are not two communities; they are two collections of Timber Ridge, and the
 * masterplan is a perfectly good answer to which place the send is about. Jack, on the
 * form: "could not know like if i select multiple collections from the same mpc that it
 * would say on community/mpc level?"
 *
 * DELIBERATELY CONSERVATIVE — it answers null unless it is certain:
 *   · fewer than two picks: there is nothing to roll up (a sole pick is already
 *     community level, and a sole masterplan is handled by the borrow helpers);
 *   · any pick that is itself a masterplan: an MPC is not a collection of an MPC, and
 *     mixing levels has no single truth;
 *   · any pick with no pageUrl: no link, no parent — never inferred from names, because
 *     two divisions slug to the same word (the trap `echCollectionsOf` documents);
 *   · anything other than EXACTLY ONE masterplan matching every pick: two candidates
 *     means nested paths and an ambiguous answer, zero means they are unrelated
 *     communities and market really is the right fallback.
 *
 * Returns the masterplan as a PICK, so the caller can adopt it as the thing to resolve
 * against — same shape every other consumer already handles.
 */
export function echSharedParent(
  picks: EchPick[] | null | undefined,
  bagM: Record<string, AddrRow> | null | undefined,
  idsM: Record<string, string> | null | undefined,
): EchPick | null {
  try {
    const ps = picks || [];
    if (ps.length < 2) return null;
    if (ps.some((p) => !p || p.kind !== "CM")) return null;
    const norm = (u: unknown) => String(u == null ? "" : u).toLowerCase().replace(/\/+$/, "");
    const urls = ps.map((p) => norm(p.pageUrl));
    if (urls.some((u) => !u)) return null;

    const bag = bagM || {};
    /* one segment deeper than the masterplan's own path, for EVERY pick */
    const parentOf = (mpPath: string): boolean =>
      !!mpPath && urls.every((u) => {
        if (u.indexOf(mpPath + "/") !== 0) return false;
        return u.slice(mpPath.length + 1).indexOf("/") < 0;
      });

    const hits = Object.keys(bag).filter((rid) => bag[rid] && parentOf(norm(bag[rid].pageUrl)));
    if (hits.length !== 1) return null;

    const rid = hits[0];
    const row = bag[rid];
    const idMap = idsM || {};
    let name = "";
    Object.keys(idMap).forEach((n) => { if (String(idMap[n]) === rid) name = n; });
    if (!name) return null;                 /* a row we cannot name is not usable as a pick */

    return {
      name, kind: "MP",
      code: row.code || undefined,
      pageUrl: row.pageUrl,
      address: row.address, city: row.city, state: row.state, zip: row.zip,
      amenities: row.amenities,
      market: String(row.market || "").split("|")[0].split(/\s*-\s*/)[0].trim().toUpperCase() || undefined,
    };
  } catch {
    /* a malformed bag must never decide the level */
    return null;
  }
}

/**
 * ── AND THE MASTERPLAN ABOVE ONE PICK (2026-08-28) ─────────────────────────────
 *
 * `echSharedParent` answers "which masterplan are all these picks collections of?"
 * and deliberately refuses a single pick, because a sole community IS the send's
 * identity and rolling it up would throw that away.
 *
 * The Grand Opening blocks ask a different question. They declare `CommunityName` AND
 * `MasterplanName`, because the invite reads "at <collection>, in <masterplan>" — so
 * for ONE pick the masterplan is a second fact rather than a replacement, and this
 * returns just its NAME. Nothing resolves an address or a page from it: the community
 * answers those itself.
 *
 * Same rule as everywhere else in this file, because it is the only link the feeds
 * carry: a collection of masterplan X sits at `<mpc-path>/<collection-slug>`, exactly
 * one segment deeper. Same conservatism too — one pick, a real pageUrl, a community
 * (not a masterplan; an MPC is not a collection of an MPC), exactly one matching
 * masterplan row, and a row this app can NAME. Anything else is "".
 */
export function echParentName(
  picks: EchPick[] | null | undefined,
  bagM: Record<string, AddrRow> | null | undefined,
  idsM: Record<string, string> | null | undefined,
): string {
  try {
    const ps = picks || [];
    if (ps.length !== 1) return "";
    const p = ps[0];
    if (!p || p.kind !== "CM") return "";
    const norm = (u: unknown) => String(u == null ? "" : u).toLowerCase().replace(/\/+$/, "");
    const u = norm(p.pageUrl);
    if (!u) return "";
    const bag = bagM || {};
    const hits = Object.keys(bag).filter((rid) => {
      const mp = bag[rid] && norm(bag[rid].pageUrl);
      if (!mp) return false;
      if (u.indexOf(mp + "/") !== 0) return false;
      return u.slice(mp.length + 1).indexOf("/") < 0;
    });
    if (hits.length !== 1) return "";
    const rid = hits[0];
    const idMap = idsM || {};
    let name = "";
    Object.keys(idMap).forEach((n) => { if (String(idMap[n]) === rid) name = n; });
    return name ? String(name).replace(/\*+/g, "").trim() : "";
  } catch {
    return "";
  }
}

export function echCollectionsOf(
  mp: EchPick | null | undefined,
  bag: Record<string, AddrRow> | null | undefined,
  ids: Record<string, string> | null | undefined,
): EchCollection[] {
  try {
    /* only a MASTERPLAN rolls up. A community's collections are itself. */
    if (!mp || mp.kind !== "MP") return [];
    if (!bag) return [];

    /* the masterplan's own path, preferred. A NAME slug is the fallback, not the
       rule, because two divisions can slug to the same word. */
    const ownPath = String(mp.pageUrl || "").toLowerCase().replace(/\/+$/, "");
    /* THE SLUG COMES OFF THE CORE NAME, NOT THE DISPLAY NAME (2026-08-22). With the
       tag left on, "Edenbrooke at Hyland Trail | 55+" slugged to
       `edenbrooke-at-hyland-trail-55` — a segment no Lennar URL has — so a masterplan
       whose own row carries no pageUrl (this one) matched nothing and the whole fill
       came up empty. */
    const slug = echNameCore(mp.name).toLowerCase()
      .replace(/&/g, " and ").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
    if (!ownPath && slug.length < 4) return [];         /* too short to match safely */

    const nameOf: Record<string, string> = {};
    const idMap = ids || {};
    Object.keys(idMap).forEach((n) => { nameOf[String(idMap[n])] = n; });

    const under = (url: string): boolean => {
      const u = String(url || "").toLowerCase().replace(/\/+$/, "");
      if (!u) return false;
      if (ownPath) {
        if (u.indexOf(ownPath + "/") !== 0) return false;
        /* exactly ONE segment deeper — a plan page three levels down is not a
           collection of the masterplan */
        return u.slice(ownPath.length + 1).indexOf("/") < 0;
      }
      const at = u.indexOf("/" + slug + "/");
      if (at < 0) return false;
      /* ONE SEGMENT DEEPER HERE TOO (tightened 2026-08-20 when this became shared).
         The borrow-only version accepted anything past `/<slug>/`, which was harmless
         while the answer was one street address; it is not harmless now, because
         every match becomes a /sfmc-content FETCH and a group heading in the picker,
         and a floorplan page two levels down is neither a collection nor a pack. This
         only ever removes rows that were never collections. */
      const rest = u.slice(at + slug.length + 2);
      return rest.length > 0 && rest.indexOf("/") < 0;
    };

    const order = (a: string, b: string): number => {
      const ca = String(bag[a].code || ""), cb = String(bag[b].code || "");
      const na = Number(ca), nb = Number(cb);
      if (isFinite(na) && isFinite(nb) && na !== nb) return na - nb;
      return ca < cb ? -1 : ca > cb ? 1 : (a < b ? -1 : 1);
    };
    const out = (rid: string): EchCollection =>
      ({ rid, code: String(bag[rid].code || ""), name: nameOf[rid] || String(bag[rid].code || ""), row: bag[rid] });

    const byUrl = Object.keys(bag).filter((k) => bag[k] && under(bag[k].pageUrl || ""));
    if (byUrl.length) return byUrl.sort(order).map(out);

    /* ── NAME FALLBACK, WHEN NO CHILD HAS A pageUrl (2026-08-22) ──────────────────
       Jack, on ATL / Envoy at Ponce: "i still think the mpc isn't pulling in homesite
       info… or any in preivew." Measured on the live feed: BOTH of that masterplan's
       collections — "Single-Family Detached - Envoy at Ponce" and "Townhomes - Envoy at
       Ponce" — carry NO pageUrl in refdata, and the masterplan's own row carries
       neither a url nor a code. The matcher above is pageUrl-only, so it resolved zero
       collections, `collPacks` stayed null and the listing table kept its placeholder
       rows. Nothing about that is visible to a requester: the pick looks fine and the
       table is simply empty.
       The name is the other thing we know, and Airtable's own convention puts the
       masterplan IN the collection's name ("<variant> - <masterplan>"). Matching on the
       CORE name (tags like "| 55+" stripped, same `echNameCore` the slug uses) recovers
       exactly those rows.
       TWO GUARDS, both learned from the tests this first broke:
       · it runs only when the URL pass found NOTHING, so no masterplan that resolves
         today can change; and
       · it only considers rows with NO pageUrl AT ALL. A row that HAS a url has already
         been judged by `under()` — re-judging it by name would undo exactly the
         exclusions that matter: a floorplan page two segments deep ("deep - Granary
         Park") and a same-slug community in another market ("Aloravita Signature")
         both carry urls and must stay out.
       A row with no code is dropped too — there would be nothing to fetch a pack with. */
    const core = echNameCore(mp.name).trim().toLowerCase();
    if (core.length < 4) return [];
    const byName = Object.keys(bag).filter((k) => {
      const r = bag[k];
      if (!r || !String(r.code || "").trim()) return false;
      if (String(r.pageUrl || "").trim()) return false;     /* already judged by url */
      const nm = String(nameOf[k] || "").trim().toLowerCase();
      if (!nm || nm === core) return false;                 /* the masterplan itself */
      return echNameCore(nm).toLowerCase().indexOf(core) > -1;
    });
    return byName.sort(order).map(out);
  } catch { return []; }
}

/**
 * AN AREA OF INTEREST → ITS COMMUNITIES. 2026-08-22.
 *
 * The AOI tier was the one Step-1 focus that could not auto-fill, because nothing in
 * the feed connected an AOI to the communities inside it. It does now: `/refdata`
 * schema 8 carries each community's own `aoi` (see `AddrRow`).
 *
 * Returns the SAME `EchCollection[]` shape a masterplan's collections do, so an AOI
 * request rides the plumbing that already exists — one `/sfmc-content` per community
 * into `EchCtx.collPacks`, the grouped listing fill, and the picker's scope — instead
 * of a second parallel path that would drift from it.
 *
 * MATCHING. Case- and space-insensitive, and the stored value is SPLIT on commas
 * before comparing: "Harrah, East OKC" is one community in two AOIs, and comparing
 * the whole string would silently drop it. A slash is NOT a separator — "Summerville/
 * Goose Creek" is one AOI's real name.
 *
 * ORDER + CAP. Alphabetical by name, capped at 10 like the masterplan path (the
 * listing table has ten rows and the fill applies its own 5-per-community rule).
 * Alphabetical rather than by inventory because inventory is not known until the packs
 * land — the fill re-orders by it afterwards.
 */
export function echCommsInAoi(
  aoi: string,
  bag: Record<string, AddrRow> | null | undefined,
  ids: Record<string, string> | null | undefined,
): EchCollection[] {
  try {
    const want = String(aoi || "").trim().toLowerCase();
    if (!want || !bag) return [];
    const nameOf: Record<string, string> = {};
    const idMap = ids || {};
    Object.keys(idMap).forEach((n) => { nameOf[String(idMap[n])] = n; });
    const hit = (row: AddrRow): boolean => String(row.aoi || "")
      .split(",")
      .map((x) => x.trim().toLowerCase())
      .filter(Boolean)
      .some((x) => x === want);
    return Object.keys(bag)
      .filter((rid) => bag[rid] && bag[rid].code && hit(bag[rid]))
      .map((rid) => ({ rid, code: String(bag[rid].code || ""), name: nameOf[rid] || String(bag[rid].code || ""), row: bag[rid] }))
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
      .slice(0, 10);
  } catch { return []; }
}

export function echBorrowAddr(
  picks: EchPick[],
  bag: Record<string, AddrRow>,
  ids: Record<string, string>,
): { addr: string; city: string; state: string; zip: string; from: string } | null {
  try {
    /* THE MULTI-PICK GATE LIVES HERE, not in the caller (the lesson of code review
       #11 above): one community's street on a five-community send is wrong, and Get
       Directions' layout is locked so it cannot list several. */
    if (!picks || picks.length !== 1) return null;
    const mp = picks[0];
    if (!mp) return null;
    if (String(mp.address || "").trim()) return null;   /* it owns one; leave it alone */

    /* THE MATCH IS `echCollectionsOf`'s, not a second copy of it (2026-08-20). The
       only thing the borrow adds is "…and it must have a street": a collection with a
       blank address is a useless donor, but it is still a perfectly good FLOORPLAN
       source, which is precisely why the two callers had to stop being one function.
       Ordered by code, so the first addressed collection is a stable answer. */
    const hit = echCollectionsOf(mp, bag, ids).find((c) => String(c.row.address || "").trim());
    if (!hit) return null;

    const row = hit.row;
    /* city/state/zip travel WITH the street — a street in one town under another
       town's name is worse than a blank */
    return {
      addr: String(row.address || ""),
      city: String(row.city || ""),
      state: String(row.state || ""),
      zip: String(row.zip || ""),
      from: hit.name,
    };
  } catch { return null; }
}

/**
 * A MASTERPLAN WITH NO AMENITIES OF ITS OWN BORROWS ITS COLLECTIONS' (2026-08-21).
 *
 * WHY IT IS NEEDED. `addr.masterplans` rows frequently carry only `{city,state}` —
 * there is no Amenities field on the Masterplans table at all — while every child
 * collection carries the full multi-select. Live: JAX's "Granary Park" masterplan
 * has none, and all four of its collections (40s / 50s / 60s / 50s Phase 3) carry
 * the identical nine ("Swimming Pool|Clubhouse|Exercise/Fitness Center|Picnic Area|
 * Dog Park|Playground|Pond|Waterfront Homesite(s)|Park(s)"). Jack picked the
 * masterplan and the amenity block came up empty, which is the bug this fixes — and
 * it only became VISIBLE once "nothing resolvable" started blanking the block
 * instead of leaving the shipped placeholders sitting there.
 *
 * THE UNION, NOT THE FIRST COLLECTION. The address borrow takes the first addressed
 * collection because a send has one street; amenities are a set, and a masterplan
 * email speaks for the whole community, so a pool in one phase belongs in it. Order
 * is preserved as encountered and de-duped case-insensitively; `amenFromAirtable`
 * re-orders to the icon library afterwards, so the caller's order only decides which
 * survive the trim to the block's cell count.
 *
 * Same matcher as the address borrow (`echCollectionsOf`) on purpose — the comment
 * on that function explains what two drifting copies of this rule cost.
 */
export function echBorrowAmenities(
  picks: EchPick[],
  bag: Record<string, AddrRow>,
  ids: Record<string, string>,
): { amenities: string; from: string[] } | null {
  try {
    /* one pick only, for the same reason the address borrow gates on it: a
       multi-community send has no single answer */
    if (!picks || picks.length !== 1) return null;
    const mp = picks[0];
    if (!mp) return null;
    if (String(mp.amenities || "").trim()) return null;   /* it owns some; leave it */

    const seen: Record<string, 1> = {};
    const vals: string[] = [];
    const from: string[] = [];

    /**
     * COLLECTIONS BY NAME, ONLY WHEN THE PATH FINDS NONE (2026-08-21, second pass —
     * Jack: "some masterplans still aren't pulling from collections").
     *
     * `echCollectionsOf` links collection to masterplan through the pageUrl PATH, and
     * falls back to the masterplan's NAME SLUG when the masterplan row carries no URL.
     * Measured across JAX's 16 masterplans: 6 resolved nothing. Three were Airtable
     * DATA gaps — collections stating TBD or None, which must stay blank because there
     * is nothing to show (Diamond Springs, Hardwick Farms, Saratoga Springs). The other
     * three were MATCHING failures, where marketing's URL and the Airtable name simply
     * disagree:
     *   Edenbrooke at Hyland Trail | 55+   slug `edenbrooke-at-hyland-trail-55`
     *                                      path  …/edenbrooke-at-hyland-trail/…
     *   Stillwater | Active Adult 55+      slug `stillwater-active-adult-55`
     *                                      path  …/stillwateractiveadult55/…
     *   Kings Creek                        slug `kings-creek`
     *                                      path  …/kings-creek2/…
     * A "| 55+" qualifier, a run-together segment, a numeric suffix — each defeats the
     * slug, and Edenbrooke's collections carry a full amenity list that was being
     * thrown away.
     *
     * THE NAME IS THE RELIABLE LINK: collections are named "<collection> - <masterplan>",
     * the convention `echCollectionsOf`'s own header documents. Matched as an EXACT
     * suffix, so "40s - Edenbrooke at Hyland Trail | 55+" cannot answer for a
     * masterplan called "Hyland Trail" the way a substring would.
     *
     * TWO LIMITS KEEP IT CONSERVATIVE:
     *  · it runs ONLY when the path match found NOTHING. Where the path works it stays
     *    authoritative, including its own exclusions, so a masterplan that resolves
     *    today behaves identically.
     *  · a row whose URL puts the masterplan slug two or more segments up is a
     *    FLOORPLAN page, not a collection, and is skipped even if the name matches.
     *
     * DELIBERATELY LOCAL TO THE AMENITY BORROW. `echCollectionsOf` is shared with the
     * ADDRESS borrow and the floorplan union; widening it would change which street and
     * which plans a masterplan email shows — a far larger blast radius than was asked
     * for. Promoting this into the shared matcher is a separate, tested change.
     */
    const byName = (): { rid: string; code: string; name: string; row: AddrRow }[] => {
      const nm = String(mp.name || "").trim();
      const suffix = " - " + nm;
      if (nm.length < 4) return [];
      const slug = nm.toLowerCase()
        .replace(/&/g, " and ").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
      /* the floorplan-page exclusion, by the row's OWN url */
      const isPlanPage = (url: string): boolean => {
        const u = String(url || "").toLowerCase().replace(/\/+$/, "");
        if (!u || !slug) return false;
        const at = u.indexOf("/" + slug + "/");
        if (at < 0) return false;
        return u.slice(at + slug.length + 2).indexOf("/") > -1;
      };
      const idMap = ids || {};
      const out: { rid: string; code: string; name: string; row: AddrRow }[] = [];
      Object.keys(idMap).forEach((n) => {
        const rid = String(idMap[n]);
        const row = bag[rid];
        if (!row) return;
        const name = String(n);
        if (name.length <= suffix.length) return;      /* the masterplan alone is not its own collection */
        if (name.slice(-suffix.length).toLowerCase() !== suffix.toLowerCase()) return;
        if (isPlanPage(row.pageUrl || "")) return;
        out.push({ rid, code: String(row.code || ""), name, row });
      });
      /* the same ordering contract as echCollectionsOf — by code, numeric when it can be */
      return out.sort((a, b) => {
        const na = Number(a.code), nb = Number(b.code);
        if (isFinite(na) && isFinite(nb) && na !== nb) return na - nb;
        return a.code < b.code ? -1 : a.code > b.code ? 1 : (a.rid < b.rid ? -1 : 1);
      });
    };

    const byPath = echCollectionsOf(mp, bag, ids);
    (byPath.length ? byPath : byName()).forEach((c) => {
      const raw = String(c.row.amenities || "").trim();
      if (!raw) return;
      let used = false;
      raw.split("|").forEach((v) => {
        const t = v.trim();
        if (!t) return;
        const k = t.toLowerCase();
        /* "None"/"TBD" are not amenities — carrying them would make a masterplan
           whose collections all say None look like it stated something */
        if (k === "none" || k === "tbd") return;
        if (seen[k]) return;
        seen[k] = 1; vals.push(t); used = true;
      });
      if (used) from.push(c.name);
    });
    if (!vals.length) return null;
    return { amenities: vals.join("|"), from };
  } catch { return null; }
}

export function echCommInfo(ctx: EchCtx): CommInfo | null {
  /* ── SEVERAL COLLECTIONS OF ONE MASTERPLAN ARE ONE PLACE (2026-08-27) ─────────
     The rule below — "a multi-community send must never answer with one community's
     address" — is intact: with a shared parent we answer with the MASTERPLAN's own
     row, not with pick[0]'s. `ctx.parent` is only ever set when every pick is a
     collection of that one masterplan (`echSharedParent`), so there is a single
     correct address to give. `pc` (the WHC pack branch) is deliberately left on the
     literal single-pick test: `ctx.pack` is null for several picks by design, so the
     parent case takes the Airtable branch below, which is where masterplan rows have
     always been read from. */
  const one = ctx.picks.length === 1 || !!ctx.parent;
  const pick = ctx.parent || (ctx.picks.length === 1 ? ctx.picks[0] : null);
  /* ONE PICK OR NO PACK (code review 2026-08-19 #11). The header comment above says
     a multi-community send must never answer with one community's address, and the
     Airtable branch below enforced it — but the WHC branch read `ctx.pack` with no
     pick check, so as a pure function this happily returned "9 WHC Way" for a
     five-community send. It was safe only because `useEchDyn` nulls `pack` when
     picks.length !== 1: the invariant lived in the caller, not in the function, and
     this function is exported. The gate now lives here too. */
  const pc = one ? ctx.pack?.community || null : null;
  if (pc && pc.whcAddress && (pc.whcCity || pc.whcState)) {
    return {
      name: pc.name || pick?.name || "", phone: pc.phone || "",
      addr: pc.whcAddress, city: pc.whcCity || "", state: pc.whcState || "", zip: pc.whcZip || "",
      code: pc.code || pick?.code || "", market: pick?.market || "", whc: true,
    };
  }
  /* Airtable next — it has the location for masterplans and for communities whose
     SFMC content pack leaves city/state blank. MULTI-COMMUNITY SENDS HAVE NO
     SINGLE ADDRESS (per Jack): one community's street address on a five-community
     send is simply wrong, and the Get Directions layout is locked so it can't list
     several. With more than one pick this yields nothing and echLevel() has
     already fallen back to market. */
  if (pick && (pick.address || pick.city)) {
    /* ── A CITY WITH NO STREET IS NOT AN ANSWER (2026-08-31, ATL pilot) ───────────
       This branch accepted the Airtable row on `pick.city` ALONE and returned with
       `addr: ""`, which is how Get Directions shipped city/state/zip and no street.
       The street was usually right there: the WHC branch above only fires when the
       pack has a street AND a city or state, so a pack with a street and neither fell
       through to here and its address was never read. Roughly 56% of community rows
       carry no street of their own, so this is the common path, not the edge.

       So MERGE rather than pick a winner — Airtable keeps the city it knows, the pack
       supplies the street. `whc: true` because the street genuinely is the Welcome
       Home Center's, and callers key provenance off that flag. */
    if (!pick.address && pc?.whcAddress) {
      return {
        name: pick.name || pc.name || "", phone: pc.phone || "",
        addr: pc.whcAddress,
        city: pick.city || pc.whcCity || "", state: pick.state || pc.whcState || "", zip: pick.zip || pc.whcZip || "",
        code: pick.code || pc.code || "", market: pick.market || "", whc: true,
        ...(pick.addrFrom ? { addrFrom: pick.addrFrom } : {}),
      };
    }
    return {
      name: pick.name, phone: pc?.phone || "",
      addr: pick.address || "", city: pick.city || "", state: pick.state || "", zip: pick.zip || "",
      code: pick.code || "", market: pick.market || "", whc: false,
      /* carried, never derived here: `echBorrowAddr` decided it and useEchDyn folded
         it into the pick, so a borrowed street can never read as the masterplan's own */
      ...(pick.addrFrom ? { addrFrom: pick.addrFrom } : {}),
      /* no street here and none anywhere else — say so rather than let the caller read
         an empty string as "not looked up yet" */
      ...(!pick.address ? { addrMissing: true } : {}),
    };
  }
  if (pc) {
    return {
      name: pc.name || pick?.name || "", phone: pc.phone || "",
      addr: pc.whcAddress || "", city: pc.whcCity || "", state: pc.whcState || "", zip: pc.whcZip || "",
      code: pc.code || pick?.code || "", market: pick?.market || "", whc: false,
    };
  }
  if (pick) return { name: pick.name, phone: "", addr: "", city: "", state: "", zip: "", code: pick.code || "", market: pick.market || "", whc: false };
  return null;
}

/* ── CONTACT US IS THE DIVISION'S OWN BLOCK, ISC PHOTO AND ALL ───────────────
   Port of the monolith's `__iscFor` swap inside `__echFillAll` (index.html
   L11670–11701 + L11794–11808). Jack, 2026-08-19: "contact us should have the
   isc photos as well."

   Layne's guardrail on ECH_ContactUsDefault already says the block "is selected
   automatically from the division code", and SFMC has the mapping: a DE points
   each division at its own Contact Us layoutblock, which carries that division's
   real ISC photograph. Those are `layoutblock` assets, so their markup lives in
   TOP-LEVEL `slots` rather than `views.html.content` — reading the content alone
   returns a few hundred bytes of shell with no photo, which is why this looked
   impossible before the worker learned to assemble the slots. `/sfmc-isc?div=`
   returns the assembled markup plus an `imgCount`.

   TWO RULES, BOTH THE MONOLITH'S AND BOTH LOAD-BEARING:
     1. only an ASSEMBLED block is accepted (`ok && html && imgCount`). The bare
        shell would blank a working block — that guard lives in `useEchDyn`,
        which is what talks to the route, so `ctx.iscHtml` is already either real
        markup or "".
     2. the requester's URL is rewritten INTO the swapped markup. Contact Us is
        in ECH_MOSTLY_LOCKED, meaning its one editable thing is `ContactUrl` —
        and the division's own block carries the division's own /contact href, so
        without this rewrite that "editable" field would be a lie: typing in it
        would change nothing on the canvas or in the export. Token substitution
        cannot do the job either, because the swapped markup is Content Builder's
        and has no `{{ContactUrl}}` in it.

   Deliberately NOT a field fill: ECH_ContactUsDefault has no ECH_DYN entry, and
   the monolith `return`s out of the block before its fill runs. This is markup. */
export const ECH_ISC_RE = /^ECH_ContactUs/i;
/* ── OUTLOOK DARK: `mso-color-alt` DEFEATS THE `keep-white` TEXT-FILL TRICK (2026-09-01/02) ──
   Jie's audits (LTGME-627/628, the 44-render Grand Opening docx) showed the Contact Us
   label vanishing in Outlook 365/2021 dark: navy pill, navy text. Measured on the live
   default block (ECH_ContactUsDefault, Litmus 22645207 v6→v8): the VML fallback span
   carried BOTH `class="keep-white"` and `mso-color-alt:#ffffff`, and the alt colour wins
   over the gradient text-fill in Outlook's dark inversion. Dropping `mso-color-alt` and
   keeping `keep-white` renders white-on-navy — that is the fix applied to the default block.
   THE DEFAULT IS NOT WHAT SHIPS. `/sfmc-isc?div=` swaps in the DIVISION's own Contact Us
   layoutblock (AZH, ATL, OCH … measured 2026-09-02: every one still carries the old span,
   no `keep-white`), so Jie's 9:15 AZH test still showed the defect eighteen minutes after
   the default block was fixed. Normalising the swapped markup here fixes every division
   in one place instead of ~47 Content Builder edits, and is idempotent on a block that
   already has the fix. Only a WHITE alt colour on a white span is touched — the Cobalt
   `keep-cobalt` pairing on the Grand Opening pills is a different treatment and is left
   alone. The head's `<!--[if gte mso 16]>` style block defines `.keep-white`. */
export function echIscOutlookDark(html: unknown): string {
  return String(html ?? "").replace(/<span\b([^>]*?)\sstyle="([^"]*)"([^>]*)>/gi, (whole: string, pre: string, style: string, post: string) => {
    if (!/mso-color-alt:\s*#fff(?:fff)?\b/i.test(style)) return whole;
    if (!/(^|;)\s*color:\s*#fff(?:fff)?\s*(;|$)/i.test(style)) return whole;
    const cleaned = style.replace(/\s*mso-color-alt:\s*#fff(?:fff)?\s*;?/gi, "").replace(/\s*\n\s*/g, " ").replace(/\s{2,}/g, " ").trim();
    const attrs = (pre || "") + (post || "");
    if (/\bclass="/i.test(attrs)) {
      const withCls = attrs.replace(/\bclass="([^"]*)"/i, (m, c) => (/\bkeep-white\b/.test(c) ? m : 'class="' + (c ? c + " " : "") + 'keep-white"'));
      return "<span" + withCls + ' style="' + cleaned + '">';
    }
    return '<span class="keep-white"' + attrs + ' style="' + cleaned + '">';
  });
}

/* ── THE TEAM PHOTOS CAN BE LEFT OUT (2026-09-23, Justin Bradford) ───────────────
   A request came back asking for every ISC photo to be removed, and the Studio had no
   way to do it: Contact Us is locked except its links, so the change landed on Justin
   as a revision. The photos are the one part of the division's block a requester has a
   real reason to drop (a photo of someone who has left, a send where faces don't fit).

   Every division block that carries photos wraps them in Layne's own markers —
   `<!--BEGIN Com contact images-->` … `<!--END Com contact images-->` — measured on
   /sfmc-isc 2026-09-23: NEV (4 headshots), CHB (5), SEA (4), BOI (3), FNO (3), ATL and
   OCH (one team photo), every photo and every Outlook `v:oval` inside the pair. The
   blocks with no photos (JAX, AZH, HOU, IND, MIN: the house icon only) carry no pair at
   all, so the choice is offered only where there is something to remove.

   The cut is the pair and everything between it: photos, the Outlook versions, the row's
   own spacing. Heading, copy, buttons and links are outside the pair and never move.
   The flag lives on the block (`__iscNoPhotos`), and the cut is re-made from the
   division's untouched markup on every fill, so switching back restores the photos
   exactly as the division set them. */
export const ECH_ISC_NO_PHOTOS = "__iscNoPhotos";
/** the requester asked for this Contact Us block to ship without its team photos */
export const echIscNoPhotos = (data: Record<string, unknown> | undefined): boolean =>
  !!data && String(data[ECH_ISC_NO_PHOTOS] ?? "") === "1";
/** [start, end) of the marked photo region INCLUDING both markers, or null */
function iscPhotoSpan(html: string): [number, number] | null {
  const b = CONTACT_IMAGES_BEGIN.exec(html);
  if (!b) return null;
  const rest = html.slice(b.index + b[0].length);
  const e = CONTACT_IMAGES_END.exec(rest);
  if (!e) return null;
  return [b.index, b.index + b[0].length + e.index + e[0].length];
}
/** the division's block marks a photo region AND there is a photo in it */
export function echIscHasPhotos(html: unknown): boolean {
  const h = String(html ?? "");
  const s = iscPhotoSpan(h);
  return !!s && /<img\b|<v:fill\b/i.test(h.slice(s[0], s[1]));
}
/** the markup with every marked photo region removed; unmarked markup is returned as is */
export function echIscStripPhotos(html: unknown): string {
  let h = String(html ?? "");
  for (let s = iscPhotoSpan(h), guard = 0; s && guard < 10; s = iscPhotoSpan(h), guard++) {
    h = h.slice(0, s[0]) + h.slice(s[1]);
  }
  return h;
}

/** the swapped markup with the requester's link in it, or "" when there is none */
export function echIscHtml(html: unknown, contactUrl: unknown): string {
  let want = String(html ?? "");
  if (!want) return "";
  const u = withScheme(contactUrl);   /* `www.…` is an answer — see echContact.withScheme */
  /* A HOST IS REQUIRED, not just a scheme — one character tighter than the
     monolith's `/^https?:\/\//` and deliberately so. Every ECH url field SHIPS the
     bare placeholder "https://", which passes the monolith's test, so its rewrite
     turns the division's working contact link into `href="https://"` on any block
     nobody has typed into. Here the placeholder is treated as no answer, which it
     is, and the division's own href stands. */
  if (/^https?:\/\/[^\s/]/i.test(u))
    want = want.replace(/href="https?:\/\/[^"]*\/contact[^"]*"/gi, 'href="' + u.replace(/"/g, "&quot;") + '"');
  return want;
}
/**
 * the `__html` patch for one Contact Us block, or null when nothing changes
 *
 * ── EVERY BUTTON GETS ITS OWN LINK (2026-08-28, NEV) ──────────────────────────
 * `echIscHtml` rewrites only an href containing `/contact`. On NEV's two-button
 * block ("Call Us" → `tel:`, "Email Us" → `mailto:`) that matches NEITHER, so the
 * one field the panel offered could not change anything — which is the half of
 * NEV's report that is a genuine defect rather than a block-schema fact.
 *
 * `contactRewrite` maps field N onto button N, which is the only mapping that
 * stays true when the number of buttons is a property of the division's own
 * layoutblock. Runs FIRST; the `/contact` pass still runs after it so a block
 * carrying a single contact link behaves exactly as before, and an untyped field
 * leaves its button alone either way.
 */
export function echIscPatch(m: DynBlock, ctx: EchCtx): Record<string, string> | null {
  /* ── A BLOCK THAT DECLARES A DATA SOURCE IS NOT SWAPPED (2026-09-02) ─────────────
     Layne's rule, from the Contact Us ask: "the two must never both be active. If both
     run, a division gets its old swapped-in block AND the new data, and nobody will be
     able to tell which won." This makes that true by construction rather than by a
     flag someone has to flip on the right day: the moment a Contact Us block's header
     declares `dataSource`, the per-division markup swap stands down for THAT block, and
     the declared fields are filled from the DE row instead. A block that does not
     declare one — every division today — keeps the swap exactly as it was. Per block,
     automatic, no coordinated release. */
  if (echDataSource(echNameOf(m.type))) return null;
  /* ── NO DIVISION BLOCK ≠ NO REWRITE (2026-08-31, caught by the full review build) ──
     When the division swap is empty — the division has no Contact Us layoutblock, or
     `/sfmc-isc` returned nothing — this used to `return null`, which silently dropped
     the requester's typed URL on the floor: the DEFAULT block's placeholder
     `href="#"` shipped in the export even though the panel had accepted the value and
     cleared the required check (JAX Granary Park test, asset 1031696). The default
     markup is in `m.data.__html` (registry `defaults`), so the per-button rewrite
     applies to it exactly as it does to a swapped block. With nothing typed the
     rewrite is a no-op and the `__html !== want` guard below patches nothing. */
  /* the DIVISION markup is normalised for Outlook dark on the way in (echIscOutlookDark);
     the default block is already fixed in Content Builder and is left exactly as placed,
     so a no-swap, nothing-typed pass still writes nothing */
  const swapped = echIscOutlookDark(ctx.iscHtml ?? "");
  /* the DEFAULT block ships its Stensul tokens as literal text and broken images when
     no division block swaps in — strip them (echStripUnresolved); a division's own
     markup is fixed values and is left alone */
  const whole = swapped || echStripUnresolved(String(m.data.__html ?? ""));
  if (!whole) return null;
  /* the requester's "No photos" (2026-09-23) — cut from the untouched markup, every pass */
  const base = echIscNoPhotos(m.data as Record<string, unknown>) ? echIscStripPhotos(whole) : whole;
  const perButton = contactRewrite(base, m.data as Record<string, unknown>);
  /* ── SEED THE FIELD FROM THE BLOCK'S OWN HREF (2026-08-31, ATL pilot) ──────────
     Reported as the contact-us block needing its link re-pasted every time. The
     division's own layoutblock arrives with its contact href ALREADY in the markup,
     but `ContactUrl` stayed empty — so the panel showed a blank required field and
     preflight read it as a dead link, demanding a link that was demonstrably already
     there and would have sent correctly untouched.
     Read it out once so the field shows what will actually send; the requester can
     then edit it or leave it. Only when the field is empty — a typed value is theirs
     and is never overwritten. */
  let seeded = "";
  if (!String(m.data.ContactUrl || "").trim()) {
    /* seed only from a DIVISION's own markup — the default block's placeholder
       hrefs ("#", "https://#") are nobody's link and must never seed the field */
    const src = swapped ? String(perButton || swapped) : "";
    /* ── ONE LINK ONLY (2026-08-31, caught by NEV's two-button test) ─────────────
       `echIscHtml` rewrites the block's contact href to the single value it is given,
       so seeding a MULTI-button block from its first href made every button point at
       that one link — it overwrote NEV's "Call Us" `tel:` and "Email Us" `mailto:`
       with the market rfi url. On those blocks the per-button fields (ContactUrl,
       ContactUrl2, …) are what govern, and one field has nothing sensible to say
       about several buttons, so there is nothing to seed. Single-link blocks — the
       ones the ATL report was about — are unaffected. */
    /* EVERY href counts, not just the absolute ones: a `tel:` and a `mailto:` are two
       buttons whether or not they are http, and counting only https made a three-link
       block look like a one-link block and seed anyway */
    const links = String(src).match(/href="[^"]+"/gi) || [];
    if (links.length === 1) {
      const hit = /href="(https?:\/\/[^"]*\/contact[^"]*)"/i.exec(src) || /href="(https?:\/\/[^"]+)"/i.exec(src);
      if (hit && hit[1]) seeded = hit[1].replace(/&amp;/g, "&");
    }
  }
  const want = echIscHtml(perButton, seeded || m.data.ContactUrl);
  const out: Record<string, string> = {};
  if (want && m.data.__html !== want) out.__html = want;
  if (seeded) out.ContactUrl = seeded;
  return Object.keys(out).length ? out : null;
}

/* ── THE RIGHT LENNAR.COM PAGE FOR THE SCOPE (per Jack) ─────────────────────
   One community → its own page. Anything broader — several communities, a market
   send, a division send, an AOI — must NOT link to one community's page; it links
   to the MARKET page, which is the same URL truncated to
   /new-homes/<state>/<market>/. Several communities only collapse to a market page
   if they SHARE one — otherwise this returns nothing rather than picking one
   arbitrarily. */
export const LEN_OK = /^https:\/\/([a-z0-9-]+\.)*lennar\.com(\/|$)/i;
export function echMarketUrl(u: string): string {
  const m = /^(https:\/\/[^/]+\/new-homes\/[^/]+\/[^/]+)(\/|$)/i.exec(String(u || ""));
  return m ? m[1] + "/" : "";
}
/** the STATE segment of a lennar.com community/market url: /new-homes/<state>/ */
export function echStateUrl(u: string): string {
  const m = /^(https:\/\/[^/]+\/new-homes\/[^/]+)(\/|$)/i.exec(String(u || ""));
  return m ? m[1] + "/" : "";
}

/** the two-letter state a lennar.com page url is in ("" when unknown): /new-homes/california/… → "CA" */
/**
 * The masterplan page behind a set of collection urls (2026-09-11). Every url must be a
 * lennar.com community page, they must all share ONE parent path, and that parent's last
 * slug must be the masterplan's own name slugified — "Brightlyn at SilverLeaf" ↔
 * `…/st-johns/brightlyn-at-silverleaf/brightlyn-at-silverleaf-50s`. Anything looser ("" ,
 * mixed parents, a parent that is just the city) returns "" — a guessed slug 404s, and a
 * dead CTA is the most expensive defect this form ships.
 */
export function echSharedParentUrl(name: unknown, urls: unknown[]): string {
  const slug = (v: unknown) => String(v || "").toLowerCase().replace(/&/g, " and ").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  const want = slug(name);
  if (!want) return "";
  const parents = (urls || [])
    .map((u) => String(u || "").replace(/[?#].*$/, "").replace(/\/+$/, ""))
    .filter((u) => LEN_OK.test(u))
    .map((u) => u.replace(/\/[^/]+$/, ""));
  if (!parents.length) return "";
  const first = parents[0];
  if (!parents.every((p) => p === first)) return "";
  /* a masterplan page sits at /new-homes/<state>/<market>/<city>/<name> — never shallower */
  if (!/\/new-homes\/[^/]+\/[^/]+\/[^/]+\/[^/]+$/.test(first)) return "";
  return first.replace(/^.*\//, "") === want ? first : "";
}

export function echStateOfUrl(u: unknown): string {
  const m = /\/new-homes\/([a-z-]+)(\/|$)/i.exec(String(u || ""));
  if (!m) return "";
  return US_STATE_ABBR[m[1].toLowerCase()] || "";
}
const US_STATE_ABBR: Record<string, string> = {
  alabama: "AL", alaska: "AK", arizona: "AZ", arkansas: "AR", california: "CA", colorado: "CO", connecticut: "CT",
  delaware: "DE", florida: "FL", georgia: "GA", hawaii: "HI", idaho: "ID", illinois: "IL", indiana: "IN", iowa: "IA",
  kansas: "KS", kentucky: "KY", louisiana: "LA", maine: "ME", maryland: "MD", massachusetts: "MA", michigan: "MI",
  minnesota: "MN", mississippi: "MS", missouri: "MO", montana: "MT", nebraska: "NE", nevada: "NV", "new-hampshire": "NH",
  "new-jersey": "NJ", "new-mexico": "NM", "new-york": "NY", "north-carolina": "NC", "north-dakota": "ND", ohio: "OH",
  oklahoma: "OK", oregon: "OR", pennsylvania: "PA", "rhode-island": "RI", "south-carolina": "SC", "south-dakota": "SD",
  tennessee: "TN", texas: "TX", utah: "UT", vermont: "VT", virginia: "VA", washington: "WA", "west-virginia": "WV",
  wisconsin: "WI", wyoming: "WY", "district-of-columbia": "DC",
};

/** every lennar.com page url the context knows about, most specific first */
function scopeUrls(ctx: EchCtx): { picks: string[]; packs: string[] } {
  const ok = (u: unknown) => { const v = String(u || ""); return v && LEN_OK.test(v) ? v : ""; };
  const picks = (ctx.picks || []).map((p) => ok(p.pageUrl)).filter(Boolean);
  /* the RESOLVED packs' own community pages. These exist at market and AOI level — where
     no pick carries a url — and were ignored, which is the whole reason a Market send
     could reach this function and get "" (2026-08-27). */
  const packs: string[] = [];
  const add = (u: unknown) => { const v = ok(u); if (v && packs.indexOf(v) < 0) packs.push(v); };
  add(ctx.pack?.community?.url);
  (ctx.collPacks || []).forEach((p) => add(p && p.C && p.C.community && p.C.community.url));
  return { picks, packs };
}

/**
 * ── THE CTA'S DESTINATION, MOST SPECIFIC PAGE WE CAN HONESTLY NAME (2026-08-27) ──
 *
 * The chain is community → masterplan → market → division, and every tier is derived from
 * a url the feeds actually returned. Nothing is synthesised from a name: a slug built out
 * of "Green Cove Springs" looks plausible and 404s, and a dead CTA is the single most
 * expensive defect this form ships.
 *
 * WHAT CHANGED AND WHY. The old version answered a single community's page or a SHARED
 * market page, and otherwise "" — and `echDynVal` writes that empty over the block's
 * shipped "https://", so a Market or Division send left `href=""` on every button unless
 * the requester noticed and typed one. Two tiers were missing underneath:
 *
 *   · MASTERPLAN — a masterplan pick's own page, and the masterplan behind several
 *     collections (`ctx.parent`, see `echSharedParent`).
 *   · DIVISION — the state page, `/new-homes/<state>/`, which is a real Lennar page and
 *     the honest answer for a send that spans markets. Below that, the national
 *     find-a-home page rather than nothing.
 *
 * …and the market tier only ever looked at Step 1's PICKS, while a Market or AOI send
 * resolves communities into `pack` / `collPacks` and carries their pages all along.
 */
export function echScopeUrl(ctx: EchCtx, lvl: string): string {
  const { picks: pickUrls, packs } = scopeUrls(ctx);

  /* 1 · COMMUNITY — one pick, its own page */
  if (lvl === "community") {
    /* several collections of ONE masterplan → the masterplan's page, not the market's.
       Checked first, because the collapse below would walk these same urls up to
       /new-homes/<state>/<market>/ and lose a perfectly good page. */
    const parentUrl = String((ctx.parent && ctx.parent.pageUrl) || "");
    if (ctx.parent && LEN_OK.test(parentUrl)) return parentUrl;
    if ((ctx.picks || []).length === 1 && pickUrls.length === 1) return pickUrls[0];
    /* ONE pick refdata gave no page, but the community's own /sfmc-content pack carries
       its url (2026-09-11, found beside the Brightlyn case: a Coming Soon community not
       yet in Airtable fell straight to the MARKET page while the pack in hand knew the
       community's). The pack is the pick's own resolution — same community, so its
       page is the truest answer at this tier. */
    if ((ctx.picks || []).length === 1 && !pickUrls.length && ctx.pack && ctx.pack.community) {
      const own = String(ctx.pack.community.url || "");
      if (LEN_OK.test(own)) return own;
    }
  }

  /* 2 · MASTERPLAN — a sole masterplan pick, at any level. A masterplan page is a
     narrower and truer answer than its market, and `echLevel` reports "market" for
     several picks, so this cannot hang off `lvl` alone. */
  const mps = (ctx.picks || []).filter((p) => p && p.kind === "MP" && LEN_OK.test(String(p.pageUrl || "")));
  if (mps.length === 1 && (ctx.picks || []).length === 1) return String(mps[0].pageUrl);

  /* 3 · MARKET — the shared /new-homes/<state>/<market>/ of everything we hold. Picks
     first; a send with no pick urls falls to the resolved packs' pages. Only when they
     AGREE — otherwise this is a multi-market send and the market tier has no answer. */
  const marketOf = (list: string[]): string => {
    const mkts = list.map(echMarketUrl).filter(Boolean);
    if (!mkts.length) return "";
    const first = mkts[0];
    return mkts.every((x) => x === first) ? first : "";
  };
  const mkt = marketOf(pickUrls) || marketOf(packs);
  if (mkt) return mkt;

  /* 4 · DIVISION — the state page. A division can span markets, so when the market tier
     disagrees the state is usually still one answer; when even that disagrees, the
     national find-a-home page is a live destination and "" is not. */
  const stateOf = (list: string[]): string => {
    const sts = list.map(echStateUrl).filter(Boolean);
    if (!sts.length) return "";
    const first = sts[0];
    return sts.every((x) => x === first) ? first : "";
  };
  const st = stateOf(pickUrls) || stateOf(packs);
  if (st) return st;
  return pickUrls.length || packs.length ? "https://www.lennar.com/new-homes/" : "";
}

/**
 * The lot id in SFMC is <communityCode><6 chars> — "25086616025" for community
 * 25086. Verified across 8 communities / 22 lots (2026-08-19): stripping the code
 * always leaves exactly 6 characters, which is exactly what the block's Homesite
 * field caps at. Sending the raw 11-character id would be silently truncated by
 * the guardrail.
 */
/**
 * THE ADVERTISED HOMESITE NUMBER, NOT THE JDE TAIL (Jack, 2026-08-21: "for 24ft
 * Townhomes - Shearwater it would be 90, 173, 156" — lot 7330610090 advertises as
 * Homesite 90). The old code stripped the community-code PREFIX, which on
 * Shearwater left "610090" standing. The production Stensul module takes the
 * LAST FOUR characters (Substring(lot, len-3, 4)); the Spec Sheet's verified ATL
 * examples agree once leading zeros drop (…600049 → 49, …60122 → 122, …60005 → 5),
 * and so do Jack's JAX ones (0090→90, 0173→173, 0156→156). So: a numeric lot of
 * five or more digits shows its last four, leading zeros stripped; anything
 * non-numeric or shorter passes through untouched (Paige Quinn's rule — we don't
 * advertise the full JDE, and we never invent a number). `code` is kept in the
 * signature for the callers but no longer decides anything.
 */
export function echShortLot(lot: unknown, _code?: unknown): string {
  const l = String(lot || "").trim();
  if (!/^\d{5,}$/.test(l)) return l;
  const tail = l.slice(-4).replace(/^0+/, "");
  return tail || "0";
}
export function echMoney(n: unknown): string {
  const v = Number(n);
  if (!v || !isFinite(v)) return "";
  return "$" + Math.round(v).toLocaleString("en-US");
}
/**
 * Square footage with the thousands comma — "2,165", never "2165" (2026-08-21,
 * off Justin's review screenshots: the fill wrote raw feed numbers while Layne's
 * own block default reads "1,234" and the production Stensul module runs
 * FormatNumber(@sqFt,"N0"). Only a PLAIN number is formatted — a value that
 * already carries a comma, a range ("2,100–2,400") or text passes through. */
/**
 * A PLAN NAME READS AS A NAME, NOT AS A SHOUT (2026-08-24, Jack: "for both preview and
 * the actual email plan names must start with a capital letter and then be lower case —
 * so should be Charle II").
 *
 * The feed is inconsistent by community: JAX alone returns "CHARLE II", "SIERRA BONUS"
 * and "HALLE II" alongside "Belmont", "Easton" and "Mallard" — the same email can show
 * both, and the shouted ones look like a defect next to Layne's typography. So every
 * plan name the fill writes is normalised here, in ONE place, which is why the preview
 * rail and the exported email cannot disagree: both render from these values.
 *
 * WHAT IS PRESERVED, and why each one is deliberate:
 *   · ROMAN NUMERALS stay upper — "Charle II", never "Charle Ii". They are numbers.
 *   · A word that already has inner capitals is left ALONE ("SilverLeaf", "McKinley",
 *     "LaSalle"): mixed case is a deliberate spelling, and lowercasing it would be
 *     inventing a different name.
 *   · Hyphens and apostrophes capitalise the part after them ("Twenty-One", "O'Brien").
 *   · Digits and single letters pass through ("Plan 4", "A Series").
 * Nothing else is touched: no trimming of real words, no dictionary, no invention.
 */
const ROMAN = /^(?:II|III|IV|V|VI|VII|VIII|IX|X|XI|XII)$/i;
export function echPlanName(v: unknown): string {
  const raw = String(v == null ? "" : v).trim();
  if (!raw) return "";
  const capPart = (w: string): string => {
    if (!w) return w;
    /* a real word can be spelled entirely from roman-numeral letters ("CIVIC", "MILL"),
       so the test is the KNOWN numerals a plan name actually uses, not the alphabet
       (audit 2026-08-24) */
    if (ROMAN.test(w)) return w.toUpperCase();
    /* already mixed case — a real spelling, not a shout */
    if (/[a-z]/.test(w) && /[A-Z]/.test(w.slice(1))) return w;
    return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
  };
  return raw.split(/\s+/).map((word) => (
    /* split on the separators that carry their own capital, keeping them in place */
    word.split(/([-'\u2019])/).map((part, i) => (i % 2 ? part : capPart(part))).join("")
  )).join(" ");
}

export function echSqft(n: unknown): string {
  if (n == null || n === "") return "";
  const s = String(n).trim();
  /* the block's own rule: "no decimals" — a feed "2413.0" rounds to the integer */
  if (/^\d+\.\d+$/.test(s)) return Math.round(Number(s)).toLocaleString("en-US");
  if (!/^\d+$/.test(s)) return s;
  return Number(s).toLocaleString("en-US");
}
/** the block's own rule: "Beds must be a whole number with no decimal: 4, not 4.0" */
export function echBeds(n: unknown): string {
  if (n == null || n === "") return "";
  const s = String(n).trim();
  if (!/^\d+(\.\d+)?$/.test(s)) return s;
  return String(Math.round(Number(s)));
}
/** the block's own rule: "Baths may be a whole or half number only: 2 or 2.5" */
export function echBaths(n: unknown): string {
  if (n == null || n === "") return "";
  const s = String(n).trim();
  if (!/^\d+(\.\d+)?$/.test(s)) return s;
  const v = Math.round(Number(s) * 2) / 2;
  return v % 1 === 0 ? String(v) : v.toFixed(1);
}
/** strip the "50's - " / "(Coming Soon)" decoration off a community name */
export const echClean = (s: unknown) =>
  String(s || "").replace(/^\s*\d{1,3}'?\s*s?\s*[-–—]\s*/, "").replace(/\([^)]*\)/g, "").replace(/\s+/g, " ").trim();

/**
 * THE FEED DOESN'T OBEY THE BLOCK'S RULE. SFMC hands back "(770) 670-2770";
 * ECH_Logo's guardrail says 000-000-0000 and the block underlines that exact
 * string. A dynamic value has to satisfy the same guardrail a typed one does, so
 * normalise here rather than ship a phone that breaks the header lockup. Anything
 * that isn't 10 digits is dropped — better an empty field than a malformed number
 * in a live send.
 */
export function echPhone(ph: unknown): string {
  let d = String(ph == null ? "" : ph).replace(/\D+/g, "");
  if (d.length === 11 && d.charAt(0) === "1") d = d.slice(1);
  return d.length === 10 ? d.slice(0, 3) + "-" + d.slice(3, 6) + "-" + d.slice(6) : "";
}

/** Which plan does THIS block show? A template can carry two Featured blocks and
 *  they should not both show plan 1, so index by position among the Featured
 *  blocks in the document (monolith __echPlanIdx). */
export function echPlanIdx(doc: DynBlock[], m: DynBlock): number {
  let n = 0;
  for (const b of doc) {
    if (/^ECH_Featured/.test(echNameOf(b.type))) { n++; if (b === m) return n; }
  }
  return 1;
}

/**
 * THIS CARD'S ORDINAL AMONG THE *PLANS* CARDS ONLY (2026-08-24, from an audit).
 *
 * `echPlanIdx` above counts EVERY `ECH_Featured*` block, whatever level it sits at —
 * right for its own job (two Featured blocks should not both show plan 1) and wrong for
 * the one-card-per-community mapping, because `echPlanScope` counts only the cards at
 * PLANS level. A document with a Featured HOMES or COMMUNITIES card ahead of a plans
 * card therefore mapped card 1 to community 2 and card 2 to a community that does not
 * exist: community 1 never appeared and the second card blanked. Counting the same set
 * on both sides is the whole fix.
 */
export function echPlanCardIdx(doc: DynBlock[], m: DynBlock): number {
  let n = 0;
  for (const b of doc) {
    if (/^ECH_Featured/.test(echNameOf(b.type)) && echFeatOf(b) === "plans") { n++; if (b === m) return n; }
  }
  return 1;
}

/**
 * THE HOME AN UNPICKED *HOMES* CARD SHOWS (2026-09-10, Jack: "the last homesite isn't
 * populating anything, just the default blank homesite").
 *
 * A Featured HOMES card with no `__homePick` of its own fell back to
 * `ctx.pack.homesites[echPlanIdx - 1]` — ONE pack, indexed by the card's position among
 * EVERY Featured block. Both halves were wrong for a card set minted from a listing
 * table: a masterplan's homes live in its collections' packs, not in `ctx.pack`, and the
 * ordinal counted plan-level cards too. The last card asked for a home past the end of
 * the wrong list and rendered "" on the canvas — the shipped placeholder on the rail.
 *
 * Now: every homesite across every pack in hand, minus the ones sibling cards picked
 * explicitly, dealt to the UNPICKED homes cards in document order. A card only reads ""
 * when the inventory is genuinely exhausted.
 */
export function echHomeCardFallback(
  doc: DynBlock[] | undefined,
  m: DynBlock | undefined,
  packs: Pack[],
): { h: HomesiteInfo & { lot?: string }; code: string } | undefined {
  if (!m) return undefined;
  const isHomesCard = (b: DynBlock) => /^ECH_Featured/.test(echNameOf(b.type)) && echFeatOf(b) === "homes";
  const firstPick = (b: DynBlock) => String((b.data && b.data[ECH_HOME_PICK]) || "").split("\n").map((x) => x.trim()).filter(Boolean)[0] || "";
  const taken: Record<string, 1> = {};
  let ord = 0, found = false;
  for (const b of doc || []) {
    if (!isHomesCard(b)) continue;
    const pk = firstPick(b);
    if (pk) { taken[pk] = 1; continue; }
    if (!found) { ord++; if (b === m) found = true; }
  }
  if (!found) ord = 1;
  /* ROUND-ROBIN across the packs (2026-09-11): with several communities in scope the
     cards are "one per community" (Jack, 2026-09-10 call: "one home site per area or per
     community"), so card 1 is community 1's first free home, card 2 community 2's, and
     only past the last community does the deal come back round. A single pack is
     unchanged: its homes in order. */
  const lists = packs.map((p) => (p.C.homesites || []).filter((h) => !taken[homeKey(p.code, h)]).map((h) => ({ h: h as HomesiteInfo & { lot?: string }, code: p.code })));
  const most = lists.reduce((a, l) => Math.max(a, l.length), 0);
  let n = 0;
  for (let r = 0; r < most; r++) {
    for (const l of lists) {
      if (r >= l.length) continue;
      n++;
      if (n === ord) return l[r];
    }
  }
  return undefined;
}

/** the shape echFillAll walks — matches AnyBlockInstance */
export interface DynBlock { id: string; type: string; data: Record<string, unknown> }

/* ═══════════════════════════════════════════════════════════════════════════════
   AN EXPLICIT PER-BLOCK PICK, WHICH `plan.self` / `home.<n>` HONOUR OVER POSITION
   (2026-08-20 — the third of the three faults behind Jack's "dynamic is not set
   up" on the Featured Plan block's Dynamic pane)
   ═══════════════════════════════════════════════════════════════════════════════

   THE PROBLEM. `echPlanIdx` (L606 above) makes `plan.self.*` POSITIONAL: the 1st
   Featured block in the document gets plan 1, the 2nd gets plan 2. So Dynamic
   silently auto-filled and there was nothing for the stepped picker to choose —
   opening it, picking a floorplan, and pressing Done could not change the canvas
   because nothing read the choice. Same for the Listing table's `home.1..10`,
   which walked `C.homesites` in feed order.

   THE FIX, AND ITS ONE HARD RULE. Two keys on the BLOCK'S OWN `data`:

     __planPick  ONE plan,  "<code>|pn|<num>|<name>"
     __homePick  MANY homes, newline-joined `homeKey()` strings, in PICK ORDER

   An explicit pick wins over position, always. NO explicit pick → today's
   positional behaviour, byte-identical. This is a superset, not a change — see the
   "no pick leaves the positional fill byte-identical" test in echPickWire.test.ts.

   WHY NOT STORE THE ARRAY INDEX, which is what the picker's own keys carry.
   `C.plans[]` order is not guaranteed across refetches of /sfmc-content: a plan
   selling out or a sort change would leave a stored `2` pointing at a DIFFERENT
   floorplan, and the block would confidently ship the wrong one into a live send.
   So the plan is stored by its own identifiers — `PlanInfo.num` first, `name`
   second — and resolved to a row at READ time. Either one identifies it alone, so
   a renumbered plan and a renamed plan both still resolve.

   WHY THE COMMUNITY CODE IS PART OF THE KEY. Plan num "1" exists in very nearly
   every community. Without the code, changing Step 1's community under a saved
   document would silently show the new community's plan 1 as if it had been
   chosen. A code mismatch is treated as unresolvable.

   WHY `pn` AND NOT `p`. The picker's plan key is `<code>|p|<idx>` — index-based
   AND 0-based. Read as a pick, its `<idx>` would land in the `num` slot and
   `num "1"` would resolve to plan 1 while the user had picked plan 2. A distinct
   marker makes the two shapes unmistakable rather than merely unlikely.

   WHY AN UNRESOLVABLE PICK FALLS BACK INSTEAD OF BLANKING. A plan or homesite
   genuinely leaves the feed when it sells. Returning "" there would blank a
   Featured card mid-document in a customer email; the positional answer is at
   least a real floorplan from the right community. Both readers below return
   `undefined` for "no usable pick" and the caller falls through to `[idx - 1]`. */

/** the block-data key holding ONE plan pick — `"<code>|pn|<num>|<name>"` */
export const ECH_PLAN_PICK = "__planPick";
/** the block-data key holding the ordered home picks — newline-joined `homeKey()` */
export const ECH_HOME_PICK = "__homePick";

/** the pack's own community code, however the route spelled it */
const packCode = (C: CommunityPackResponse) => String((C.community && C.community.code) || C.code || "");

/**
 * The plan this block explicitly picked, or `undefined` when there is no pick, the
 * pick belongs to another community, or the plan has left the feed. `undefined`
 * means "fall back to the position" — never "render blank".
 */
export function echPlanFor(C: CommunityPackResponse, m?: DynBlock): PlanInfo | undefined {
  /* `__planPick` is a LIST since 2026-08-28 (the Listing table picks several). A card
     holds one plan, so it reads the first line — which is the whole value for every
     pick written before that change. */
  const raw = String((m && m.data && m.data[ECH_PLAN_PICK]) || "").split("\n")[0].trim();
  if (!raw) return undefined;
  const p = raw.split("|");
  if (p[1] !== "pn") return undefined;             /* a legacy `|p|` picker key, not a pick */
  const code = String(p[0] || "").trim();
  const cc = packCode(C);
  if (code && cc && code !== cc) return undefined; /* another community's plan */
  const num = String(p[2] || "").trim();
  const name = String(p[3] || "").trim().toLowerCase();
  const plans = C.plans || [];
  if (num) {
    const byNum = plans.find((x) => String(x.num == null ? "" : x.num).trim() === num);
    if (byNum) return byNum;
  }
  if (name) {
    const byName = plans.find((x) => String(x.name || "").trim().toLowerCase() === name);
    if (byName) return byName;
  }
  return undefined;
}

/* ═══════════════════════════════════════════════════════════════════════════════
   EVERY PACK A PICK MAY RESOLVE AGAINST — 2026-08-20
   ═══════════════════════════════════════════════════════════════════════════════

   THE SILENT NO-OP THIS CLOSES, and it is the same class that was found and fixed
   once already today (the cross-community pick, see DynLiveFill's `commNames`).
   `echPlanFor` compares the stored pick's code slot against ONE pack's code and
   refuses a mismatch — correctly, because plan num "1" exists in nearly every
   community. With a masterplan pick, `ctx.pack` is the MASTERPLAN's pack and every
   plan the requester can actually see belongs to a CHILD COLLECTION, so every pick
   would mismatch, fall back to the position, find no plans in the masterplan's own
   pack, and leave the canvas exactly as it was. The pick would store, the
   selected-cards panel would update, and nothing would move — two renderings of the
   same pick disagreeing.

   So the resolution is over a LIST of packs, and the pick's code slot chooses which
   one. That is why the code was put in the key in the first place; this is the read
   side finally using it. */

/** the Step-1 pick's own pack first, then its collections, then the wizard's, deduped by code */
export function echPacks(ctx: EchCtx): Pack[] {
  const out: Pack[] = [];
  const seen: Record<string, 1> = {};
  const coll = ctx.collPacks || [];
  /* ONE VOCABULARY FOR THE GROUP HEADINGS (2026-08-20, seen in Chrome on Timber
     Ridge). A masterplan row in /refdata often has NO code — Timber Ridge's is just
     `{address, city, state}` — so `useEchDyn` resolves its pack by NAME through
     /sfmc-communities, and that search lands on one of its own COLLECTIONS (here
     26166). `ctx.pack` is then a collection wearing SFMC's name for it, and the
     picker drew "TIMBER RIDGE 50'-60'" next to "VILLAGE - TIMBER RIDGE" — the same
     masterplan's two children labelled in two different vocabularies. When the sole
     pack turns out to BE one of the collections, it takes the collection's name. */
  const collName: Record<string, string> = {};
  for (const p of coll) if (p && p.code && p.name) collName[p.code] = p.name;
  const C = ctx.pack;
  if (C) {
    const code = packCode(C);
    seen[code] = 1;
    out.push({ code, name: collName[code] || String((C.community && C.community.name) || ""), C });
  }
  for (const p of coll) {
    if (!p || !p.code || seen[p.code]) continue;
    seen[p.code] = 1;
    out.push(p);
  }
  /* ── AND WHATEVER THE WIZARD LOADED, LAST (2026-08-20) ─────────────────────────
     LAST, and deduped against everything above, so this cannot change a single
     existing answer: with Step 1 scoped to one community the wizard's pack for that
     community is already `seen` and is skipped, and `echPackFor` / `echHomeIn` both
     search the list in order. What it adds is the ONLY thing it adds — a community
     the requester chose in the wizard that Step 1 never named. See `EchCtx.pickPacks`
     for why that has to be possible at all. */
  for (const p of ctx.pickPacks || []) {
    if (!p || !p.code) continue;
    if (seen[p.code]) {
      /* ── A SECOND PACK FOR THE SAME COMMUNITY MERGES ITS HOMESITES (2026-08-25) ──
         Found by auditing the "6 homes selected, 1 in the table" fix, which did not
         actually fix it: `/sfmc-content` caps a community at THIRTY homesites, the
         wizard's pack carries the picked row that falls outside that slice, and this
         loop dropped the whole wizard pack because Step 1 had already claimed the code.
         `echHomeIn` then could not resolve the pick and the row stayed blank — the two
         halves of the fix cancelled each other out.
         Merging adds only rows the kept pack does not already have, keyed by
         `homeKey`, so the order and content of an existing answer cannot change. */
      const keep = out.find((q) => q.code === p.code);
      if (!keep || !keep.C) continue;
      const have = new Set((keep.C.homesites || []).map((h) => homeKey(keep.code, h)));
      const add = ((p.C && p.C.homesites) || []).filter((h) => !have.has(homeKey(p.code, h)));
      if (add.length) keep.C.homesites = ((keep.C.homesites || []) as typeof add).concat(add) as never;
      continue;
    }
    seen[p.code] = 1;
    out.push(p);
  }
  return out;
}

/**
 * THE COMMUNITY CODE THE BLOCK'S OWN PICK NAMES — "" when it has no pick. 2026-08-20.
 *
 * Both keys carry it in the same slot, which is why one reader covers both:
 * `__planPick` is `"<code>|pn|<num>|<name>"` and `__homePick` is newline-joined
 * `"<code>|h|<addr>|<price>"`, so `split("|")[0]` off the raw string is the code in
 * either case (the same slice `echPackFor` takes). The plan key wins when a block
 * somehow carries both: a Featured block consumes exactly one of them per level and
 * the level table decides which, so the other is a leftover from a level the
 * requester switched away from — deliberately kept rather than cleared, see
 * `echFeatPickKind`.
 */
export function echPickCode(m: DynBlock | null | undefined): string {
  const slot = (k: string) => String((m && m.data && m.data[k]) || "").trim().split("|")[0].trim();
  return slot(ECH_PLAN_PICK) || slot(ECH_HOME_PICK);
}

/**
 * THE LEVEL **THIS BLOCK** RESOLVES AT — 2026-08-20, and the whole of why Step 1 is
 * no longer a precondition for the Dynamic pane.
 *
 * `echLevel` answers for the SEND: Step 1's focus, and nothing else. That is right
 * for `community.name` in a header, and it was the second lock on the picker — every
 * source a pick feeds declares `levels:["community"]`, so with the focus on Market
 * `echSrcOk` skipped all seven of `ECH_Featured1CTA`'s fields and a floorplan chosen
 * in the wizard changed nothing. The pane was not the problem; the level was.
 *
 * AN EXPLICIT PICK IS ITSELF A COMMUNITY SCOPE. `__planPick` / `__homePick` name a
 * community by CODE — the requester walked Communities › Floorplans and chose one
 * plan out of one community. That block is at community level whatever the send is
 * scoped to, exactly as the monolith's Featured section is (it fills from
 * `__lvPackCache`, which is the wizard's cache, and never consults the form's focus).
 *
 * NOTHING ELSE MOVES. A block with no pick — every non-Featured ECH block, and a
 * Featured block at Community feature level, whose `pick` is `""` — gets `echLevel`'s
 * answer unchanged, so the Logo, Get Directions, the CTAs and the market/division
 * header all behave exactly as before.
 */
export function echBlockLevel(m: DynBlock | null | undefined, ctx: EchCtx): string {
  if (m && echPickCode(m)) return "community";
  /* `ctx.parent` is set by useEchDyn when every pick is a collection of one
     masterplan — computed once there rather than by each caller, so the level and the
     fields that fill at that level can never disagree (2026-08-27) */
  return echLevel(ctx.focus, ctx.picks.length, !!ctx.parent);
}

/**
 * Every community code the DOCUMENT's stored picks name, deduped, in document order.
 * The caller fetches the ones it does not already hold — see StudioView's pick-pack
 * effect. Reopening a saved job is the case this exists for: the picks survive in the
 * block data, the packs they resolve against do not.
 */
export function echPickCodes(doc: DynBlock[]): string[] {
  const out: string[] = [];
  const seen: Record<string, 1> = {};
  for (const m of doc || []) {
    const c = echPickCode(m);
    if (!c || seen[c]) continue;
    seen[c] = 1;
    out.push(c);
  }
  return out;
}

/**
 * The pack whose code the block's stored pick names, or `undefined`. A pick with no
 * code slot, or a code that names nothing in hand, resolves to nothing rather than
 * to a plausible neighbour — the caller then falls back to the POSITION inside
 * `ctx.pack`, which is the pre-existing behaviour.
 */
export function echPackFor(packs: Pack[], m: DynBlock | undefined, key: string): Pack | undefined {
  const code = String((m && m.data && m.data[key]) || "").trim().split("|")[0].trim();
  if (!code) return undefined;
  return packs.find((p) => p.code === code);
}

/**
 * The home this block picked for ROW `n`, searched across EVERY pack in hand, with
 * the pack it came from. The pack matters as much as the home: `home.<n>.homesite`
 * strips the COMMUNITY CODE off the lot id, and stripping the masterplan's code off
 * a collection's lot would ship an 11-character id the block's guardrail truncates.
 */
export function echHomeIn(
  packs: Pack[],
  m: DynBlock | undefined,
  n: number,
): { h: HomesiteInfo & { lot?: string }; code: string } | undefined {
  const raw = String((m && m.data && m.data[ECH_HOME_PICK]) || "");
  if (!raw) return undefined;
  const key = raw.split("\n").map((x) => x.trim()).filter(Boolean)[n - 1];
  if (!key) return undefined;
  for (const p of packs) {
    const h = (p.C.homesites || []).find((x) => homeKey(p.code, x) === key);
    if (h) return { h: h as HomesiteInfo & { lot?: string }, code: p.code };
  }
  /* ── THE SAME HOME AT A DIFFERENT PRICE IS STILL THE SAME HOME (2026-09-22, #135/#139) ──
     `homeKey` is `code|h|addr|price`, and the two feeds do not agree on price
     (`/homesites.json` reads the Sitecore DE, `/sfmc-content` the Salesforce-enriched
     one — see echDirect.ts). A pick stored from one feed therefore misses the exact
     match in a pack fetched from the other, and used to fall through to the positional
     home. Within the pick's OWN community a street address is unique, so the address is
     the second key; nothing else is guessed at. */
  const parts = key.split("|");
  const code = String(parts[0] || "").trim();
  const addr = String(parts[2] || "").trim().toLowerCase();
  if (code && addr) {
    const p = packs.find((x) => String(x.code) === code);
    const h = p && (p.C.homesites || []).find((x) => String(x.addr || "").trim().toLowerCase() === addr);
    if (h) return { h: h as HomesiteInfo & { lot?: string }, code: p!.code };
  }
  return undefined;
}

/**
 * ── A PICK THAT NAMES A HOME NO PACK IN HAND CAN SHOW (2026-09-22, #135/#139) ──────────
 * Kailey White (NEV, 2026-09-21): "The preview is incorrectly showing what homes were
 * selected for the explore homes section" / "Showing the wrong selected homes in the
 * email preview." Her Studio showed the two homes she picked (3054 W Orco Ln, 664 S
 * Cecita Way); the form's preview rail showed 11848 Mimicking Song Ln — a home she never
 * chose. The rail fills with the FORM's ctx, which has no `pickPacks` (those live in the
 * Studio's state) and whose `/sfmc-content` pack is capped at thirty homesites, so her
 * picks resolved to nothing and `home.self` fell back to "the next free home" — the
 * positional fallback inventing a card. A requester who picked a home has said which
 * home; when the packs in hand cannot show it, the honest fill is NO fill — the block
 * keeps the values the Studio wrote from the real row. True when row `n` (1-based) of
 * this block's home pick list has a key and no pack in hand resolves it.
 */
export function echHomePickUnresolved(packs: Pack[], m: DynBlock | undefined, n: number): boolean {
  const raw = String((m && m.data && m.data[ECH_HOME_PICK]) || "");
  if (!raw.trim()) return false;
  const key = raw.split("\n").map((x) => x.trim()).filter(Boolean)[n - 1];
  if (!key) return false;
  return !echHomeIn(packs, m, n);
}

/**
 * The plan this block picked for ROW `n`, searched across every pack in hand, with the
 * pack it came from. 2026-08-28 — the plans-level twin of `echHomeIn`.
 *
 * The pack travels with the plan for the same reason it does for a home: the row's
 * "View" link and its community heading both need to know which community this plan
 * belongs to, and a masterplan's collections share a table.
 */
export function echPlanIn(
  packs: Pack[],
  m: DynBlock | undefined,
  n: number,
): { p: PlanInfo; code: string } | undefined {
  const raw = String((m && m.data && m.data[ECH_PLAN_PICK]) || "");
  if (!raw) return undefined;
  const line = raw.split("\n").map((x) => x.trim()).filter(Boolean)[n - 1];
  if (!line) return undefined;
  const parts = line.split("|");
  if (parts[1] !== "pn") return undefined;         /* a legacy `|p|` picker key */
  const code = String(parts[0] || "").trim();
  const num = String(parts[2] || "").trim();
  const name = String(parts[3] || "").trim().toLowerCase();
  for (const p of packs) {
    if (code && String(p.code) !== code) continue;
    const plans = p.C.plans || [];
    /* num FIRST, name second — the same order `echPlanFor` resolves in */
    let hit = num ? plans.find((x) => String(x.num == null ? "" : x.num).trim() === num) : undefined;
    if (!hit && name) hit = plans.find((x) => String(x.name || "").trim().toLowerCase() === name);
    if (hit) return { p: hit, code: p.code };
  }
  return undefined;
}

/**
 * The home this block explicitly picked for ROW `n` (1-based), or `undefined`.
 * The list is in pick order, so row 1 is the first thing the user chose — the
 * Listing table's ten slots are slots, not the feed's first ten homesites.
 * A row past the end of the pick list falls back to its position, which is why a
 * two-home pick still leaves rows 3–10 showing real inventory.
 */
export function echHomeFor(C: CommunityPackResponse, m: DynBlock | undefined, n: number): (HomesiteInfo & { lot?: string }) | undefined {
  /* ONE MATCHER, the multi-pack one, with a one-pack list — the single-community
     read and the collection read can never drift apart (2026-08-20) */
  return echHomeIn([{ code: packCode(C), name: "", C }], m, n)?.h;
}

/* ═══════════════════════════════════════════════════════════════════════════════
   WHICH COMMUNITY DOES EACH FEATURED FLOORPLAN CARD SPEAK FOR? (2026-08-24)
   ═══════════════════════════════════════════════════════════════════════════════

   Jack's rule, verbatim: *"should auto pull in one per community if there's more than
   one community or area of interest level or higher … and if it's just one community
   should pull in a max of 3."*

   So the scope is a LIST OF COMMUNITIES, not a list of packs:

     · MORE THAN ONE community in scope → card N shows community N's FIRST floorplan.
       One card, one community — never three plans from whichever community happened to
       load first, which is what a naive widening of the level gate would have produced.
     · EXACTLY ONE community → card N shows that community's plan N, capped at three.

   POOLED BY PARENT COMMUNITY, for the reason the Listing table already is: a masterplan
   arrives as several collection packs ("50s - Granary Park", "24ft Townhomes- Granary
   Park") and they are ONE community to the reader. `echParentCommunity` strips the
   collection marker, and the first pack under each parent that actually carries plans
   represents it. A pack with no plans is not a community for this purpose — it would
   render a card with a name and no specs, and this block's own guardrail forbids an
   empty spec line. */
/**
 * ── TWO CEILINGS, BECAUSE THE TWO CASES ARE DIFFERENT QUESTIONS (2026-08-28) ─────
 *
 * Standup ruling: **one community → up to 3 cards; several communities or a market →
 * one card per community, up to 5.** One number served both until now, so a five-
 * community send silently stopped at three and two communities went unrepresented —
 * the opposite of what "one per community" promises.
 *
 * They are different questions. With ONE community the cards are three views of the
 * same place, and three is an editorial limit on how much of one community to show.
 * With SEVERAL, each card IS a community, so the limit is how many communities an
 * email can carry — which is what Layne's own `maxCount: 5` on the Featured blocks
 * says, and `echFeaturedMax` already reads that number for the palette. This brings
 * the auto-expansion in line with the cap the block itself declares.
 */
export const ECH_PLAN_CARDS_MAX = 3;
export const ECH_PLAN_CARDS_MAX_MULTI = 5;

/**
 * HOW MANY MORE PLAN CARDS DOES THIS DOCUMENT NEED? (2026-08-24)
 *
 * Jack: "why can't you just auto add it like we did with the listing, or how we can add
 * more listings within Creative Studio." The listing table grows INSIDE one block — ten
 * `home<n>*` field sets, so another home is a fill. A Featured plan card is one BLOCK per
 * card, so another card is a block INSERT, which is why "one per community" stopped at one
 * on Available Now: its template sequence carries exactly one `ECH_Featured2CTA`.
 *
 * This says how many to append: one card per community in scope, capped at
 * `ECH_PLAN_CARDS_MAX`, minus the cards the document already has.
 *
 * IT NEVER FIGHTS THE REQUESTER. Returns 0 when the scope is one community (the single
 * card already speaks for it), when the cards already cover the scope, and — via
 * `autoDone` — when this document has already been expanded once, so deleting a card
 * Jack does not want is permanent rather than a card that reappears on the next fill.
 * That was the exact shape of the "can't delete homes, you keep re-adding them" bug.
 */
export const ECH_CARDS_AUTO = "__cardsAuto";

/* ── THE CARD SET'S LEVEL: PLANS OR HOMES (2026-09-11) ─────────────────────────────
   Lauren, Teams 1:54 PM with a screenshot of ONE homesite card under a ten-home listing:
   "the listing table vs plan cards view is still not toggling correctly." Every count
   below filtered on `echFeatOf(b) === "plans"`, and the table→cards toggle mints HOMES
   cards — so the auto-expansion, the excess trim and the insertion point all saw zero
   cards and did nothing, and a listing of ten homes became exactly one card, however
   many communities were in scope. Same shape Jack drove live 40 minutes earlier
   (Granary Park → one card, "2633 Field Daisy Ln"). The set's level is whichever kind
   of Featured card the document carries; plans wins if both are present, which is the
   old behaviour exactly. Community-level cards are never expanded (one community, one
   card). */
export function echCardLevel(doc: DynBlock[]): "plans" | "homes" | null {
  let homes = false;
  for (const b of doc) {
    if (!/^ECH_Featured/.test(echNameOf(b.type))) continue;
    const f = echFeatOf(b);
    if (f === "plans") return "plans";
    if (f === "homes") homes = true;
  }
  return homes ? "homes" : null;
}
const cardsAt = (doc: DynBlock[], lvl: "plans" | "homes") =>
  doc.filter((b) => /^ECH_Featured/.test(echNameOf(b.type)) && echFeatOf(b) === lvl);
/* how many cards the scope can honestly fill at this level: one community → its own
   inventory (plans, or homes-backed rows, at plans level; homes at homes level) capped
   at 3; several → one per community that HAS that inventory, capped at 5 */
function cardsWanted(scope: Pack[], lvl: "plans" | "homes", ctx?: EchCtx): number {
  const inv = (p: Pack) => lvl === "homes"
    ? (p.C.homesites || []).length
    : Math.max((p.C.plans || []).length, (p.C.homesites || []).length);
  /* one community's plan cards count its DISTINCT plans, family-wide (see echSoleDeal) */
  if (scope.length === 1 && lvl === "plans" && ctx) return Math.min(echSoleDeal(ctx, scope).length, ECH_PLAN_CARDS_MAX);
  if (scope.length === 1) return Math.min(inv(scope[0]), ECH_PLAN_CARDS_MAX);
  const withInv = lvl === "homes" ? scope.filter((p) => inv(p) > 0).length : scope.length;
  return Math.min(withInv, ECH_PLAN_CARDS_MAX_MULTI);
}

export function echPlanCardsNeeded(doc: DynBlock[], ctx: EchCtx): number {
  const lvl = echCardLevel(doc);
  if (!lvl) return 0;                                             /* no card block, nothing to clone */
  const cards = cardsAt(doc, lvl);
  const autoDone = cards.some((b) => String((b.data && b.data[ECH_CARDS_AUTO]) || "").trim());
  if (autoDone) return 0;
  /* ── A SET THE TOGGLE ALREADY DECIDED IS LEFT ALONE (2026-09-11) ────────────────
     The table→cards mint (echCardMint) seeds every card it makes with an explicit pick
     and applies Jack's own count rule — one per market, else one per community, else
     three from the one community. Two or more picked cards therefore ARE the answer;
     growing them here would fight that rule (a two-market division table would swell
     to five). One card, picked or not, still grows: that is the single-card set Lauren
     and Jack saw, and the expansion below is the second mechanism that makes it right.
     (2026-10-27: a set made in the inventory picker, or left by a card's ✕, carries
     `__cardsAuto` and stops at `autoDone` above — one plan picked is one card, Layne.) */
  const hasPick = (b: DynBlock) => !!String((b.data && (b.data[ECH_PLAN_PICK] || b.data[ECH_HOME_PICK])) || "").trim();
  if (cards.length >= 2 && cards.every(hasPick)) return 0;
  const scope = echPlanScope(ctx);
  if (!scope.length) return 0;
  /* ── ONE COMMUNITY STILL GROWS TO THREE (2026-08-24, from an audit) ───────────────
     `min(scope.length, 3)` made a one-community send want exactly ONE card, so Jack's
     "if it's just one community should pull in a max of 3" only worked on a template
     that already shipped three. With a single community the ceiling is what that
     community can actually SHOW — capped at three — so a community with one plan still
     gets one card and never a blank. `cardsWanted` carries that rule for both levels. */
  return Math.max(0, cardsWanted(scope, lvl, ctx) - cards.length);
}

/**
 * ── THE CARDS THE SCOPE CANNOT FILL COME OFF (2026-09-10) ───────────────────────────
 *
 * The mirror of `echPlanCardsNeeded`. Measured on prod (ATL · Market · Find Your Dream
 * Home, whose sequence ships FIVE ECH_Featured1CTA cards): the market scope resolved
 * three communities with plans, so cards 4 and 5 were "dry" (`echPlanDry`), the fill
 * left them on Layne's sample content — "Plan Name · 1,234 sq ft" with an empty
 * Floorplan URL — and the Done gate flagged "Featured Plan · Floorplan URL" twice.
 * "Fix →" landed on the section's first card, whose URL was full, so Jack read it as a
 * false error: *"Floorplan URL shown as missing a URL but there is a populated URL."*
 * The gate was right about a card nobody could see the point of; the card was the bug.
 *
 * So: when the scope is settled and can fill N cards, a plan card past N that is still
 * PRISTINE — no pick, nothing typed (`__socOff`), nothing ever fed into it (`__socFed`)
 * — is removed. Never a card the requester touched, never while packs are still
 * loading, never when the scope is empty (nothing has answered yet — a blank scope is
 * a wait, not a verdict). If the scope later grows, `echPlanCardsNeeded` adds cards
 * back the way it always has.
 */
export function echPlanCardsExcess(doc: DynBlock[], ctx: EchCtx): string[] {
  if (ctx.pending) return [];
  const lvl = echCardLevel(doc);
  if (!lvl) return [];
  const cards = cardsAt(doc, lvl);
  if (cards.length < 2) return [];
  const scope = echPlanScope(ctx);
  if (!scope.length) return [];
  const want = cardsWanted(scope, lvl, ctx);
  if (want < 1) return [];
  const pristine = (b: DynBlock): boolean => {
    const d = b.data || {};
    if (String(d[ECH_PLAN_PICK] || "").trim() || String(d[ECH_HOME_PICK] || "").trim()) return false;
    if (String(d.__socOff || "").trim()) return false;
    /* ── A CARD THE FILL FED, AND NOBODY CHANGED, IS STILL THE FILL'S (2026-09-14) ──
       "Fed" used to mean "keep". Driven on dev with Jie's picks: Cypress Preserve +
       Brunswick Plantation dealt Owen (Cypress) and Blakely (Brunswick); remove Cypress
       and the Owen card was past the one-community ceiling but FED, so it stayed — a
       plan from a community no longer on the send. Fed content the requester has not
       altered is the fill's own words; the card is pristine when every fed field still
       reads exactly what the fill wrote. A hand edit flips `__socOff` above and also
       differs from `fed`, so a touched card is still never removed. */
    const fed = String(d.__socFed || "").trim();
    if (!fed || fed === "{}") return true;
    let fedMap: Record<string, string> = {};
    try { fedMap = JSON.parse(fed) as Record<string, string>; } catch { return false; }
    return Object.keys(fedMap).every((k) => String(d[k] == null ? "" : d[k]).trim() === String(fedMap[k] ?? "").trim());
  };
  return cards.slice(want).filter(pristine).map((b) => b.id);
}

/** the LAST Featured plan card in the document — new cards go straight after it */
export function echLastPlanCardIdx(doc: DynBlock[]): number {
  const lvl = echCardLevel(doc);
  let at = -1;
  if (!lvl) return at;
  doc.forEach((b, i) => { if (/^ECH_Featured/.test(echNameOf(b.type)) && echFeatOf(b) === lvl) at = i; });
  return at;
}

/**
 * CAN THE FILL SAY ANYTHING ABOUT THIS FEATURED CARD'S FLOORPLAN? (2026-08-24)
 *
 * True = no. No explicit pick on the block, and no community in Step 1's scope that
 * carries floorplans — so every `plan.*` source answers "" and writing those empties
 * would delete the card's image cell and spec lines. Both the fill (`echDynPatch`) and
 * the clear-restore branch (`echPickPatch`) read this, so "the level sources it" and
 * "the level can answer it" can never drift apart: widening the level gate without this
 * is precisely what blanked a cleared card at Market focus.
 */
export function echPlanDry(m: DynBlock, ctx: EchCtx, doc?: DynBlock[]): boolean {
  if (echFeatOf(m) !== "plans") return false;
  if (String((m.data && m.data[ECH_PLAN_PICK]) || "").trim()) return false;
  const scope = echPlanScope(ctx);
  if (!scope.length) return true;
  /* ── A CARD WITH NO COMMUNITY OF ITS OWN IS ALSO DRY (2026-08-24, from an audit) ──
     Three plan cards on a two-community send: the third has nothing to speak for. Dry
     means the per-field loop SKIPS it, so it keeps the block's shipped content; without
     this the fill wrote empties over a real card and the empty-segment rule stripped
     its image cell. Same for a card past the three-card ceiling. */
  if (!doc) return false;
  const cIdx = echPlanCardIdx(doc, m);
  /* ── THE CEILING IS THE SCOPE'S OWN, AND PAST THE LAST COMMUNITY THE DEAL GOES ROUND
     AGAIN (2026-09-11 — Lauren on the testing call, plan cards at Market level: "It'll
     populate the first three, but not the 4th… it's always the last one"; Jack: "Either
     way, it should pull an image"). Two faults sat here. (1) The dry test used the ONE-
     community ceiling (3) on every scope, while the multi-community mint goes to 5
     (`ECH_PLAN_CARDS_MAX_MULTI`, the 2026-08-28 standup ruling) — so on a market with
     four or five communities the mint produced cards 4 and 5 and this function declared
     them dry, and they kept Layne's seed placeholder. Moving a card changed its ordinal,
     which is why the image "appeared" after a drag. (2) A card past the last community
     was dry by rule; it now takes the next plan round the communities (`echPlanDeal`),
     the same "deal the inventory, blank only when exhausted" the homes cards got in
     #226. Dry is now exactly: past the scope's ceiling, or past the dealt inventory. */
  const ceiling = scope.length > 1 ? ECH_PLAN_CARDS_MAX_MULTI : ECH_PLAN_CARDS_MAX;
  if (cIdx > ceiling) return true;
  /* one community: past its DISTINCT plans the card has nothing new to say (2026-09-14,
     see `echSoleDeal`) — dry, so the field loop skips it rather than blanking it */
  if (scope.length === 1) return cIdx > echSoleDeal(ctx, scope).length;
  return cIdx > echPlanDeal(scope).length;
}

/**
 * The plan inventory of a multi-community scope, dealt ROUND-ROBIN: every community's
 * first plan, then every community's second, and so on — so cards 1..n still read one
 * community each (the #222 "one card per community" mint), and a card past the last
 * community reads the next plan of the first community rather than a placeholder. A
 * pack with no `plans` (the market/division tier builds from /homesites.json) counts its
 * homesites — the positional branch already answers a card from a homesite row.
 */
export function echPlanDeal(scope: Pack[]): { C: Pack["C"]; pIdx: number }[] {
  const out: { C: Pack["C"]; pIdx: number }[] = [];
  const size = (p: Pack) => (p.C.plans || []).length || (p.C.homesites || []).length;
  const most = scope.reduce((a, p) => Math.max(a, size(p)), 0);
  for (let r = 0; r < most; r++) {
    for (const p of scope) if (r < size(p)) out.push({ C: p.C, pIdx: r + 1 });
  }
  return out;
}

/** the Step 1 packs — the sole community's own, then every collection pack — that
 *  answer to one parent (the key `echPlanScope` dedups on) and carry inventory */
function echSoleFamily(ctx: EchCtx, head: Pack): Pack[] {
  const key = echScopeKey(head);
  const step1: Pack[] = [];
  if (ctx.pack) step1.push({ code: packCode(ctx.pack), name: String((ctx.pack.community && ctx.pack.community.name) || ""), C: ctx.pack });
  for (const p of ctx.collPacks || []) if (p && p.C) step1.push(p);
  const out: Pack[] = [];
  const seenCode: Record<string, 1> = {};
  for (const p of step1) {
    if (!p.C || !((p.C.plans || []).length || (p.C.homesites || []).length)) continue;
    if (echScopeKey(p) !== key) continue;
    const c = String(p.code || "").toLowerCase();
    if (c && seenCode[c]) continue;
    if (c) seenCode[c] = 1;
    out.push(p);
  }
  return out.length ? out : [head];
}

/**
 * ── ONE COMMUNITY'S CARDS ARE ITS DISTINCT PLANS, FAMILY-WIDE (2026-09-14) ──────────
 *
 * `echPlanScope` counts a masterplan and its collections as ONE community (the
 * dedup-by-parent that keeps the card count honest), so a sole masterplan reached the
 * positional branch as a single pack and card N read plan N OF THAT ONE PACK. Driven on
 * dev — CRS · Grand Opening Thank You · Brunswick Plantation alone: the masterplan's own
 * pack has no plans and two homesites built on the same plan, so cards 1 and 2 both read
 * "Blakely" while the collections under it held other plans. This deals the whole
 * family instead — the head pack's plans, then each collection's, then any homesite's
 * plan not yet seen — DISTINCT BY PLAN NAME, in Step 1 order. Card N is entry N. A plain
 * community with plans [A, B, C] is unchanged: the same three, the same order. Each
 * entry names the pack it came from so the card's photo, hero and View link follow the
 * plan's own community, and whether it is read off `plans` or off a homesite row.
 */
export function echSoleDeal(ctx: EchCtx, scope: Pack[]): { C: Pack["C"]; pIdx: number; home: boolean }[] {
  const out: { C: Pack["C"]; pIdx: number; home: boolean }[] = [];
  if (scope.length !== 1) return out;
  const seen: Record<string, 1> = {};
  const key = (n: unknown) => String(n || "").trim().toLowerCase();
  for (const p of echSoleFamily(ctx, scope[0])) {
    (p.C.plans || []).forEach((pl, i) => {
      const k = key((pl as { name?: string }).name);
      if (!k || seen[k]) return;
      seen[k] = 1;
      out.push({ C: p.C, pIdx: i + 1, home: false });
    });
    (p.C.homesites || []).forEach((h, i) => {
      const k = key((h as { plan?: string }).plan);
      if (!k || seen[k]) return;
      seen[k] = 1;
      out.push({ C: p.C, pIdx: i + 1, home: true });
    });
  }
  return out;
}

/** the parent key one Step 1 pack answers to — what `echPlanScope` dedups on */
function echScopeKey(p: Pack): string {
  return (echParentCommunity(p.name || (p.C.community && p.C.community.name) || "") || p.code).toLowerCase();
}

export function echPlanScope(ctx: EchCtx): Pack[] {
  const out: Pack[] = [];
  const seen: Record<string, 1> = {};
  /* STEP 1'S OWN COMMUNITIES ONLY — NOT `pickPacks`. `echPacks` also carries every pack
     the WIZARD happened to fetch while browsing, which is a browsing artifact and not
     what the send is scoped to: auto-filling from it would put a community the requester
     merely looked at onto the card, and (measured) it re-filled a card the requester had
     just cleared at Market focus. An EXPLICIT pick still resolves against every pack —
     that path never comes through here. */
  const step1: Pack[] = [];
  if (ctx.pack) step1.push({ code: packCode(ctx.pack), name: String((ctx.pack.community && ctx.pack.community.name) || ""), C: ctx.pack });
  for (const p of ctx.collPacks || []) if (p && p.C) step1.push(p);
  for (const p of step1) {
    /* HOMESITES COUNT AS INVENTORY FOR A CARD (2026-08-24). The market/division tier
       builds its packs from /homesites.json, which carries NO plans at all, and an AOI
       like JAX/Duval resolves to a community with 2 plans and 0 available homes. Filtering
       on plans alone made every one of those scopes "dry", so the card kept whatever the
       last scope had written — Jack, switching Communities → Area of Interest: "not seeing
       it for area of interest or market or division." A homesite row carries a plan name,
       the same three spec numbers and a photo, so it can answer the card honestly. */
    if (!p || !p.C || !((p.C.plans || []).length || (p.C.homesites || []).length)) continue;
    const parent = echScopeKey(p);
    if (seen[parent]) continue;
    seen[parent] = 1;
    out.push(p);
  }
  return out;
}

/** one source key → one value at the chosen level, or "" when nothing has loaded */
export function echDynVal(src: string, lvl: string, ctx: EchCtx, doc?: DynBlock[], m?: DynBlock): string {
  const s = String(src || "");
  lvl = lvl || "community";

  /* the initiative seed is parked (see the ECH_DYN comment) — the route returns the
     wrong row often enough that it must not write into a customer email */
  if (s.indexOf("initiative.") === 0) return "";

  /* ── A DECLARED DATA-EXTENSION COLUMN (2026-09-02) ───────────────────────────────
     `de:<externalKey>:<column>`, produced by `echDeclaredDyn` from a block's own header.
     Resolved from the row the hook fetched for the current division. Matched on the
     column LOWERCASED, because the rowset API lowercases every name and the header is
     written in the DE's own casing. Empty is "" — never a fallback, never the field's
     default: Layne's rule for these blocks is "empty means hide", and the renderer
     owns that decision, not the fill. */
  const de = DE_SRC.exec(s);
  if (de) {
    const rows = ctx.deRows;
    const row = rows ? rows[de[1]] : undefined;
    /* `DE_FAILED` reads as "" here exactly as `null` and `undefined` do — the
       resolver's job is the value, and there isn't one in any of the three cases. The
       states diverge for the GATE, not for the fill (see `EchCtx.deRows`). Tested
       rather than left to the truthiness of a non-empty string. */
    if (!row || deFailed(row)) return "";
    return String(row[de[2].toLowerCase()] ?? "");
  }

  /* ── LIVE PLANS AND HOMESITES ────────────────────────────────────────────
     `plan.<n|self>.<field>` and `home.<n>.<field>` read the community's SFMC pack.
     `self` means "whichever Featured block this is". Anything unresolved returns ""
     and the pending guard stops Dynamic writing that emptiness over real copy. */
  const pm = /^(plan|home)\.([0-9]+|self)\.([a-z]+)$/.exec(s);
  if (pm) {
    /* THE PICK CHOOSES THE PACK (2026-08-20). With a masterplan on Step 1 the plans
       the requester saw all came from CHILD COLLECTIONS, so resolving only against
       `ctx.pack` refused every pick and left the canvas unmoved. `ctx.pack` stays the
       fallback and stays first in the list, so a plain community is unchanged. */
    const packs = echPacks(ctx);
    const kind = pm[1], f = pm[3];
    const idx = pm[2] === "self" ? (doc && m ? echPlanIdx(doc, m) : 1) : parseInt(pm[2], 10);
    /* ── STEP 1 IS NO LONGER REQUIRED (2026-08-20, Jack: "i don't even need a
       community — i click the + button and can select the info myself") ────────────
       `C` used to be `ctx.picks.length === 1 ? ctx.pack : null` and an early "" on
       null, which made Step 1's single-community scope a hard precondition: at Market
       focus, or with nothing picked, every plan and home source returned "" and the
       wizard's Done could not move the canvas. That is the silent no-op this codebase
       has hit twice — the pick stores, the selected-cards panel updates, the email
       does not change.
       THE ORDER IS DELIBERATE AND IT IS WHY THIS IS A SUPERSET: Step 1's pack STILL
       WINS whenever there is one, so the POSITIONAL fallback (`[idx-1]`, taken only
       when a pick is absent or no longer resolves) reads exactly the pack it always
       read. The wizard's own pack is consulted only when Step 1 has none — and the
       EXPLICIT pick is resolved against `own` / `echHomeIn` below, which search every
       pack in `echPacks` regardless. */
    const picked = (kind === "home" || pm[2] === "self")
      ? echPackFor(packs, m, kind === "home" ? ECH_HOME_PICK : ECH_PLAN_PICK)?.C
      : undefined;
    let C = (ctx.picks.length === 1 ? ctx.pack : null) || picked || null;
    /* ── ONE CARD, ONE COMMUNITY (2026-08-24 — see `echPlanScope`) ────────────────
       `C` above is Step 1's SOLE community, or the community an explicit pick named.
       With two or more communities picked — or at Area of Interest / Market / Division
       focus — it was `null`, every plan source returned "" and the Featured card kept
       Layne's seed. The scope list answers it instead: this card's position among the
       Featured blocks chooses the community, and it shows that community's FIRST plan.
       An explicit pick still wins, and a single community is untouched. */
    let pIdx = idx;
    /* the sole-family deal handed this card a HOMESITE row's plan — read it off
       `homesites[pIdx-1]`, never `plans[pIdx-1]` */
    let soleHome = false;
    if (kind === "plan" && pm[2] === "self" && !picked) {
      const scope = echPlanScope(ctx);
      /* the ordinal is counted over the SAME set the scope is — plans cards only. Using
         `echPlanIdx` (every Featured block, any level) mapped card 1 to community 2 the
         moment a Featured HOMES or COMMUNITIES card sat ahead of it. (audit, 2026-08-24) */
      const cIdx = doc && m ? echPlanCardIdx(doc, m) : idx;
      if (scope.length > 1) {
        /* card N reads the N-th entry of the round-robin deal — community N's first plan
           while there are communities left, then the first community's second plan, and
           so on (2026-09-11, see `echPlanDry`). A card past the dealt inventory has
           nothing to say; `echPlanDry` reports the same condition to `echDynPatch`, which
           skips the field and leaves Layne's own content on the card — returning "" here
           would have BLANKED it, because an empty is authoritative in the fill. */
        const one = echPlanDeal(scope)[cIdx - 1];
        if (!one) return "";
        C = one.C;
        pIdx = one.pIdx;
      } else {
        /* ── ONE COMMUNITY: CARD N IS ITS PLAN N (2026-08-24) ──────────────────────
           `C` is only non-null when Step 1 named a SOLE community; an Area of Interest
           resolves to communities without naming one (JAX / Palm Coast → Lakeview
           Estates with 0 plans and Resilience at Palm Coast with 3), which left the card
           on its seed with a real floorplan in hand. Read live off the running app. */
        if (!C && scope.length === 1) C = scope[0].C;
        pIdx = cIdx;
        /* ── AND PLAN N IS THE N-TH DISTINCT PLAN OF THE WHOLE FAMILY (2026-09-14) ──
           see `echSoleDeal`: a sole masterplan's cards were all reading one pack. The
           entry names the pack and whether it is a plans row or a homesite row. */
        if (scope.length === 1) {
          const one = echSoleDeal(ctx, scope)[cIdx - 1];
          if (!one) return "";
          C = one.C;
          pIdx = one.pIdx;
          soleHome = one.home;
        }
      }
    }
    /* ── AN EXPLICIT PLAN PICK NEEDS NO STEP-1 COMMUNITY (2026-08-28) ─────────────
       `C` is Step 1's SOLE community, and it is null the moment a send names two. That
       was a hard precondition for every plan source, so a Floorplans pick made at
       Market or AOI focus — where the wizard lists communities precisely because Step 1
       named none — returned "" for all seventy row fields and the table came back a
       heading with no rows. The pick resolves against every pack in hand (`echPlanIn`),
       so it does not need `C`; only the POSITIONAL fallback below does. */
    const planPicked = kind === "plan" && pm[2] !== "self"
      ? echPlanIn(packs, m, idx) : undefined;
    /* the pick names its own community, so it SUPPLIES the scope the rest of this
       branch reads (photo backfill, community hero, the View link's community page)
       rather than merely bypassing the guard */
    if (!C && planPicked) C = packs.find((p) => p.code === planPicked.code)?.C || null;
    /* ── AN UNPICKED HOMES CARD ON A MULTI-COMMUNITY SEND (2026-09-11) ─────────────
       `C` is null the moment Step 1 names two communities (or a market resolves
       several), so every homes card returned "" before `echHomeCardFallback` — which
       deals homes ACROSS the packs — was ever consulted. That is why a market-level
       table→cards toggle produced cards with nothing on them. The fallback supplies the
       pack the home came from; `C` becomes that pack's content. */
    if (!C && kind === "home" && pm[2] === "self" && echFeatOf(m) === "homes") {
      const fb0 = echHomeCardFallback(doc, m, packs);
      if (fb0) C = packs.find((p) => p.code === fb0.code)?.C || null;
    }
    if (!C) return "";
    if (kind === "plan") {
      /* AN EXPLICIT PICK BEATS THE POSITION (2026-08-20 — see the ECH_PLAN_PICK
         block above).
         ── AND `plan.<n>` CONSULTS IT TOO NOW (2026-08-28) ─────────────────────────
         The note that used to stand here — "No ECH block declares `plan.<n>` today;
         if one ever does, its `<n>` should keep meaning `<n>`" — was already false
         when it was written: `LST_PLANS` declares `plan.1` … `plan.10`, which is the
         Listing table at Floorplans level. So the ONE thing that could read a plan
         pick was `self`, which the table never asks for, and a floorplan pick moved
         nothing on the canvas. `<n>` still means row `<n>`; what changed is that row
         `<n>` reads the `<n>`-th PICK when there is a pick list, and its position
         only when there is not — the same rule `home.<n>` has followed since
         2026-08-20, and the reason the two levels now behave alike.
         `?? [idx - 1]` is the fallback, so a pick that no longer resolves lands on
         the SAME row it would have before this change. */
      /* `?? C` and not `undefined`: a pick with no code slot at all (never produced
         by `planPickKey`, but readable out of an older saved document) still resolves
         against the Step-1 community exactly as it did before this change —
         `echPlanFor` does its own code check, so a MISmatch is still refused. */
      const own = (pm[2] === "self" ? picked : undefined) ?? C;
      const planList = String((m && m.data && m.data[ECH_PLAN_PICK]) || "")
        .split("\n").map((x) => x.trim()).filter(Boolean);
      /* past the end of a pick list the row is EMPTY, not the community's next plan —
         the same "a pick is the whole truth" rule the home rows follow */
      const pastPlans = planList.length > 0 && pm[2] !== "self" && idx > planList.length;
      let p: PlanInfo | undefined =
        (pm[2] === "self" ? echPlanFor(own, m) : planPicked?.p)
        ?? (pastPlans || soleHome ? undefined : (C.plans || [])[pIdx - 1]);
      /* ── A COMMUNITY WITH NO PLANS ANSWERS FROM A HOMESITE (2026-08-24) ───────────
         The market and division tiers build their packs from /homesites.json, which
         carries no `plans` at all. Rather than leave those scopes unable to fill a card,
         read the homesite row: it has the PLAN NAME, the same sqft/beds/baths trio and a
         photo. Nothing is invented — the numbers are that home's own, and the name is
         the plan it is built from. */
      if (!p && !pastPlans) {
        const h0 = (C.homesites || [])[pIdx - 1] as (HomesiteInfo & { img?: string }) | undefined;
        if (h0 && h0.plan) {
          p = { name: h0.plan, beds: h0.beds, baths: h0.baths, sqft: h0.sqft, price: h0.price,
            hero: h0.img || "", url: h0.url || "" } as unknown as PlanInfo;
        }
      }
      if (!p) return "";
      /* ── AND A PLAN WITH NO ARTWORK BORROWS ITS OWN HOME'S PHOTO (2026-08-24) ─────
         Jack, on the third card: "why no image for the Charle?" Measured: CHARLE II
         carries no hero, elevation or kitchen image in `/sfmc-content`, so the card
         rendered the 270x180 placeholder next to two cards with real photos. The worker
         already does exactly this backfill for homesite rows (imgSrc: plan → community);
         the plans list never got it. Same order, same reasoning: a photo of a home built
         to this plan, then the community's own hero. Never a placeholder. */
      /* a plan whose artwork is itself the "Images coming soon" tile borrows too —
         `echFirstImg` skips placeholders at every rung (2026-09-11) */
      if (f === "image" && !echFirstImg(p.hero, p.elevation, p.kitchen)) {
        const same = (C.homesites || []).find((hh) => {
          const x = hh as HomesiteInfo & { img?: string };
          return !!echRealImg(x.img) && String(x.plan || "").trim().toLowerCase() === String(p!.name || "").trim().toLowerCase();
        }) as (HomesiteInfo & { img?: string }) | undefined;
        const anyImg = same && echRealImg(same.img);
        const commHero = echRealImg((C.community && (C.community as { hero?: string }).hero) || "");
        const borrowed = anyImg || commHero;
        return borrowed ? echCardImg(borrowed, m) : "";
      }
      if (f === "name") return echPlanName(p.name);
      if (f === "num") return String(p.num == null ? "" : p.num);
      if (f === "image") return echCardImg(echFirstImg(p.hero, p.elevation, p.kitchen), m);
      if (f === "alt") return p.name ? echPlanName(p.name) + " floorplan" : "";
      if (f === "beds") return echBeds(p.beds);
      if (f === "baths") return echBaths(p.baths);
      if (f === "sqft") return echSqft(p.sqft);
      if (f === "price") return echMoney(p.price);
      if (f === "url") return String(p.url || "");
      if (f === "tour") return String(p.tour || "");
      if (f === "ei") return String(p.ei || "");
      return "";
    }
    /* THE LISTING TABLE'S TEN SLOTS ARE SLOTS, NOT THE FEED'S FIRST TEN HOMES
       (2026-08-20). `home.<n>` is consulted against the pick list at n-1 — unlike
       `plan.<n>`, because every one of ECH_ListingTable's `home1..10*` fields is a
       ROW the requester is choosing the contents of, which is the entire point of
       the picker on that block. Past the end of the pick list, position again. */
    /* SEARCHED ACROSS EVERY PACK, for the same reason the plan branch is: with a
       masterplan on Step 1 the wizard's homesite step draws from the collections, so
       a row picked there is not in `ctx.pack` at all. The pack the home came from
       travels with it — `homesite` strips the COMMUNITY CODE off the lot id, and the
       masterplan's code would strip nothing. */
    /* ── `home.self` READS THE BLOCK'S OWN FIRST PICK, NOT ITS POSITION'S ──────
       (2026-08-22.) `idx` is this Featured card's position among the Featured blocks,
       which is right for the POSITIONAL fallback and wrong for the pick list: a card
       holds ONE home, so the third card was asking for the third key of a one-key
       list, getting nothing, and falling through to `C.homesites[2]` — its community's
       third home, which for a one-home community is nothing at all. That is Jack's
       "the first card is still a mockup/empty": whichever card's position ran past its
       own pick list rendered blank, and the ones that looked right were luck (card two
       fell back to its pack's second home, which happened to be the intended one).
       `home.<n>` still means position <n> — that is the Listing table's ten rows. */
    const hit = echHomeIn(packs, m, pm[2] === "self" ? 1 : idx);
    /* ── A PICK IS THE WHOLE TRUTH (Jack, 2026-08-28: "show only what I picked") ──
       The positional fallback below used to run for ROWS PAST THE PICK LIST too, so a
       two-home pick filled rows 3–10 from the community's own inventory. That rule and
       the markup re-cut — which cuts the table to the pick count — have contradicted
       each other since 2026-08-22, and the cut has been the one the canvas obeys. The
       cost of the disagreement was measured on the QA build: canvas 3 rows, Inspector
       "10 of 10 shown", and the picked home appearing twice, once where the pick put it
       and once where the feed's fallback had already put it (248/Teller at 3 and 5).
       So a block that HAS a home pick answers "" past the end of that list. The
       fallback still stands where there is no pick at all, which is the fed table, and
       for `home.self`, which is a Featured card asking for its own one home. */
    const pickList = String((m && m.data && m.data[ECH_HOME_PICK]) || "")
      .split("\n").map((x) => x.trim()).filter(Boolean);
    /* PAST THE END OF THE LIST, not "the key did not resolve". A pick whose home has
       left the feed still falls back to its position — that row was asked for. */
    const past = pickList.length > 0 && pm[2] !== "self" && idx > pickList.length;
    /* ── AN UNRESOLVED PICK IS NOT A MISSING PICK (2026-09-22, #135/#139) ──────────────
       The rule above ("a pick whose home has left the feed still falls back to its
       position") is what put 11848 Mimicking Song Ln on Kailey's preview rail in place of
       the home she chose: the rail's ctx cannot see the Studio's packs, her key resolved
       to nothing, and the fallback dealt her card the next free home. A key IS a choice.
       When no pack in hand can show it the answer is "", and `echDynPatch` leaves the
       block's current values standing (see `echHomePickUnresolved` there) — nothing is
       invented, and the two renderings of one email stop disagreeing. */
    const pickedHere = pickList.length > 0 && !!pickList[(pm[2] === "self" ? 1 : idx) - 1];
    if (!hit && pickedHere) return "";
    /* a HOMES card with no pick of its own: the next free home across every pack, by its
       ordinal among the unpicked cards — not `ctx.pack[position among all Featured]` */
    const homesSelf = !hit && !past && pm[2] === "self" && echFeatOf(m) === "homes";
    const fb = homesSelf ? echHomeCardFallback(doc, m, packs) : undefined;
    const h: (HomesiteInfo & { lot?: string }) | undefined =
      hit?.h ?? (homesSelf ? fb?.h : (past ? undefined : (C.homesites || [])[idx - 1]));
    if (!h) return "";
    if (f === "homesite") return echShortLot(h.lot, hit ? hit.code : fb ? fb.code : ((C.community && C.community.code) || C.code));
    if (f === "plan") return echPlanName(h.plan);   /* the table's Plan column is a plan name too */
    /* ── `name` AND `img`: WHAT A FEATURED *HOME* CARD NEEDS (2026-08-20) ────────
       The Listing table only ever wanted columns; a Featured card wants a title and a
       photo, which is why these two arrived with `ECH_FEAT.homes`.
       `name` is the monolith's own card title, exactly: `hm.addr||hm.plan||"Home"`
       (index.html `__dynItemsForLevel`) — MINUS the literal "Home". A street address
       identifies a homesite where a plan name cannot (two homes in one community share
       a plan); the plan name is the honest fallback for the rows that carry no street;
       and "" is the honest answer for a row with neither, because a fill that invents
       a label is a fill that lies in a live send. */
    /* the street address is a real address and is left exactly as the feed spells it;
       the PLAN-NAME fallback goes through the same casing rule as every other plan
       name, or a photo-less home would shout "CHARLE II" where its neighbour reads
       "202 Oak" (2026-08-24) */
    if (f === "name") return h.addr ? String(h.addr) : echPlanName(h.plan);
    /* Lennar serves a branded "Images coming soon" file for photo-less homes — it
       LOADS fine, so it passes every truthiness test and would ship as the featured
       photo in a live send (Homesite Spec Sheet audit, 2026-08-21). The worker now
       cleans and backfills at the source; this is the belt for older cached packs.
       "" is the honest answer — the empty-image rule already hides the block's img. */
    if (f === "img") {
      const own = echRealImg(h.img);
      if (own) return echCardImg(own, m);
      /* ── A HOME WITH NO PHOTO BORROWS ITS PLAN'S, THEN THE COMMUNITY'S (2026-09-11) ──
         Driven live on dev, Granary Park → Now Selling → table→cards: the one card was
         "2633 Field Daisy Ln" with the 270×180 grey placeholder, because that homesite
         row carries no photo. The PLAN cards got this exact borrow on 2026-08-24 ("why no
         image for the Charle?"); the homes cards never did. Same order, same reasoning: a
         render of the plan this home is built to is an honest photo of it; the
         community's hero is the last resort. Never a placeholder into a live send. */
      const packCode = hit ? hit.code : fb ? fb.code : ((C.community && C.community.code) || C.code);
      const srcC = (packs.find((p) => p.code === packCode) || { C }).C || C;
      const norm = (v: unknown) => String(v || "").trim().toLowerCase();
      const pl = (srcC.plans || []).find((p) => norm(p.name) === norm(h.plan));
      /* the borrowed rungs are filtered too — a plan whose render is the "Images coming
         soon" tile is not a photo of anything (2026-09-11, Granary Park 50s / Segan) */
      const borrowed = echFirstImg(pl && pl.hero, pl && pl.elevation, pl && pl.kitchen, srcC.community && (srcC.community as { hero?: string }).hero);
      return borrowed ? echCardImg(borrowed, m) : "";
    }
    if (f === "sqft") return echSqft(h.sqft);
    if (f === "beds") return echBeds(h.beds);   /* whole number, per the block's own rule */
    if (f === "baths") return echBaths(h.baths); /* whole or half only */
    if (f === "price") return echMoney(h.price);
    if (f === "url") return String(h.url || "");
    /* the homesite's own schedule-tour page — see ECH_FEAT.homes.cta (2026-09-08) */
    if (f === "tour") return echHomeTourUrl(h.url);
    if (f === "addr") return String(h.addr || "");
    /* the FULL lot id — the key ent.Sitecore Homesites resolves by at send time
       (echSendTime). `homesite` above is the SHORT display form; this one is the
       join key and never renders. */
    if (f === "lot") return String(h.lot || "");
    /* the Next Gen logo switch — see echYesNo (2026-10-29) */
    if (f === "nextgen") return echYesNo(m, "ShowNextGen", !!h.nextGen);
    return "";
  }

  const I = echCommInfo(ctx);
  const dr = ctx.division || null;
  /* `aoi` reads the market too (2026-08-24) — an Area of Interest send presents as its
     market, so the locator and the phone both resolve through `mk`. See the AOI note in
     `community.name` below. Narrower scopes still get `null`: a community send must not
     have a market name available to fall back onto, which is what kept the header from
     reading "Phoenix, AZ | Phoenix, AZ". */
  const mk = (lvl === "market" || lvl === "aoi") ? ctx.market || null : null;

  if (s === "community.name") {
    /* ── A DIVISION SEND HAS NO SINGLE COMMUNITY EITHER (2026-08-24) ─────────────
       This returned the DIVISION's name, and with `cityState` now feeding the
       locator the header read "Jacksonville | Jacksonville, FL | 904-380-0777" —
       the same duplication the market branch was fixed for on 8/21, one level up.
       Honest empty; the segment and its pipe drop, and the locator says where. */
    if (lvl === "division") return "";
    /* NO market fallback any more (Jack, 2026-08-21: "you're auto putting the area
       in community instead of the new field"). The fallback predates `cityState`:
       with no locator field, "Phoenix, AZ" in the community slot was better than an
       empty header. Now the market's name+state is `community.citystate`'s answer,
       and echoing it here rendered "Phoenix, AZ | Phoenix, AZ | phone" on every
       market send. A market send has no single community — honest empty, and the
       segment-drop rule in the registry removes the dangling pipe. */
    if (lvl === "market") return "";
    /* ── AN AREA-OF-INTEREST SEND READS AS ITS MARKET, NOT AS THE AOI (2026-08-24,
       Jack on the live dev site: "area of interest not market level is getting pulled
       in") ─────────────────────────────────────────────────────────────────────────
       This returned the AOI label, so the JAX / Palm Coast send headed itself "Palm
       Coast | 904-380-0777" while the phone underneath it was the DIVISION's. An AOI is
       an internal targeting grouping — it is not a place a buyer recognises as the
       sender, and it has no phone, no page and no address of its own (see the phone
       branch below, which already falls to the division for exactly that reason). So an
       AOI send is a market send for header purposes: empty here, and the locator says
       the market. `ctx.aoiLabel` still names the scope everywhere it belongs — the
       panel, the picker groups and the audience — just not in the customer's header. */
    if (lvl === "aoi") return "";
    /* Jack's ask was "reference the community from tab 1", so the Step-1 selection
       is the fallback for the NAME. The address can't come from here. */
    if (I && I.name) return echClean(I.name);
    /* with a shared parent the masterplan names the send — pick[0] would name one of
       its collections and read as if the others were not on it (2026-08-27) */
    const cs = ctx.parent || ctx.picks[0];
    return cs && cs.name ? String(cs.name).replace(/\*+/g, "").trim() : "";
  }
  /* ── THE MASTERPLAN, WHEN IT IS A SECOND FACT AND NOT THE SEND (2026-08-28) ────
     For the Grand Opening blocks, which name both. Two refusals, both deliberate:
      · anything broader than community level — a market send has no masterplan;
      · `ctx.parent` set, i.e. several collections of one masterplan. There the
        masterplan IS the send's identity and `community.name` already returns it, so
        filling this too would render "Timber Ridge, in Timber Ridge".
     What is left is the honest case: ONE community pick that sits under a masterplan,
     resolved by `echParentName` off the same pageUrl path rule everything else uses. */
  if (s === "community.masterplan") {
    if (lvl !== "community") return "";
    if (ctx.parent) return "";
    return echClean(String(ctx.parentName || ""));
  }
  /* ── THE SECTION HEADER FOLLOWS THE BLOCK IT HEADS (2026-08-28) ────────────────
     Fixed fields (see `ECH_FIXED`), so these resolve from the TEMPLATE, not from
     Step 1. The default is Layne's own authored value — her wording, read out of the
     block's guardrail rather than restated here, so changing it in Content Builder
     changes it in the app. The one override is the case where her default is simply
     wrong: "Explore Homes" over the amenity grid. */
  if (s === "table.title" || s === "table.viewall") {
    const authored = (() => {
      const meta = m ? echMeta(m.type) : null;
      const f = meta && meta.full && meta.full.fields
        && meta.full.fields[s === "table.title" ? "sectionTitle" : "linkLabel"];
      return String((f && f.default) ?? "");
    })();
    if (s === "table.viewall") return authored;
    /* the next CONTENT block below this one, in the live document */
    if (doc && m) {
      const i = doc.findIndex((b) => b && b.id === m.id);
      const next = i >= 0 ? doc[i + 1] : null;
      if (next && echNameOf(next.type) === "ECH_Amenities") return "Amenities";
    }
    return authored;
  }
  /* the address comes from the community's /sfmc-content pack (or Airtable), via
     echCommInfo(). Empty until it lands — honest, and ctx.pending stops Dynamic
     writing that emptiness over a real value. */
  if (s === "community.address1") return (I && I.addr) || "";
  if (s === "community.address2") {
    if (!I || !I.city) return ""; /* a bare zip with no city reads as noise */
    /* the address line under the header joins city + state the same way (2026-10-30, Justin, SLC 1041310) */
    return [echCityState(String(I.city || ""), String(I.state || "")), I.zip].filter(Boolean).join(" ");
  }
  /* the Header's "| City, State |" line — city+state only, NO zip (that line is a
     locator, not a mailing address). Scope-aware: a community resolves its own
     city/state; market scope resolves the market's own name + state. */
  /* deliberately empty — mapped onto label fields a level needs CLEARED (the
     Featured Community card blanks the sq ft / beds / baths unit labels) */
  if (s === "none") return "";
  /* the community's honest card facts (Jack's own AOI mockup content, 2026-08-21:
     "Lavon, TX / From $399,990 · 14 homes available") — real feed numbers only */
  if (s === "community.fromprice") {
    if (lvl !== "community") return "";
    const C0 = ctx.pack;
    const hs = (C0 && C0.homesites) || [];
    const prices = hs.map((h) => Number(h.price)).filter((v) => v > 0 && isFinite(v));
    if (!prices.length && C0) (C0.plans || []).forEach((pl) => { const v = Number(pl.price); if (v > 0 && isFinite(v)) prices.push(v); });
    if (!prices.length) return "";
    return "From " + echMoney(Math.min(...prices));
  }
  if (s === "community.availcount") {
    if (lvl !== "community") return "";
    const n = ((ctx.pack && ctx.pack.homesites) || []).length;
    if (!n) return "";
    return n + (n === 1 ? " home available" : " homes available");
  }
  if (s === "community.citystate") {
    /* `aoi` answers with the MARKET's locator, same as `market` — see the AOI note in
       `community.name` above. Without this the fall-through below picked ONE of the
       AOI's communities out of `ctx.pack` and headed the send with that community's
       city, which is a narrower claim than the send actually makes. */
    if (lvl === "market" || lvl === "aoi") return mk && mk.name ? echCityState(String(mk.name), String(mk.state || "")) : "";
    /* ── THE PACK CALLS IT whcCity, NOT city (2026-08-24) ────────────────────────
       Lauren, on the live dev site: the Header read "Granary Park | City, State |
       904-712-6860" — the block's own placeholder, showing because this resolver
       returned "" and the seed survived. Measured `/sfmc-content?community=7855`:
       its `community` object carries `whcCity` / `whcState` (the welcome-home-centre
       address) and NO `city` / `state` at all, so the old `!I.city` guard was true
       for every community. `cityOf` in dynamicFill already reads both spellings —
       this is the same rule, in the one place that feeds Layne's cityState field. */
    const pc = (ctx.pack && ctx.pack.community) as ({ city?: string; state?: string; whcCity?: string; whcState?: string } | null | undefined);
    const city = String((I && I.city) || (pc && (pc.city || pc.whcCity)) || "").trim();
    const st = String((I && I.state) || (pc && (pc.state || pc.whcState)) || "").trim();
    if (!city) return "";
    return echCityState(city, st);
  }
  if (s.indexOf("social.") === 0) {
    /* ── THE FALLBACK IS PER CHANNEL, NOT PER ROW (2026-08-25) ─────────────────────
       BEN (Bentonville-Fayetteville), via Feedback: "missing social links, required to
       update but nowhere can you edit this." Measured on /sfmc-social-links: BEN's row
       EXISTS and every one of its six channels is empty — so `SL[dv] || SL.LNR` chose
       BEN's row (an object, therefore truthy), the national row was never consulted, and
       every icon resolved to "" on a block whose fields the panel doesn't surface.
       Jack's ruling: "if we don't have the specific division's URL for social we need to
       have the Lennar corp social link as back up." So each channel falls back on its
       own — a division with four real handles keeps its four and borrows the rest. */
    const ch = s.slice(7);
    const SL = ctx.social;
    if (!SL) return "";
    const dv = String(ctx.divisionCode || "").toUpperCase();
    const own = SL[dv] || null;
    const nat = SL.LNR || null;
    const val = (own && own[ch]) || (nat && nat[ch]) || "";
    return val ? String(val) : "";
  }
  if (s === "community.pageurl") return echScopeUrl(ctx, lvl);
  /* ── THE COMMUNITY'S OWN HERO PHOTO (2026-08-20, for `ECH_FEAT.comms`) ────────
     `/sfmc-content`'s CommunityInfo really does carry `hero`, and the wizard already
     paints its community cards with it (`DynFeaturePicker`: `pk.C.community.hero`) —
     so a Featured COMMUNITY card has a real image source and does not need one
     invented. ONE PICK ONLY, the same gate every plan/home source has: with several
     communities in the send there is no single hero, and `echLevel` has already
     fallen back to market, so this returns "" and the mapping's `levels:["community"]`
     stops it being asked in the first place.
     (Note this does NOT reopen ECH_MainImage, which stays MANUAL-ONLY: that was Jack's
     product call about a hero the requester picks out of Bynder, not a claim that the
     feed has no photo.) */
  if (s === "community.hero") {
    const pc = ctx.picks.length === 1 ? ctx.pack?.community || null : null;
    /* a Featured community card's photo takes the same Bynder crop (2026-09-25) */
    return echCardImg(String((pc && pc.hero) || ""), m);
  }
  if (s === "community.directions") {
    /* a street address is best, but city+state still gives a usable maps link — 330
       of 900 masterplans have no street address, and "Arlington Heights, Warner
       Robins, GA" gets a buyer there. The block's guardrail puts no lennar.com
       restriction on this field, unlike the image link, so a maps deep link is
       allowed here. */
    if (!I || (!I.addr && !I.city)) return "";
    const full = [I.addr || I.name, I.city, I.state, I.zip].filter(Boolean).join(", ");
    return "https://www.google.com/maps/dir/?api=1&destination=" + encodeURIComponent(full);
  }
  if (s === "community.phone") {
    let ph = "";
    if (lvl === "division") ph = (dr && dr.phone) || "";
    else if (lvl === "market") ph = (mk && mk.phone) || (dr && dr.phone) || "";
    /* an AOI has no phone of its own, and as of 2026-08-24 it presents as its MARKET —
       so it takes the market's number when there is one and the division's otherwise,
       identical to the market branch above. */
    else if (lvl === "aoi") ph = (mk && mk.phone) || (dr && dr.phone) || "";
    else ph = (I && I.phone) || (dr && dr.phone) || "";
    return echPhone(ph);
  }
  return "";
}

/**
 * A photo url that is really Lennar's "Images coming soon" card, or any other
 * placeholder, reads as NO photo (2026-09-11). Driven live on dev after #246, Granary
 * Park 50s → table → cards: the Segan cards showed the branded "Images coming soon"
 * tile at 540 wide, because the homesite's own photo was knocked out correctly and the
 * BORROWED plan render was that same file — the borrow chain never asked. Every rung of
 * the chain now goes through this: a placeholder anywhere falls through to the next
 * honest photo, and only "" ships when there is none. Same pattern echOrder uses to
 * sort photo-less homes last.
 */
export function echRealImg(u: unknown): string {
  const im = String(u || "").trim();
  return !im || /comingsoon|coming-soon|placeholder|no-?image|\/default\//i.test(im) ? "" : im;
}
/** the first REAL photo in a list of candidates, or "" */
export function echFirstImg(...cands: unknown[]): string {
  for (const c of cands) { const v = echRealImg(c); if (v) return v; }
  return "";
}

/**
 * A homesite's schedule-tour page: its lennar.com listing URL + `/schedule-tour`.
 * Only for a real lennar.com listing under /new-homes/ — anything else (a
 * placeholder, a third-party link, "") has no such page and returns "" so the
 * caller falls through to the community-level answer instead of inventing a link.
 */
/**
 * ── ONE SIZE OF FEED PHOTO ON A CARD SET (2026-09-11) ─────────────────────────────
 * Katy on the testing call: "the plan module spit out, I think, three different size
 * images"; Justin: "if one is 270 by 180, the rest need to be 270 by 180" — filed as a
 * future guardrail. A Bynder pick already arrives at the slot's 270×180 transform; a FEED
 * photo (plan hero / elevation / kitchen, homesite img, community hero) is whatever the
 * source holds — measured: lennar.com plan renders are 2070×900 and 1920×1080 at
 * `?w=2100`, so a card set mixed a 2 MB 16:9 render with a 270×180 crop.
 *
 * What the CDN can do, measured 2026-09-11 against cdn.lennar.com/api/images: `w=` resizes,
 * snapping to a preset ladder (w=270→375, w=540→576, w=600→738, w=800→900); `h=`, `fit=`,
 * `mode=crop`, `rmode=crop` and `ar=` are all ignored — it cannot crop. So the honest
 * normalisation is ONE WIDTH: every feed photo on a card asks for `w=540`, the 2× of the
 * card's 270 — the same 576-px asset for every card in the hand-off, and no oversize file
 * next to a thumbnail. The card's own <img width=270 height=180> (Layne's markup) still
 * does the framing; an aspect mismatch (16:9 into 3:2) is unchanged by this and is a crop
 * the CDN does not offer — cropping would need a store for derived images, which the
 * team parked ("Bynder vs Stensul vs SFMC — later", same call).
 *
 * Only cdn.lennar.com/api/images URLs are touched; anything else (Bynder, SFMC image
 * library, a requester's paste) passes through untouched.
 *
 * ── SUPERSEDED 2026-09-14: THE FEED URL SHIPS UNTOUCHED ─────────────────────────
 * Jack, on the Find Your Dream Home preview (JAX, Granary Park + Saddle Oaks): the Halle II
 * card was a BROKEN image. Its feed url is `…/jax_1703_rend_halle-ii_kitchen_1of2_f1.jpg
 * ?d=20250220T160502`, and the rule above appended `&w=540`. Measured in Chrome against
 * cdn.lennar.com on that asset: the exact feed url loads (2070 px); `&w=540`, `&w=576`,
 * `&w=900`, `&w=2100`, `&width=540`, `?w=540` alone and the bare path ALL fail. So the
 * resize the 09-11 rule relied on is not a property of the CDN — it works on some assets
 * and 404s on others, and nothing in the url says which. A broken photo in a live send is
 * the worst outcome this module can produce, and an oversized one is merely heavy (the
 * card's own width/height attributes still frame it), so the one-width normalisation is
 * withdrawn: every feed url ships exactly as the feed spells it. Uniform card sizing, if
 * it comes, needs a derived-image store (parked by the team — see above), not a query
 * param. `echCardImg` is kept as the single seam so that store can plug in here.
 */
export function echCardImg(url: unknown, m?: DynBlock): string {
  const u = String(url || "");
  /* ── THE SEAM IS USED (2026-09-25): A LENNAR.COM PHOTO WITH A BYNDER ORIGINAL SHIPS
     BYNDER'S CROP OF IT, at the card's declared frame — see echBynderMatch. With no block
     in hand, or no match (yet), the feed URL is the answer exactly as before. */
  if (!m || !u) return u;
  const name = echNameOf(m.type);
  if (!/^ECH_Featured/.test(name || "")) return u;
  const f = (echMeta(m.type)?.full?.fields || {}) as Record<string, { width?: number; height?: number }>;
  const w = Number((f.PlanImage && f.PlanImage.width) || 270);
  const h = Number((f.PlanImage && f.PlanImage.height) || 180);
  return bynderCardUrl(u, w, h) || u;
}

export function echHomeTourUrl(url: unknown): string {
  const u = String(url || "").trim();
  if (!/^https?:\/\/(www\.)?lennar\.com\/new-homes\/[^?#\s]+/i.test(u)) return "";
  return u.replace(/[?#].*$/, "").replace(/\/+$/, "") + "/schedule-tour";
}

/**
 * The tour FORM for the community a featured plan card belongs to (2026-09-10): the
 * community page's `/schedule-tour` — Jack 2026-09-11: "schedule tour should default to
 * /schedule-tour … it's all the same url format" (…/oak-hill-village-townhomes/schedule-tour).
 * The feed's `community.tour` (`private_self_tour_url || community_schedule_tour_url`) is
 * only the answer when the community has no lennar.com page to hang the suffix on. "" when
 * the card resolves to no community at all — the caller then falls through to the
 * scope-level answer rather than inventing a link.
 */
export function echFeaturedTourForm(ctx: EchCtx, doc?: DynBlock[], m?: DynBlock): string {
  const packs = echPacks(ctx);
  const picked = m ? echPlanIn(packs, m, 1) : undefined;
  const pack = picked ? packs.find((p) => p.code === picked.code) : undefined;
  const cm = (pack && pack.C.community) || (ctx.pack && ctx.pack.community);
  /* ── THE PLAN'S OWN PAGE NAMES ITS COMMUNITY (2026-09-14) ─────────────────────────
     Jie (CRS), Cypress Preserve + Brunswick Plantation on a Grand Opening Thank You: both
     plan cards' "Schedule a Tour" pointed at bare lennar.com/new-homes/ — the send has no
     single community, the collection packs carry no `community.url`, so this fell through
     to the market scope, which for a two-state division is nothing. But the card knows
     exactly where it is: its plan url is …/cypress-preserve/townhomes/owen, and the
     community page is that url minus the plan slug. Jack's rule — Schedule a Tour is
     always the community url + /schedule-tour — now holds on a card whose community the
     feed never named. */
  const planUrl = String((picked && picked.p && (picked.p as { url?: string }).url) || "");
  const fromPlan = planUrl ? planUrl.replace(/[?#].*$/, "").replace(/\/+$/, "").replace(/\/[^/]+$/, "") : "";
  if (!cm) return echHomeTourUrl(fromPlan);
  return echHomeTourUrl(cm.url) || echHomeTourUrl(fromPlan) || String(cm.tour || "");
}

/* ── THE URL CONTRACT, FROM LAYNE'S FAST TRACK DECK ─────────────────────────
     View Community / View Homes ....... community URL
     Join Our Interest List ............ community URL + #join-interest-list
     Visit Us Today / Schedule a Tour .. community URL + /schedule-tour
     Contact Us ........................ /contact/rfi?communityNumber=&state=&market=
     Get Directions .................... maps link off the address
     RSVP .............................. division-provided, "unable to automate"
   So the LABEL decides the link. */
export function echRfiUrl(ctx: EchCtx, lvl: string): string {
  const I = echCommInfo(ctx) || ({} as Partial<CommInfo>);
  /* the COMMUNITY's market wins over the division's — a division can span markets */
  const mkt = String(I.market || "") || String(ctx.divisionCode || "").toUpperCase();
  const st = String(I.state || "").toUpperCase().trim();
  const qs: string[] = [];
  /* `communityNumber` is the RFI form's COMMUNITY key. With a shared parent the code
     we hold is the masterplan's, and nothing here has verified that the form accepts
     one, so it is left off and the request arrives scoped by state+market — exactly
     what shipped before the parent was recognised. The page link (echScopeUrl) still
     goes to the masterplan; only this query key is held back (2026-08-27). */
  if (lvl === "community" && I.code && !ctx.parent) qs.push("communityNumber=" + encodeURIComponent(I.code));
  if (st) qs.push("state=" + encodeURIComponent(st));
  if (mkt) qs.push("market=" + encodeURIComponent(mkt));
  if (!qs.length) return "";
  return "https://www.lennar.com/contact/rfi?" + qs.join("&");
}
/** the community (or collection) page a Featured card's own URL sits under: a plan URL minus
    its plan slug; a homesite URL minus its numeric homesite id and then the plan slug; a
    community URL as-is. "" for anything that is not a lennar.com listing. */
export function echCardCommunityPage(url: unknown, feat: EchFeat): string {
  const u = String(url || "").trim().replace(/[?#].*$/, "").replace(/\/+$/, "");
  if (!/^https?:\/\/(www\.)?lennar\.com\/new-homes\/[^?#\s]+/i.test(u)) return "";
  if (feat === "comms") return u;
  let page = u;
  if (feat === "homes" && /\/\d+$/.test(page)) page = page.replace(/\/[^/]+$/, "");
  page = page.replace(/\/[^/]+$/, "");
  /* still a community-shaped path (/new-homes/<state>/<market>/<city>/<name>…)? */
  return /\/new-homes\/[^/]+\/[^/]+\/[^/]+\/[^/]+/.test(page) ? page : "";
}

export function ctaUrlFor(label: unknown, lvl: string, ctx: EchCtx, doc?: DynBlock[], m?: DynBlock): string {
  const l = String(label || "").toLowerCase();
  if (/contact|consultant|request info/.test(l)) return echRfiUrl(ctx, lvl);
  if (/directions/.test(l)) return echDynVal("community.directions", lvl, ctx);
  /* ── "CALL NOW" DIALS THE HEADER'S NUMBER (2026-09-22, pilot #128) ─────────────────
     Kailey White (NEV): a button that calls. The destination is the same phone the
     header already shows — `community.phone`, which is the community's line, else the
     market's, else the division's — as a `tel:` link with the digits only, the shape
     Layne's own Contact Us "Call Us" button ships. No number in scope → "" and the
     required gate asks for one, rather than a button that dials nothing. */
  if (/\bcall\b/.test(l)) {
    const digits = echDynVal("community.phone", lvl, ctx, doc, m).replace(/[^\d+]/g, "");
    return digits ? "tel:" + digits : "";
  }
  /* A FEATURED BLOCK'S BUTTONS POINT AT THE THING IT IS FEATURING. On a block that
     shows one floorplan, "View Floorplan" means that plan's page and "Take a Tour"
     means its Matterport link — not the community page. Falls through to the
     community-level answers whenever there's nothing behind the block, so ECH_2CTA is
     unaffected.
     …AND "THE THING IT IS FEATURING" IS WHATEVER THE FEATURE LEVEL SAYS (2026-08-20).
     At Homesites level "View Home" has to reach the HOME, or the button contradicts
     the card above it — the same label lying about its destination that put
     ECH_Featured1CTA into ECH_CTA_PAIRS in the first place. The three sources come
     from `ECH_FEAT[feat].cta`, THE SAME TABLE the field fill reads: the label→intent
     regexes are level-independent, only the source moves, and there is exactly one
     place that says where. An empty source means "this level has no such thing"
     (a homesite has no tour, a community has none of the three) and falls through
     rather than inventing a link. */
  /* ── THE CARD'S OWN PAGE, FOR EVERY OTHER LABEL (2026-09-18 audit) ───────────────
     Driven on a JAX market send, five plan cards filled positionally from the feed:
     "Schedule a Tour" and "View Offer" on every card resolved to the DIVISION landing
     page (…/jacksonville-st-augustine/). The tour form derived its community from the
     card's stored PICK (echFeaturedTourForm, 2026-09-14) — a card the feed filled by
     position has no pick, so it fell to the send's scope, which at market level is the
     division. But the card already knows its page: the same `src.url` that fills "View
     Floorplan" is the plan's URL, and the community page is that URL minus the plan slug
     (minus the homesite number first, on a homes card). So every label that is not the
     item itself lands on the card's community: /schedule-tour, #join-interest-list, or
     the page — the homesite card's own rule (Lauren, 2026-09-08), applied to plans. */
  let cardPage = "";
  if (m && /^ECH_Featured/.test(echNameOf(m.type))) {
    const src = ECH_FEAT[echFeatOf(m)]?.cta || ECH_FEAT.plans.cta;
    const own = src.url ? echDynVal(src.url, lvl, ctx, doc, m) : "";
    cardPage = echCardCommunityPage(own, echFeatOf(m));
    if (src.url && /floorplan|view plan|view home\b|view floor plan/.test(l)) {
      if (own) return own;
    }
    /* ── A TOUR BUTTON IS A TOUR FORM UNLESS THE LABEL SAYS "VIRTUAL" (2026-09-10
       testing call — Justin, on the Cedar Point GO Thank You plan card: "the schedule a
       tour link is a Modsy view floor plan link… it just takes you through a 3D
       rendering") ──────────────────────────────────────────────────────────────────
       `plan.self.tour` is the backend's `virtual_tour_url || video_tour_url` — a
       Matterport/Modsy walkthrough. Serving that under "Schedule a Tour" sends a
       prospect who wanted an appointment into a 3D viewer. So the walkthrough is reached
       only by a label that asks for it (virtual / 3D / video / walkthrough); every other
       tour or appointment label resolves to the featured community's tour FORM —
       `community.tour` (`private_self_tour_url || community_schedule_tour_url`) or the
       community page + /schedule-tour — the same destination the homesite card and the
       community-level rule below already use. Homes level keeps its own source: a
       homesite's `tour` IS its listing's /schedule-tour (echHomeTourUrl). */
    if (src.tour && /tour|appointment/.test(l)) {
      const wantsWalkthrough = /virtual|3d|video|walk-?through/.test(l);
      const homesLevel = echFeatOf(m) === "homes";
      if (wantsWalkthrough || homesLevel) {
        const pt = echDynVal(src.tour, lvl, ctx, doc, m); if (pt) return pt;
      } else {
        const form = echFeaturedTourForm(ctx, doc, m) || echHomeTourUrl(cardPage); if (form) return form;
      }
    }
    if (src.ei && /included|everything/.test(l)) {
      const pe = echDynVal(src.ei, lvl, ctx, doc, m); if (pe) return pe;
    }
  }
  const base = cardPage || echScopeUrl(ctx, lvl); if (!base) return "";
  /* A SUFFIX IS VALID ON A COMMUNITY OR MASTERPLAN PAGE, NEVER A MARKET OR STATE PAGE.
     Was `lvl === "community"` plus a path check — so a sole masterplan pick (whose scope
     url IS the masterplan page, see echScopeUrl step 2) came back as the bare page for
     every label: "Join Interest List" on a masterplan Coming Soon lost its anchor (Uche,
     2026-09-10 call). Jack 2026-09-11: View Community → the MPC page; Join Interest List →
     the community/collection page + #join-interest-list; a masterplan page carries the same
     anchor (…/st-johns/brightlyn-at-silverleaf#join-interest-list is `meta-Page: MPC`), and
     Schedule a Tour is the page + /schedule-tour everywhere. So the test is the PAGE'S
     SHAPE: /new-homes/<state>/<market>/<city>/<name> or deeper — a community, a masterplan
     or a collection — and not the level the form reports. /new-homes/<state>/<market>/ (the
     market tier) and /new-homes/<state>/ (the division tier) still get no suffix. */
  const onCommunity = /\/new-homes\/[^/]+\/[^/]+\/[^/]+\/[^/]+/.test(base.replace(/[?#].*$/, ""));
  if (!onCommunity) return base;
  const b = base.replace(/\/$/, "");
  if (/interest list|vip/.test(l)) return b + "#join-interest-list";
  if (/tour|appointment/.test(l)) return b + "/schedule-tour";
  return base;
}

/* ── manual/dynamic mode, and per-field user takeover ───────────────────────
   Mode is persisted on the block as `__echMode`. Only the explicit string
   "manual" turns Dynamic off, so a block that has never been touched lands on
   Dynamic — the values arrive already filled from Step 1, and Manual is the
   deliberate override, not the starting point. */
/**
 * Is this block filled from the feed right now?
 *
 * The MANUAL-ONLY check lives HERE rather than at the panel, on purpose. The panel
 * decides what to draw; this decides what the FILL does. Gating only the strip
 * would hide the toggle while the feed carried on overwriting the block — the
 * `__socOff` bug in reverse, and the reason a hero photo could be replaced by hand
 * and then silently reverted. A manual-only block is never dynamic, whatever
 * `__echMode` a saved document happens to carry.
 */
export const echDynOn = (m: DynBlock) => {
  if (m && echManualOnly(echNameOf(m.type))) return false;
  /* ALWAYS-DYNAMIC beats a saved `__echMode` for the same reason MANUAL-ONLY does:
     the panel can stop drawing the strip, but if the FILL still honoured an old
     "manual" the block would open looking live and never refresh. A job saved before
     2026-08-22 carries that value, so it has to be ignored here, not just hidden.
     Per-field edits are unaffected — those go through `socIsOff`, not the mode. */
  if (m && echAlwaysDyn(echNameOf(m.type))) return true;
  return String((m && m.data && m.data.__echMode) || "dynamic") !== "manual";
};

/**
 * One list per block of fields the USER has taken over. The feed skips these — it
 * still decides the value of everything else. Covers a social channel switched off
 * and a URL typed by hand while the block is still in Dynamic. Without it,
 * editing in Dynamic is pointless: the next fill writes straight over what you
 * typed. (monolith __socIsOff / __socSetOff)
 */
export function socIsOff(m: DynBlock, k: string): boolean {
  return String((m && m.data && m.data.__socOff) || "").split(",").indexOf(k) > -1;
}
/**
 * The new `__socOff` value, RETURNED rather than written — React owns block data
 * here, so the Inspector patches this through `onData` instead of mutating
 * `m.data` the way the monolith does. `socSetOff` below is the mutating form, kept
 * because it is what a non-React caller expects and because the two must never
 * disagree about the list format.
 */
export function socOffList(m: DynBlock, k: string, off: boolean): string {
  const l = String((m && m.data && m.data.__socOff) || "").split(",").filter(Boolean).filter((x) => x !== k);
  if (off) l.push(k);
  return l.join(",");
}
export function socSetOff(m: DynBlock, k: string, off: boolean): void {
  m.data.__socOff = socOffList(m, k, off);
}

/**
 * ── A HAND EDIT CLAIMS ITS FIELD, WHEREVER IT WAS MADE (2026-08-22) ─────────────
 *
 * Jack: "should still be able to edit the listing table directly in the email."
 * The panel already did this inline (Inspector's `fedEdit`): any key the feed
 * sources gets added to `__socOff`, and clearing that key hands it back. The
 * CANVAS had no such wrapper, which is why canvas editing of a fed block was
 * locked on 2026-08-20 — a typed value there was overwritten by the next fill.
 *
 * So the claim moves here, where BOTH callers can use it. One mechanism for "the
 * requester owns this value", which is what the panel's own note asked for: the
 * lock existed because the claim was missing, not because editing on the email is
 * wrong.
 *
 * Pure: returns the patch to write (with `__socOff` appended only when the list
 * actually moved), never mutates. Keys the feed does not source pass through
 * untouched — nothing to claim, nothing to record.
 */
/** a key whose EMPTY is a decision, not a hand-back: the ten listing rows and the
 *  two collection headers (see `echClaimPatch`) */
export function echRowOwnedKey(k: string): boolean {
  return /^home[0-9]+(Homesite|Plan|SqFt|Beds|Baths|Price|Url)$/.test(k)
    || /^collection[0-9]+Name$/.test(k);
}

/**
 * ── NO TWO ROWS SHOW THE SAME HOME (2026-08-22) ────────────────────────────────
 *
 * Jack: "can't delete homes, you keep re-adding them or something weird like
 * that." Measured in his tab: rows 3 AND 4 both read "189 / Montego". Deleting a
 * row re-packs the list, the fill then wrote the same home into the next
 * unclaimed slot, and the row came back one line lower — so a delete looked like
 * a move and a second delete looked like a duplicate.
 *
 * The de-dupe runs on the finished patch, walks the rows in order, and CLEARS any
 * row that repeats a homesite+plan already shown above it. The clear is claimed
 * (`echRowOwnedKey`) so the next pass does not refill it — without that the two
 * writers oscillate forever, which is the failure this codebase keeps catching.
 */
export function echDedupeRows(m: DynBlock, patch: Record<string, string>): void {
  const d = (m && m.data) || {};
  const val = (k: string): string => String((k in patch ? patch[k] : d[k]) ?? "").trim();
  const claim = String(d.__socOff || "").split(",").filter(Boolean);
  /* ── COUNT THE CLAIM, DON'T CLOSE OVER A FLAG (2026-08-23) ─────────────────
     The flag this replaced was assigned from inside a callback that runs once per
     group, which ESLint reports as `no-loop-func` — and CRA turns every warning into
     an error when CI=true, so that one line was failing the GitHub pipeline on every
     commit. The claim list already records whether anything changed. */
  const claimAt = claim.length;
  /* ── PER GROUP, NEVER ACROSS THEM (2026-08-22, caught by a risk probe before
     Jack hit it) ─────────────────────────────────────────────────────────────────
     Homesite numbers restart inside each collection, so "4 / HALLE" in the 50s and
     "4 / HALLE" in the 60s are two REAL homes — the table shows five rows per
     collection (1–5, then 6–10), and de-duping across that boundary would delete
     the second collection's home and claim the emptiness, which is worse than the
     duplicate this exists to stop. */
  const seenIn = (from: number, to: number) => {
    const seen: Record<string, number> = {};
    for (let n = from; n <= to; n++) {
      const lot = val("home" + n + "Homesite");
      /* THE HOMESITE NUMBER IS THE IDENTITY. Without one there is nothing to
         compare: several homesites legitimately share a plan name, and keying on
         the plan alone cleared a perfectly good positional row (caught by
         echFeatLevel's own test, where rows 1 and 3 are both "Granite"). */
      if (!lot) continue;
      const key = (lot + "|" + val("home" + n + "Plan")).toLowerCase();
      if (seen[key] === undefined) { seen[key] = n; continue; }
      ROW_FIELDS_ECH.forEach((sfx) => {
        const k = "home" + n + sfx;
        if (String(d[k] ?? "") !== "") patch[k] = ""; else delete patch[k];
        if (claim.indexOf(k) < 0) claim.push(k);
      });
    }
  };
  /* the group boundaries are the BLOCK's, same derivation the grouped writer uses —
     a five-header block is five buckets of two, not two buckets of five */
  const meta = echMeta(String(m.type || ""));
  const declared = (meta && meta.declared) || {};
  const nKeys = (re: RegExp) => Object.keys(declared).filter((k) => re.test(k)).length;
  const gCap = Math.max(1, nKeys(/^collection[0-9]+Name$/) || 2);
  const rCap = Math.max(1, nKeys(/^home[0-9]+Homesite$/) || 10);
  const gSpan = Math.max(1, Math.floor(rCap / gCap));
  for (let g = 0; g < gCap; g++) seenIn(g * gSpan + 1, Math.min(rCap, (g + 1) * gSpan));
  if (claim.length !== claimAt) patch.__socOff = claim.join(",");
}


/**
 * EVERY FIELD ANY LEVEL FEEDS ON THIS BLOCK (2026-10-27, Layne, Teams: "With no plan chosen,
 * typing in Price in the right panel shows nothing. Other fields type fine. Once I pick a plan
 * from inventory, Price becomes editable.") A new Featured card starts at the community level,
 * which does not feed Price; `echClaimPatch` only claimed fields the CURRENT level feeds, so the
 * typed price was never claimed, and `echFeatRestore` (which resets a field another level feeds
 * back to the template's value unless the requester claimed it) wiped it on the next fill —
 * every keystroke. A field any level feeds is now the requester's once they type in it.
 */
export function echFeatFieldUnion(name: string): Record<string, 1> {
  const out: Record<string, 1> = {};
  (Object.keys(ECH_FEAT) as EchFeat[]).forEach((l) => {
    const map = ECH_FEAT[l].fields[name];
    if (map) Object.keys(map).forEach((k) => { out[k] = 1; });
  });
  return out;
}

export function echClaimPatch(
  m: DynBlock,
  patch: Record<string, unknown>,
): Record<string, unknown> {
  const name = echNameOf(String((m && m.type) || ""));
  if (!name) return patch;
  const map = echDynFor(name, echFeatOf(m));
  if (!map) return patch;
  const union = echFeatFieldUnion(name);
  const list = String((m && m.data && m.data.__socOff) || "").split(",").filter(Boolean);
  const d = (m && m.data) || {};
  let moved = false;
  /* ── A CLAIM THE CALLER ALREADY MADE IS KEPT, NOT REBUILT OVER (2026-09-10, Jack:
     "when I remove a collection section the tool deletes the homes but not the
     collection name — I have to click twice") ─────────────────────────────────────
     `onDropColl` arrives with its own `__socOff` that claims the rows AND the
     `collection<n>Name` header. This function rebuilt the list from the BLOCK's
     claims plus the fed keys in the patch — and the header is deliberately not a fed
     key ("collection names stay manual"), so the caller's claim was dropped on the
     floor, the next fill pass put the surviving community's name back on the empty
     heading, and only a second click (a no-op patch that passes through untouched)
     kept it. Union the incoming claims first; the per-key logic below still applies. */
  String(patch.__socOff ?? "").split(",").filter(Boolean).forEach((k) => {
    if (list.indexOf(k) < 0) { list.push(k); moved = true; }
  });
  Object.keys(patch).forEach((k) => {
    if (!map[k] && !union[k]) return;                        /* fed at no level — nothing to claim */
    /* ── A NO-OP WRITE IS NOT AN EDIT (2026-08-22) ────────────────────────────
       A blur, a re-render, a spreadsheet import writing the same text — all of
       them arrive as a patch. Claiming on those quietly took fields away from the
       feed that nobody had touched, which is how a listing table ended up with
       every row claimed and its collection headers stuck on "Collection Name". */
    if (String(patch[k] ?? "") === String(d[k] ?? "")) return;
    const has = !!String(patch[k] ?? "").trim();
    const at = list.indexOf(k);
    if (has && at < 0) { list.push(k); moved = true; }
    /* ── AN EMPTIED ROW STAYS EMPTY (2026-08-22, Jack: "can't delete homes, you
       keep re-adding them") ────────────────────────────────────────────────────
       Clearing a fed field normally hands it BACK to the feed — right for a photo
       or a typed price, WRONG for a listing row somebody deleted: the next fill
       simply put the home back. So for the row fields and the collection headers
       an empty is a DECISION and keeps the claim; everything else round-trips as
       before. */
    else if (!has && at < 0 && echRowOwnedKey(k)) { list.push(k); moved = true; }
    else if (!has && at > -1 && !echRowOwnedKey(k)) { list.splice(at, 1); moved = true; }
  });
  return moved ? { ...patch, __socOff: list.join(",") } : patch;
}

/**
 * Write every sourced field on ONE block. Returns the patch (empty when nothing
 * changed) rather than mutating, because React owns block data here — the
 * monolith writes `m.data[k]` in place and calls esCanvas().
 */
export function echDynPatch(m: DynBlock, name: string, ctx: EchCtx, doc: DynBlock[]): Record<string, string> {
  /* THE BLOCK'S OWN FEATURE LEVEL DECIDES WHICH MAP THIS IS (2026-08-20). Read off
     `m.data.__featLevel`, so a refill after a Step-1 change keeps every Featured block
     at the level its requester chose, and two Featured blocks in one document can sit
     at two different levels. A block the level table doesn't cover gets `ECH_DYN`
     whatever this resolves to, so nothing else in the document notices. */
  const feat = echFeatOf(m);
  const map = echDynFor(name, feat);
  const patch: Record<string, string> = {};
  if (!map) return patch;
  /* a field another level sources and this one doesn't goes back to the template's own
     value rather than keeping the previous level's answer — see `echFeatRestore` */
  Object.assign(patch, echFeatRestore(m, name, feat));
  /* THE BLOCK'S OWN LEVEL, NOT THE SEND'S (2026-08-20) — see `echBlockLevel`. A block
     carrying an explicit wizard pick is at COMMUNITY level whatever Step 1 says,
     because the pick names a community. Identical to `echLevel(ctx.focus, …)` for
     every block without a pick, which is every block this did not already cover. */
  const lvl = echBlockLevel(m, ctx);
  const pairs = ECH_CTA_PAIRS[name] || {};
  /* ── NO PLANS IN SCOPE MEANS LEAVE THE CARD ALONE (2026-08-24) ────────────────────
     Widening the Featured floorplan fields past `community` (see `PLAN_LEVELS`) made a
     second state reachable: a scope that legitimately has NO floorplans — nothing loaded
     yet on a broad focus, or a masterplan whose collections carry none. Every `plan.*`
     source then answers "" and, because Dynamic is authoritative including when it
     resolves to nothing, the fill wrote those empties — which deleted the card's image
     cell (the empty-segment rule removes it) while the seed guard kept the text. That is
     the "blanked a real layout" failure this file warns about, so an unanswerable scope
     is treated exactly like a wrong level: skip the field, keep the template's content. */
  const planDry = echPlanDry(m, ctx, doc);
  const declaredKeys = echMeta(m.type)?.declared || {};
  /* ── A ROW WHOSE PICK NO PACK CAN SHOW KEEPS WHAT IT HAS (2026-09-22, #135/#139) ──────
     `echDynVal` answers "" for such a row (a key is a choice; nothing is invented), and
     writing that "" here would blank the very fields the Studio filled from the real
     row. So the field is skipped instead — the block keeps the values it carries, which
     is what the preview rail, the form's own fill pass and a reopened job all need when
     their ctx lacks the pack the pick was made from. Covers the Featured card's own home
     (`home.self.*`), each listing row (`home.<n>.*`) and a homes card's CTA pair, whose
     URL derives from the same home. */
  const packsNow = echPacks(ctx);
  const rowUnresolved = (n: number) => echHomePickUnresolved(packsNow, m, n);
  const keepUnresolved = (k: string): boolean => {
    const hm = /^home\.(self|[0-9]+)\./.exec(echSrcOf(map[k]).src);
    if (hm) return rowUnresolved(hm[1] === "self" ? 1 : Number(hm[1]));
    return !!pairs[k] && feat === "homes" && rowUnresolved(1);
  };
  for (const k of Object.keys(map)) {
    if (socIsOff(m, k)) continue;          /* the user took this field over */
    if (!echSrcOk(map[k], lvl)) continue;  /* wrong level for this field — leave it */
    if (keepUnresolved(k)) continue;       /* the pick names a home no pack can show */
    /* an OPTIONAL entry fills only on a block that declares the field — see DynSource.opt */
    if (echSrcOf(map[k]).opt && !(k in declaredKeys)) continue;
    if (planDry && /^plan\./.test(echSrcOf(map[k]).src)) {
      /* NEVER while a feed is in flight (audit 2026-08-24). `echPlanScope` is empty until
         the packs land, so without this the card visibly reverted to Layne's seed on
         every Studio reopen and stayed there if /sfmc-content failed. Every other write
         path in this file honours `ctx.pending`; this branch was the exception. */
      if (ctx.pending) continue;
      /* ── A DRY SCOPE MUST NOT KEEP THE LAST SCOPE'S ANSWER (2026-08-24) ───────────
         Skipping was right for a template SEED and wrong for a value an earlier fill
         wrote: switching Step 1 from three communities to an Area of Interest that
         cannot answer left the previous communities' floorplans on the card, which is
         the stale-fill bug this file already fixed one axis over (`echFeatRestore`).
         So the field goes back to the block's DECLARED DEFAULT — but only when the last
         fill is what put the current value there, never over the requester's own words. */
      const fedNow: Record<string, string> = (() => {
        try { return JSON.parse(String(m.data.__socFed || "{}")); } catch { return {}; }
      })();
      const cur = String(m.data[k] == null ? "" : m.data[k]);
      if (cur && fedNow[k] !== undefined && String(fedNow[k]) === cur) {
        const def = String((echMeta(m.type)?.declared || {})[k]?.default ?? "");
        if (cur !== def) patch[k] = def;
      }
      continue;
    }
    /* the Featured COMMUNITY card's three fact lines write as a GROUP below —
       all or nothing, so a pack that can't answer the from-price never leaves a
       city name over two em-dashes on a layout the guardrail locks */
    if (feat === "comms" && COMMS_LINE_KEYS.has(k)) continue;
    let v: string;
    if (pairs[k]) {
      /* the CTA's own label decides the destination — straight from the Fast Track deck */
      v = ctaUrlFor(m.data[pairs[k]], lvl, ctx, doc, m);
    } else {
      v = echDynVal(echSrcOf(map[k]).src, lvl, ctx, doc, m);
    }
    /* DYNAMIC IS AUTHORITATIVE, INCLUDING WHEN IT RESOLVES TO NOTHING. Writing only
       truthy values left the previous focus's answer on the canvas: switch Step 1
       from Market to Community and the panel correctly said "pick a community"
       while the email still read "Atlanta". An empty header is honest and forces
       the pick; a stale one ships the wrong city.
       …but never clear on a PENDING feed: with Dynamic as the default, a fresh
       panel would blank real copy for the second it takes the pack to arrive. */
    if (!v && ctx.pending) continue;
    /* ── AN EMPTY PHOTO DOES NOT ERASE THE BLOCK'S OWN ART (2026-08-29) ─────────
       Jack, on a Now Selling build: "this also stems from missing images … there
       should be the place holder image instead of no image like this." Measured on
       the live Studio: the first Featured card rendered as white space while the
       Inspector beside it showed the "🏠 Images coming soon" placeholder — the two
       renderings disagreeing again, and the canvas being the one that lies.

       The cause is the rule directly above. Authoritative-empty is right for TEXT:
       a stale "Atlanta" over a community send is worse than a blank line, and a
       blank forces the pick. It is wrong for an IMAGE, because the block does not
       ship an empty `src` — it ships Layne's placeholder art, and the renderer has
       no hole to fall back to. Blanking it deletes a picture and leaves nothing.

       So an image field that resolves to nothing keeps what the block already
       carries. Nothing is invented and nothing goes stale: the feed has no photo
       for this plan (94.7% of ACTIVE plans do have one — the blanks are an
       unpopulated Airtable field, see AUDIT-PLAN-IMAGES-20260828), so the honest
       answer is the block's own art plus the required-before-send gate, which
       already lists a blank image and makes the requester choose one. */
    if (!v && String(((echMeta(m.type)?.declared || {})[k] as { type?: string } | undefined)?.type || "") === "image") continue;
    /* ── THE FILL CAN NEVER WRITE A GUARDRAIL VIOLATION (2026-08-25) ────────────────
       Two division reports in two days: "giving an error or Alt Text character limit,
       but there is no area to update" and "it is prompting me that the alt text is too
       long but it is not clear where to edit the alt text." The blocking card was
       correct — the value really was over the cap — and the requester genuinely could
       not act on it, because the field is written by the FILL, not by them.
       Clamping the derived Bynder alt (2026-08-24) fixed one source; this closes the
       class. Every value the fill writes is now trimmed to the field's own declared cap,
       so the guardrail card can only ever be about something the requester typed. */
    if (v) {
      const rule = (echMeta(m.type)?.declared || {})[k];
      /* TEXT ONLY, AND ONLY ON AN EXPLICIT `max` (2026-08-25, second pass). Two ways to
         get this wrong, both worse than the warning it removes:
           · a URL is not prose. Truncating one ships a dead link, and `capOf` will
             happily derive a cap for a url/image/tel field from `lines`×`cpl`.
           · a DERIVED cap is a layout hint, not a hard limit — clamping to it would
             silently shorten copy Layne intended to wrap.
         So the clamp fires only where the block declares a real character `max` on a
         text field, which is exactly the case the divisions reported (alt text).
         HONEST SCOPE (corrected after an audit): this does NOT close every route to an
         unactionable card. `violations()` also flags a cap DERIVED from `lines`×`cpl`,
         and three fill-written fields carry one — ECH_GetDirections.Community (2×60) and
         both Featured PlanName (2×33). Those are hard to breach in practice and
         shortening them would silently cut copy Layne meant to wrap, so they are left
         alone deliberately rather than clamped. */
      const ty = String((rule && (rule as { type?: string }).type) || "").toLowerCase();
      const hardMax = rule && typeof (rule as { max?: number }).max === "number" ? Number((rule as { max?: number }).max) : 0;
      const textish = !/^(url|image|img|tel|phone|number|num|email|date)$/.test(ty);
      if (hardMax > 0 && textish && v.length > hardMax) v = v.slice(0, hardMax);
    }
    /* A SEED NEVER OVERWRITES REAL WORDS — but it does own ITS OWN words.
       "Untouched" = empty, still the block's SHIPPED default, or still exactly
       what the LAST FILL wrote (recorded in `__socFed`, a __-prefixed key so the
       manifest never carries it and a duplicate's restored copy keeps the old
       protection). Without that third case, a Market-focus fill wrote
       "Phoenix, AZ" into the locator and a switch to Division focus could never
       clear it — the seed guard read the feed's own words as the requester's
       (found live, 2026-08-21). Hand edits still win twice over: they flip
       `__socOff` (skipped at the top of this loop) AND they differ from `fed`. */
    if (echIsSeed(map[k])) {
      const cur = String(m.data[k] == null ? "" : m.data[k]).trim();
      const shipped = String(echMeta(m.type)?.declared[k]?.default ?? "").trim();
      const fedMap = ((): Record<string, string> => {
        try { return JSON.parse(String(m.data.__socFed || "{}")); } catch { return {}; }
      })();
      const fed = String(fedMap[k] ?? "").trim();
      if (cur && cur !== shipped && cur !== fed) continue;
      if (m.data[k] !== v) {
        patch[k] = v;
        /* remember what the feed wrote so the NEXT fill still owns it */
        fedMap[k] = v;
        patch.__socFed = JSON.stringify(fedMap);
      }
      continue;
    }
    if (m.data[k] !== v) patch[k] = v;
  }
  /* ── SEND-TIME LISTING (Jack, 2026-08-21: "yes to updating send time and
     handles sold") — remember each filled row's FULL LOT ID. The export swaps
     baked prices for a send-time lookup (echSendTime.ts) and `lot_id` is the key
     `ent.Sitecore Homesites` resolves by. Captured here, by the same fill that
     writes the rows, under a __ key: the manifest drops it (a duplicate rebuilds
     baked-only, the safe posture) and it never renders (no token reads it). */
  if (map.home1Homesite && echSrcOk(map.home1Homesite, lvl) && !ctx.pending && feat === "homes") {
    const lots: Record<string, string> = {};
    const lotsWas: Record<string, string> = (() => { try { return JSON.parse(String(m.data.__lots || "{}")) || {}; } catch { return {}; } })();
    for (let n = 1; n <= 10; n++) {
      /* a row whose pick no pack can show keeps the lot the Studio recorded (#135/#139) */
      if (rowUnresolved(n)) { if (lotsWas[String(n)]) lots[String(n)] = lotsWas[String(n)]; continue; }
      const lot = echDynVal("home." + n + ".lot", lvl, ctx, doc, m);
      if (lot) lots[String(n)] = lot;
    }
    const j = JSON.stringify(lots);
    if (String(m.data.__lots || "") !== j && (Object.keys(lots).length || m.data.__lots)) patch.__lots = j;
  }
  /* ── THE COLLECTION HEADERS NAME THEMSELVES BY SCOPE (Jack, 2026-08-21) ───────
     "the collections aren't being named… there should be one collection per
     community and since there's one community it should just be one collection."
     Three cases, none touching a block with an explicit wizard pick (the pick
     list owns the rows there):
       · SEVERAL COMMUNITIES ("if there's multiple then select multiple") — the
         per-field path above is community-gated and `echLevel` deliberately reads
         a multi-pick as "market", so the grouped fill writes the rows: group A →
         rows 1–5, group B → rows 6–10, headers = community names, the two picks
         with the most available homes leading.
       · A MASTERPLAN whose homes live on its COLLECTIONS — same grouped fill,
         headers = the collection names (the positional path reads the
         masterplan's own pack, which is typically empty).
       · ONE plain community — the positional path already fills rows 1–10; this
         only names the single collection after the community and blanks the
         second header (whose row the renderer then drops entirely). */
  /* ── THE FEATURED COMMUNITY CARD'S FACT LINES, ALL OR NOTHING (2026-08-21) ──
     city/state, from-price, available-home count replace the sample specs (Jack:
     "should adapt to each level"; the content is his approved AOI-mockup card).
     Written only when the pack can answer ALL THREE — a partial fill on a spec
     layout the block's guardrail locks reads as a broken card, so a pack with no
     prices leaves the template's shipped values standing instead. The unit labels
     clear with the facts and echFeatRestore puts them back on a level switch. */
  if (feat === "comms" && map.SqFtLabel !== undefined && lvl === "community" && !ctx.pending) {
    const city = echDynVal("community.citystate", lvl, ctx, doc, m);
    const from = echDynVal("community.fromprice", lvl, ctx, doc, m);
    const count = echDynVal("community.availcount", lvl, ctx, doc, m);
    if (city && from && count) {
      const set = (k: string, v2: string) => {
        if (socIsOff(m, k)) return;
        if (String(m.data[k] ?? "") !== v2) patch[k] = v2; else delete patch[k];
      };
      set("SqFt", city); set("SqFtLabel", "");
      set("Beds", from); set("BedsLabel", "");
      set("Baths", count); set("BathsLabel", "");
    }
  }
  /* the table at FLOORPLANS level carries no lots — a stale send-time key from a
     homes fill must not reprice plan rows */
  if (map.home1Homesite && feat !== "homes" && String(m.data.__lots || "").trim() && !ctx.pending) {
    patch.__lots = "";
  }
  /* the FLOORPLANS table names its community header and keeps the 5-row serving
     default, same as the homes table (the plan rows themselves fill positionally
     through the per-field loop above) */
  /* AN AOI REQUEST HAS GROUPS INSTEAD OF ONE PACK (2026-08-22) — same reasoning as
     the homes gate above: `lvl === "community"` was a stand-in for "we resolved which
     communities this email is about", and an Area of Interest resolves that too. The
     comms branch below reads its rows straight off collPacks, so plans at AOI level
     fall through to it rather than duplicating the loop. */
  /* ── THE TABLE AT FLOORPLANS LEVEL, FROM AN EXPLICIT PICK (2026-08-28) ──────────
     Jack: "floor plans aren't showing nor saving when check marking them." Both
     halves were true and they were one hole: `__planPick` held ONE plan (the wizard
     threw the rest away), and nothing read a plan pick for ROWS — only `plan.self`,
     which a table never asks for. So Done at Floorplans level wrote a collection
     heading and ten empty rows, which is exactly "0 of 10 shown".

     The rows are the picks, in the order they were chosen, grouped by community the
     same way the home rows are — one heading per community, sections cut where the
     groups actually end. Column 1 carries the PLAN NUMBER, which is what the block's
     own "Homesite / Plan" header promises at this level and what `LST_PLANS` already
     declared; the price is the plan's From price; the link is the plan's page.

     A plan-only row is a real row — `dropEmptyHomeRows` tests Homesite OR Plan — so
     nothing here needs the block to change. */
  /* ── WHO OWNS THE TABLE'S SHAPE THIS PASS (2026-08-31) ───────────────────────
     Lauren, via Teams: "a really bizarre glitch when I attempt to switch up homes in
     the listing table. After I select options, and return to view the email. The
     table and contact us form start flashing on and off... the default generated
     table doesn't do this. It's only if I go to change it."

     Flashing is a fill that never settles, and this was a two-writer flip-flop
     measured over six passes with identical inputs every time (names=2, fair=[5,5]):

       pass A  the pick branch cuts to its own section sizes -> __html = 10 rows
       pass B  the pick branch re-cuts, finds the markup ALREADY right, and says so
               by DELETING patch.__html — which is byte-identical to "no branch cut
               this pass". The tail then re-cuts with `want` and NO sizes, producing
               the default even shape, which differs -> __html = 9 rows
       pass C  the pick branch sees the wrong shape again and re-cuts -> 10 rows ...

     So the two cutters handshook through a key that disappears exactly when the
     size-aware writer succeeds. The shape's owner is now recorded explicitly: a
     branch that knows its own section sizes claims the pass, whether or not that
     produced a patch, and the tail only cuts when nobody claimed it. */
  let shapeOwned = false;
  if (map.home1Homesite && feat === "plans" && !ctx.pending && String(m.data[ECH_PLAN_PICK] || "").trim()) {
    const ps = echPacks(ctx);
    const names: string[] = [];
    const sizes: number[] = [];
    const grpPlans: { p: PlanInfo; code: string }[][] = [];
    for (let n = 1; n <= 10; n++) {
      const hit = echPlanIn(ps, m, n);
      if (!hit) continue;
      const pk = ps.find((x) => x.code === hit.code);
      const nm = echParentCommunity(echClean(pk ? (pk.name || pk.C.community?.name || "") : ""));
      if (!nm) continue;
      const at = names.indexOf(nm);
      if (at < 0) { names.push(nm); sizes.push(1); grpPlans.push([hit]); }
      else { sizes[at] += 1; grpPlans[at].push(hit); }
    }
    if (names.length) {
      /* ── A ROW THAT CHANGES PLAN DROPS THE EDITS MADE TO THE OLD ONE (2026-09-15) ──
         Driven on dev: the requester typed "From $369,990" over Conway's price in row 1,
         then a pick put Edison in row 1 — and Edison shipped with Conway's typed price,
         because the claim is on the ROW KEY, not on the plan. A claim protects the
         requester's words about THIS plan against the feed; it is not a reason to pin
         them under a different plan. So when the plan a row is about to carry differs
         from the plan it carried, that row's claims are released before the write.
         Rows whose plan stays keep every claim exactly as before. */
      const off = String(m.data.__socOff || "").split(",").filter(Boolean);
      const offWas = off.join(",");
      const releaseRow = (n: number) => {
        ROW_FIELDS_ECH.forEach((sfx) => { const i = off.indexOf("home" + n + sfx); if (i > -1) off.splice(i, 1); });
      };
      const set = (k: string, v: string) => {
        if (off.indexOf(k) > -1) return;
        if (String(m.data[k] ?? "") !== v) patch[k] = v; else delete patch[k];
      };
      let row = 0;
      grpPlans.forEach((grp) => {
        grp.forEach((cell) => {
          row += 1;
          if (row > 10) return;
          const pl = cell.p;
          const wasPlan = String(m.data["home" + row + "Plan"] ?? "").trim().toLowerCase();
          const nowPlan = echPlanName(pl.name).trim().toLowerCase();
          if (wasPlan && nowPlan && wasPlan !== nowPlan) releaseRow(row);
          set("home" + row + "Homesite", String(pl.num == null ? "" : pl.num).trim());
          set("home" + row + "Plan", echPlanName(pl.name));
          set("home" + row + "SqFt", echSqft(pl.sqft));
          set("home" + row + "Beds", echBeds(pl.beds));
          set("home" + row + "Baths", echBaths(pl.baths));
          set("home" + row + "Price", pl.price ? "From " + echMoney(pl.price) : "");
          set("home" + row + "Url", String(pl.url || ""));
        });
      });
      for (let n = row + 1; n <= 10; n++) ROW_FIELDS_ECH.forEach((sfx) => set("home" + n + sfx, ""));
      if (off.join(",") !== offWas) patch.__socOff = off.join(",");
      shapeOwned = true;
      if (String(m.data[ECH_ROW_SPLIT] || "")) patch[ECH_ROW_SPLIT] = "";   /* the picks own the shape now */
      echCutListing(m, patch, names.length, sizes);
      const declaredHeads = Object.keys(map).filter((k) => /^collection[0-9]+Name$/.test(k)).length;
      const upTo = Math.max(names.length, declaredHeads, 2);
      for (let g = 1; g <= upTo; g++) set("collection" + g + "Name", names[g - 1] || "");
      /* a plan row has no lot, and a stale lot map would reprice it at send time */
      if (String(m.data.__lots ?? "").trim()) patch.__lots = "";
    }
  }
  if (map.home1Homesite && feat === "plans" && lvl === "community" && !ctx.pending
      && ctx.pack && (ctx.pack.plans || []).length
      && !String(m.data[ECH_PLAN_PICK] || "").trim()
      && !String(m.data[ECH_HOME_PICK] || "").trim()) {
    const I = echCommInfo(ctx);
    const set = (k: string, v: string) => {
      if (socIsOff(m, k)) return;
      if (String(m.data[k] ?? "") !== v) patch[k] = v; else delete patch[k];
    };
    set("collection1Name", echCollLabel(echClean((I && I.name) || (ctx.picks[0] && ctx.picks[0].name) || "")));
    set("collection2Name", "");
    for (let n = 6; n <= 10; n++) ROW_FIELDS_ECH.forEach((sfx) => set("home" + n + sfx, ""));
  }
  /* ── THE TABLE AT COMMUNITY LEVEL: ONE ROW PER COMMUNITY (Jack, 2026-08-21:
     "with both the homesite listing and the feature plan should be able to include
     the community or floor plan level as well … should adapt to each level").
     Rows come from Step 1's own communities — several picks (or a masterplan's
     collections) via `collPacks`, a sole community via `pack` — and each row reads
     as a comparison line: the flipped community name in the plan slot, the sqft /
     beds / baths RANGES across its floorplans (homesites when a pack carries no
     plans), the same "From $X" the Featured community card shows, and the
     community page as the View link. All-or-nothing per row: a pack with no
     inventory contributes no row rather than a half-empty one. */
  /* ── THE OLD COMMUNITY ROW IS NOW A FALLBACK ONLY ─────────────────────────────
     Runs only when the grouped pass above wrote nothing — a community with no
     homesites on file, where there are no rows to group. It still produces the
     leading-slash cell, which is why it is last resort rather than the default. */
  if (map.home1Homesite && feat === "comms" && !ctx.pending
      && !Object.keys(patch).some((k) => /^collection[0-9]+Name$/.test(k) && String(patch[k] || "").trim())) {
    const packsIn = (ctx.collPacks || []).filter((p) => p && p.C && ((p.C.plans || []).length || (p.C.homesites || []).length));
    const rowsFrom = packsIn.length ? packsIn.map((p) => p.C)
      : (ctx.pack && ((ctx.pack.plans || []).length || (ctx.pack.homesites || []).length)) ? [ctx.pack] : [];
    if (rowsFrom.length) {
      const set = (k: string, v: string) => {
        if (socIsOff(m, k)) return;
        if (String(m.data[k] ?? "") !== v) patch[k] = v; else delete patch[k];
      };
      const range = (nums: number[], fmt: (n: unknown) => string): string => {
        const xs = nums.filter((n) => Number.isFinite(n) && n > 0);
        if (!xs.length) return "";
        const lo = Math.min(...xs), hi = Math.max(...xs);
        return lo === hi ? fmt(lo) : fmt(lo) + "\u2013" + fmt(hi);
      };
      let row = 0;
      for (const C of rowsFrom.slice(0, 10)) {
        const specs: { sqft?: unknown; beds?: unknown; baths?: unknown }[] =
          (C.plans || []).length ? (C.plans || []) : (C.homesites || []);
        const prices = ([] as number[]).concat(
          (C.homesites || []).map((h) => Number((h as { price?: unknown }).price)),
          (C.plans || []).map((pl) => Number((pl as { price?: unknown }).price)),
        ).filter((n) => Number.isFinite(n) && n > 0);
        const sq = range(specs.map((x) => Number(x.sqft)), echSqft);
        const bd = range(specs.map((x) => Number(x.beds)), echBeds);
        const ba = range(specs.map((x) => Number(x.baths)), echBaths);
        const url = String((C.community && (C.community as { url?: string }).url) || "");
        if (!sq || !bd || !ba || !prices.length) continue; /* no half rows */
        row += 1;
        set("home" + row + "Homesite", "");
        set("home" + row + "Plan", echCollLabel(echClean((C.community && C.community.name) || "")));
        set("home" + row + "SqFt", sq);
        set("home" + row + "Beds", bd);
        set("home" + row + "Baths", ba);
        set("home" + row + "Price", "From " + echMoney(Math.min(...prices)));
        set("home" + row + "Url", url);
      }
      if (row) {
        for (let n = row + 1; n <= 10; n++) ROW_FIELDS_ECH.forEach((sfx) => set("home" + n + sfx, ""));
        /* per-community names live in the rows — the group headers say nothing here */
        set("collection1Name", "");
        set("collection2Name", "");
      }
    }
  }
  /* ── A PICKED TABLE STILL NEEDS ITS HEADERS NAMED (2026-08-22) ────────────────
     Jack, on the live build: "you're still not showing the collection names." With
     an explicit home pick the pick list owns the ROWS — by design, so choosing five
     homes is not undone by the feed — but nothing then named the two collection
     headers, so they kept Layne's seed and the email read "Collection Name" twice
     over real homes. The headers come from the PICKED homes' own communities: the
     first community named heads group 1, a second one heads group 2, and a pick
     inside one community clears the second header (the renderer drops its row). */
  /* ── AND A SECTION PER COMMUNITY, SIZED TO ITS OWN ROWS (2026-08-22) ──────────
     Jack picked ten homes across five communities and got two sections whose rows
     belonged to four different places — Cove at Southwood homes under "Avalon
     Woods", Emerald Isles and Silver Falls homes under "Cove at Southwood". Naming
     only the first two headers was the whole of the old fix, which is why the third
     community onward had nowhere to go.
     The pick list is now stored GROUPED by community (`homePickList`), so the rows
     are already contiguous per community. What remains is to give each community its
     OWN heading and to cut the section boundaries where the groups actually end —
     3/2/2/2/1, not an even 2/2/2/2/2 — which is what `sizes` does. */
  /* ── …AND ONLY WHEN EVERY PICKED HOME IS IN HAND (2026-09-22, #135/#139) ─────────
     This writer re-seats the picks it can resolve and BLANKS every other slot. With a
     ctx that cannot see the pack a pick was made from (the form's rail, a reopened job),
     an unresolved pick is not "no pick" — its row would be wiped and the table re-cut
     around the survivors. The rows the Studio wrote stand until the packs can answer. */
  const pickRowsN = String(m.data[ECH_HOME_PICK] || "").split("\n").map((x) => x.trim()).filter(Boolean).length;
  const anyPickUnresolved = feat === "homes" && pickRowsN > 0
    && Array.from({ length: Math.min(pickRowsN, 10) }, (_, i) => i + 1).some((n) => rowUnresolved(n));
  if (map.home1Homesite && feat === "homes" && !ctx.pending && String(m.data[ECH_HOME_PICK] || "").trim() && !anyPickUnresolved) {
    const ps = echPacks(ctx);
    const names: string[] = [];
    const sizes: number[] = [];
    /* the picked homes, grouped, in the order the pick list already carries them */
    const grpHomes: { h: HomesiteInfo & { lot?: string }; code: string }[][] = [];
    for (let n = 1; n <= 10; n++) {
      const hit = echHomeIn(ps, m, n);
      if (!hit) continue;
      const p = ps.find((x) => x.code === hit.code);
      /* the HEADING is the parent community — a masterplan's 50s and 60s are one
         community to a reader, and two headings for one place reads as a bug */
      const nm = echParentCommunity(echClean(p ? (p.name || p.C.community?.name || "") : ""));
      if (!nm) continue;
      const at = names.indexOf(nm);
      if (at < 0) { names.push(nm); sizes.push(1); grpHomes.push([hit]); }
      else { sizes[at] += 1; grpHomes[at].push(hit); }
    }
    if (names.length) {
      const set = (k: string, v: string) => {
        if (socIsOff(m, k)) return;
        if (String(m.data[k] ?? "") !== v) patch[k] = v; else delete patch[k];
      };
      /* ── A MULTI-COMMUNITY PICK OWNS THE WHOLE TABLE (2026-08-22) ─────────────
         Found by driving the real panel, not by a test: three picks across three
         communities came back "Emerald Isles Townhomes 4 of 8" — one picked home
         plus three the FEED had put in the rows past the pick list. That fallback
         ("a two-home pick still leaves rows 3–10 showing real inventory") is right
         for a single community and wrong the moment the sections are named, because
         every trailing feed row lands under the LAST community's heading, which is
         the exact mislabel this whole change is about.
         So: picks spanning MORE THAN ONE community clear the rows past the picks —
         the requester said which homes, and a heading over a home he did not choose
         is worse than a shorter table. A single-community pick is untouched. */
      const picked = sizes.reduce((a, b) => a + b, 0);
      /* ── AND EVERY SECTION GETS A FAIR SHARE OF THE SPARE ROWS (2026-08-22) ───
         Jack, on the mockup: "ok fine with it." Cutting the sections exactly at the
         picks (1/1/1 of ten rows) left the seven spares attached to the LAST
         section, so its card read "1 of 8 · 7 open" while the first two read "1 of 1
         · Full" — and "+ Add a home" only appeared on the last one, making a fourth
         home in Avalon Woods a dead end. The email never showed the difference
         (empty rows are dropped), so this is a panel-capacity bug, not a render one.
         So the ten rows are dealt out evenly — earlier sections take the remainder,
         the same convention `echExpandListingHtml` already uses — and each section's
         picks sit at the TOP of its own span with its spare slots beneath them. */
      const G = names.length;
      const spans: number[][] = [];
      if (G) {
        const base = Math.floor(10 / G), rem = 10 - base * G;
        for (let g = 0, at = 1; g < G; g++) {
          const take = Math.max(sizes[g], base + (g < rem ? 1 : 0));
          spans.push(Array.from({ length: take }, (_, i) => at + i).filter((n) => n <= 10));
          at += take;
        }
      }
      const fair = spans.map((s) => s.length);
      const fits = fair.reduce((a, b) => a + b, 0) <= 10 && fair.every((n, i) => n >= sizes[i]);
      const useFair = G > 1 && picked < 10 && fits;
      if (useFair) {
        /* re-seat every pick at the head of its own span, and blank the spares */
        const lots: Record<string, string> = {};
        spans.forEach((span, g) => {
          span.forEach((n, i) => {
            const cell = grpHomes[g][i];
            const row = cell ? cell.h : undefined;
            set("home" + n + "Homesite", row ? echShortLot(row.lot, cell.code) : "");
            set("home" + n + "Plan", row ? String(row.plan || "") : "");
            set("home" + n + "SqFt", row ? echSqft(row.sqft) : "");
            set("home" + n + "Beds", row ? echBeds(row.beds) : "");
            set("home" + n + "Baths", row ? echBaths(row.baths) : "");
            set("home" + n + "Price", row ? echMoney(row.price) : "");
            set("home" + n + "Url", row ? String(row.url || "") : "");
            if (row && row.lot) lots[String(n)] = String(row.lot);
          });
        });
        const j = JSON.stringify(lots);
        if (String(m.data.__lots || "") !== j) patch.__lots = j; else delete patch.__lots;
      } else if (G > 1 && picked < 10) {
        /* the fair deal did not fit (a group with more picks than its share) — the
           picks still own the table, so the tail clears rather than showing feed rows */
        for (let n = picked + 1; n <= 10; n++) ROW_FIELDS_ECH.forEach((sfx) => set("home" + n + sfx, ""));
      }
      /* re-cut from the PRISTINE markup, never from last pass's cut (2026-08-28) */
      shapeOwned = true;
      if (String(m.data[ECH_ROW_SPLIT] || "")) patch[ECH_ROW_SPLIT] = "";   /* the picks own the shape now */
      echCutListing(m, patch, names.length, useFair ? fair : sizes);
      /* every group named, and every heading the block still declares beyond them
         cleared — a leftover "Collection Name" over nothing is the tell that the
         re-cut and the naming disagreed */
      const declaredHeads = Object.keys(map).filter((k) => /^collection[0-9]+Name$/.test(k)).length;
      const upTo = Math.max(names.length, declaredHeads, 2);
      for (let g = 1; g <= upTo; g++) set("collection" + g + "Name", names[g - 1] || "");
    }
  }
  /* ── COMMUNITY LEVEL USES THE GROUPED SHAPE (2026-08-28, Jack) ────────────────
     The gate was `feat === "homes"`, so at community level the fill fell to the
     one-row-per-community branch below, which blanks the Homesite half and puts the
     community in the Plan half — rendering "` / Diamond Springs`", because the block
     hardcodes `{{home<n>Homesite}} / {{home<n>Plan}}`.

     The grouped writer already produces the right thing and needs no new logic: it
     names each group with the COMMUNITY (`collection<n>Name`, a full-width header row
     with no separator in it) and fills the rows beneath from that community's own
     homesites, sized off however many headers and rows the block declares. It was
     built for Jack's "one per community" ask on 2026-08-22 and was simply never
     reachable from this level. So no change to Layne's block is needed — the header
     row she already ships is the answer. */
  /* ── A HOME PICK OWNS THE ROWS ONLY AT ITS OWN LEVEL (2026-08-28) ─────────────
     This gate used to be `!__homePick` — the pick EXISTS — which meant a retained
     homesite pick silently disabled the community fill and the table came back
     empty at comms level. That is why #70's level change deleted the picks, and
     deleting them is what broke "if i toggle back they should show again" (#69).
     The honest test is whether the pick applies HERE: at homes level it owns the
     rows (the branch above fills from it), at any other level it is simply the
     selection that level will restore when the requester returns to it. */
  const homePickHere = feat === "homes" && !!String(m.data[ECH_HOME_PICK] || "").trim();
  if (map.home1Homesite && (feat === "homes" || feat === "comms") && !ctx.pending && !homePickHere) {
    const multi = lvl !== "community" && (ctx.picks || []).length > 1;
    const collGroups = (ctx.collPacks || []).filter((p) => p && p.C && (p.C.homesites || []).length);
    /* ── RESOLVED GROUPS FILL AT ANY LEVEL (2026-08-22) ────────────────────────
       This was `lvl === "community" && collGroups.length`, which was true while the
       only thing that produced groups was a masterplan (community level) or several
       picks. An AREA OF INTEREST produces them too now (`echCommsInAoi` → collPacks,
       off refdata schema 8) and its level is "aoi", so the gate refused the fill and
       an AOI send shipped the template's placeholder rows — "Collection Name / 101 /
       Plan Name" — which is exactly what driving Green Cove Springs showed.
       The honest rule is the one the data states: if groups with inventory were
       resolved, fill from them. A market/division send resolves none, so it is
       untouched. */
    if (multi || collGroups.length) {
      /* ── THE GROUPED WRITER OWNS THE SHAPE IT CUTS (2026-10-15, Paige Roemmich, COS) ──
         It re-cuts the table to its own group count, and when the markup is ALREADY
         that shape it says so by deleting patch.__html — the exact signal the tail reads
         as "nobody cut this pass". The tail then cut to its count of NAMED headings,
         which differs as soon as a requester removes a section (the × blanks and claims
         its heading): 2 sections, then 1, then 2 … at render speed, and the page stopped
         taking clicks. Same two-writer handshake #182 fixed for the pick branch, so the
         same contract: a branch that cut the shape claims the pass. */
      if (echGroupedListingPatch(m, ctx, patch)) shapeOwned = true;
    } else if (lvl === "community" && ctx.pack && (ctx.pack.homesites || []).length) {
      const I = echCommInfo(ctx);
      const set = (k: string, v: string) => {
        if (socIsOff(m, k)) return;
        if (String(m.data[k] ?? "") !== v) patch[k] = v; else delete patch[k];
      };
      /* the sole community's own name flips masterplan-first too — "24ft
         Townhomes- Shearwater" heads the email as "Shearwater - 24ft Townhomes" */
      set("collection1Name", echCollLabel(echClean((I && I.name) || (ctx.picks[0] && ctx.picks[0].name) || "")));
      set("collection2Name", "");
      /* 5 per community (Jack, 2026-08-21) — the positional loop above filled all
         ten slots; rows 6–10 clear, and the send-time lots re-key to the five kept */
      for (let n = 6; n <= 10; n++) ROW_FIELDS_ECH.forEach((sfx) => set("home" + n + sfx, ""));
      const lots5: Record<string, string> = {};
      for (let n = 1; n <= 5; n++) {
        const lot = echDynVal("home." + n + ".lot", lvl, ctx, doc, m);
        if (lot) lots5[String(n)] = lot;
      }
      const j5 = JSON.stringify(lots5);
      if (String(m.data.__lots || "") !== j5 && (Object.keys(lots5).length || m.data.__lots)) patch.__lots = j5;
      else if (patch.__lots !== undefined) delete patch.__lots;
    }
  }
  /* the LAST word on the rows: nothing this pass wrote may repeat a home already
     shown above it (2026-08-22 — see `echDedupeRows`) */
  if (map.home1Homesite) echDedupeRows(m, patch);
  /* ── THE TABLE SHRINKS BACK TOO (2026-08-22) ──────────────────────────────────
     Jack: the markup kept sections a later fill no longer needed. The re-cut only
     ever ran on the paths that GROW it, so a five-community pick left five section
     rows in `__html` and a later two-community fill — or a switch to Floorplans,
     which names no sections at all — left three of them empty. Nothing rendered
     them (the canvas drops an unnamed heading, the panel hides it), but they were
     still in the html anyone else reads.
     So the last word on the SHAPE is the same as the last word on the rows: however
     many collection names this block ends the pass with, that is how many sections
     its markup carries. One name (or none) means one section. */
  if (map.home1Homesite) {
    const nameAt = (n: number) => String(
      patch["collection" + n + "Name"] !== undefined
        ? patch["collection" + n + "Name"]
        : (m.data["collection" + n + "Name"] ?? ""),
    ).trim();
    let named = 0;
    for (let n = 1; n <= 10; n++) if (nameAt(n)) named = n;
    const want = Math.max(1, named);
    /* ── AND IT GROWS BACK (2026-08-28) ────────────────────────────────────────
       This used to be `if (heads > want)` — shrink-only — over the markup as it
       already stood. That is what made a level round-trip terminal: a Homesites
       pick cut the rows out, then Community level asked for its five comparison
       rows in markup that had three, and the canvas came back a header row and
       nothing. Re-cutting from the pristine copy means the shape follows the
       fill in BOTH directions. Only when nothing else cut this pass — a branch
       that knows its own section sizes has already said so. */
    /* a split the requester asked for stands, as long as it still names every
       section (2026-09-15, see ECH_ROW_SPLIT); anything else is the even deal */
    const split = echRowSplit(m, patch);
    if (!shapeOwned && patch.__html === undefined) echCutListing(m, patch, want, split && split.length === want ? split : undefined);
    /* ── "+ ADD A HOME" GROWS THE TABLE, IT DOES NOT GET TRIMMED (2026-08-28) ───
       A pick-driven cut sizes the markup to the PICKS, and a row typed by hand is
       not a pick — so a requester who used "+ Add a home" in a section, or typed
       into an open slot, would have had that row silently deleted by the trim
       below. The markup is still the single owner; it is just told about the row.
       Claimed rows only: a feed leftover past the pick has no business growing the
       table, which is the whole point of the cut. */
    echRowsForOwned(m, patch);
    /* the last word on the rows: data for a row the markup does not draw is gone */
    echTrimToMarkup(m, patch);
    /* ── A HEADING OVER NOTHING GOES TOO (2026-09-22, #127) ─────────────────────────
       Driven on prod after the panel ✕ took every home out: the canvas still drew three
       community headings with no rows under them, and after the picker's clear the fed
       branch above re-named the first heading over an empty table. Jack's own listing
       invariant (I5): a heading with no rows beneath it reads as a bug in a live send.
       Only when the requester EMPTIED the table — every row blank and at least one row
       claimed — so a freshly placed block keeps its two named starter sections. */
    if (!ctx.pending) {
      const after = (k: string) => String(patch[k] !== undefined ? patch[k] : (m.data[k] ?? "")).trim();
      let anyRow = false, anyClaim = false;
      for (let n = 1; n <= 10; n++) {
        if (after("home" + n + "Homesite") || after("home" + n + "Plan")) { anyRow = true; break; }
        if (socIsOff(m, "home" + n + "Homesite") || socIsOff(m, "home" + n + "Plan")) anyClaim = true;
      }
      if (!anyRow && anyClaim) {
        for (const k of Object.keys(echMeta(m.type)?.declared || {})) {
          if (!/^collection[0-9]+Name$/.test(k) || socIsOff(m, k)) continue;
          if (String(m.data[k] ?? "")) patch[k] = ""; else delete patch[k];
        }
      }
    }
  }
  /* ── A HOMESITE CARD'S UNTOUCHED BUTTON READS "VIEW HOME", NOT "VIEW FLOORPLAN" (2026-09-18
     audit) ──────────────────────────────────────────────────────────────────────────────
     Driven on a JAX market send, Listing table → Plan cards at Homes level: five address
     cards ("3958 MID CENTURY ST", "91 Grand Traverse Cir"…) each under a "View Floorplan"
     button. The label is wording, so the fill leaves it alone — but the block's DEFAULT
     wording is the plan module's, and at Homes level that default names the wrong thing.
     Only the block default moves, only to the approved "View Home", only on a card the
     requester has not touched (a taken-over field stays); anything else typed stays put. */
  /* ── AND BACK AGAIN, UNLESS THE REQUESTER CHOSE THE WORDING (2026-09-25) ─────────────
     Measured on prod: a card switched from Homes to Floorplans kept "View Home" next to a
     sibling reading "View Floorplan". The rule now runs both ways — Homes → "View Home",
     Floorplans → "View Floorplan" — and stands down entirely once a label has been picked
     by hand on the section (`__lblSet`, see echSection.ECH_LBL_SET). */
  const lblLvl = /^ECH_Featured/.test(name) && String(m.data.__lblSet ?? "") !== "1" ? echFeatOf(m) : "";
  if (lblLvl === "homes" || lblLvl === "plans") {
    Object.keys(ECH_CTA_PAIRS[name] || {}).forEach((urlKey) => {
      const k = (ECH_CTA_PAIRS[name] || {})[urlKey];
      if (!k || socIsOff(m, k)) return;
      const cur = String((patch as Record<string, unknown>)[k] ?? m.data[k] ?? "").trim();
      if (lblLvl === "homes" && /^view floor ?plan$/i.test(cur)) patch[k] = "View Home";
      if (lblLvl === "plans" && /^view home$/i.test(cur)) patch[k] = "View Floorplan";
    });
  }
  return patch;
}

/** how many communities the table will feature when it has that many to show — the
 *  block's declared header count is a mockup, not a limit (Jack, 2026-08-22) */
export const ECH_GROUP_TARGET = 5;

/** one home row's seven field suffixes (mirrors echRows.ROW_FIELDS; local to avoid the import cycle) */
const ROW_FIELDS_ECH = ["Homesite", "Plan", "SqFt", "Beds", "Baths", "Price", "Url"] as const;

/** the row keys a level change releases — the same family `echPickPatch` un-claims */
const ROW_KEY_RE = /^home[0-9]+(Homesite|Plan|SqFt|Beds|Baths|Price|Url)$/;

/**
 * IS THE ROW THIS KEY BELONGS TO EMPTY? 2026-08-28.
 *
 * The test that separates "the requester DELETED this row" from "the requester
 * TYPED this row". Both look identical in `__socOff` — a claim is just a key — and
 * the difference decides whether a release is housekeeping or data loss. Same
 * Homesite-OR-Plan test the renderer uses to drop a row.
 */
function rowIsEmpty(m: DynBlock, k: string): boolean {
  const mm = /^home([0-9]+)/.exec(k);
  if (!mm) return false;
  const n = mm[1];
  const d = m.data || {};
  return !String(d["home" + n + "Homesite"] ?? "").trim()
    && !String(d["home" + n + "Plan"] ?? "").trim();
}

/* ═══════════════════════════════════════════════════════════════════════════════
   THE LISTING TABLE HAS EXACTLY ONE OWNER FOR "WHICH ROWS EXIST". 2026-08-28.
   ═══════════════════════════════════════════════════════════════════════════════

   Jack, driving the QA build: "selecting homesites doesn't show in the listing
   table… floor plans aren't showing… community name still is messed." Four
   complaints, and three of them are this one fault.

   THE FAULT. Two lists claimed to say which rows the table has, and only one of
   them was ever updated by a pick:

     · `__html` — the `<!-- HOME n -->` markers. `echExpandListingHtml` re-cuts these
       to the pick's shape, and the canvas renders exactly what survives.
     · `home<N>*` data — filled to all ten by the positional per-field loop, and
       truncated only when the pick spanned more than one community.

   So a three-home pick in one community left three markers and ten filled rows.
   The canvas drew 3; the Inspector counted the declared keys and said "10 of 10
   shown", with the picked home appearing twice — once where the pick put it, once
   where the feed's positional fallback had already put it. Measured, not reasoned:
   248/Teller at panel positions 3 and 5.

   AND THE RE-CUT WAS DESTRUCTIVE. `echExpandListingHtml` re-emits the rows it is
   handed; the ones it does not emit are gone from the document and nothing in the
   codebase can synthesise a `<!-- HOME 7 -->` back. A block cut to one row hits the
   `homes.length < 2` guard and is frozen at one row for the rest of its life. That
   is why Homesites → Floorplans → Community came back as a header and nothing: the
   community branch wrote five comparison rows into markup that no longer had five
   rows to draw them in.

   THE RULE, now: the markup is the owner, and the cut always runs against the
   block's PRISTINE markup rather than against last pass's cut. `__html0` is that
   pristine copy, captured the first time the fill sees the block. `echTrimToMarkup`
   is the other half — data for a row the markup does not draw is cleared, so the
   two renderings cannot disagree again. */

/** the block's markup as Layne shipped it, before any pick re-cut it */
export const ECH_HTML_BASE = "__html0";

/**
 * ── THE SPLIT THE REQUESTER ASKED FOR, KEPT ON THE BLOCK (2026-09-15) ────────────
 *
 * "4,6" — how many rows each section owns, in markup order. Written by the panel when
 * a full section borrows a row from a neighbour (Inspector `freeSlotFor`, CHB's
 * "4 and 6 plans"). It has to live on the block because the shape step below re-cuts
 * EVERY pass from the pristine copy, and with no sizes to go on it deals the rows
 * evenly — which is exactly what put the shipped 5/5 back over a hand-built 4/6 one
 * render after the panel had cut it. A pick-driven fill owns the shape outright and
 * clears this; it only speaks when nothing else has.
 */
export const ECH_ROW_SPLIT = "__rowSplit";
export function echRowSplit(m: DynBlock, patch: Record<string, string>): number[] | null {
  const raw = String(patch[ECH_ROW_SPLIT] !== undefined ? patch[ECH_ROW_SPLIT] : ((m.data || {})[ECH_ROW_SPLIT] ?? "")).trim();
  if (!raw) return null;
  const out = raw.split(",").map((x) => Math.floor(Number(x))).filter((n) => Number.isFinite(n) && n > 0);
  return out.length ? out : null;
}

/**
 * The pristine listing markup, capturing it on the first pass that needs it.
 *
 * RE-CAPTURED WHEN THE BLOCK ITSELF GROWS. An SFMC re-pull replaces `__html` with
 * Layne's current markup, which may carry more rows than the copy stored here; a
 * stored base with FEWER rows than the live markup is therefore a stale base, not a
 * cut, and is replaced. The reverse — live markup with fewer rows than the base —
 * is exactly the cut this function exists to undo.
 */
export function echListingBase(m: DynBlock, patch: Record<string, string>): string {
  const cur = String((m.data || {}).__html || "");
  const base = String((m.data || {})[ECH_HTML_BASE] || "");
  if (base && htmlRowNumbers(base).length >= htmlRowNumbers(cur).length) return base;
  if (cur) patch[ECH_HTML_BASE] = cur;
  return cur;
}

/**
 * Re-cut the table to `wanted` sections — ALWAYS from the pristine markup, so the
 * cut can grow as well as shrink. `sizes` is the per-section row count when the
 * caller knows it (a pick does; a feed fill does not).
 */
export function echCutListing(m: DynBlock, patch: Record<string, string>, wanted: number, sizes?: number[]): void {
  const base = echListingBase(m, patch);
  if (!base) return;
  /* ── A CUT IS A MARKUP REFRESH, SO THE ADDED COLLECTIONS RIDE ALONG ───────────
     "+ Add a collection" writes its heading straight into `__html`, and re-cutting
     from the pristine copy would throw it away — the same problem an SFMC markup
     refresh has, and `echTemplates` already answers it the same way. `__extraColls`
     is the record kept for exactly this transplant. */
  const extra = (m.data || {})[EXTRA_COLLS];
  const cut = echExpandListingHtml(base, wanted, sizes);
  const html1 = Array.isArray(extra) ? rowApplyExtraColls(cut, extra) : cut;
  const cur = String((m.data || {}).__html || "");
  if (html1 !== cur) patch.__html = html1;
  else if (patch.__html !== undefined) delete patch.__html;
}

/**
 * WIDEN THE MARKUP TO COVER A ROW THE REQUESTER OWNS. 2026-08-28.
 *
 * The pick-driven cut sizes the table to the picks. "+ Add a home" writes a row and
 * claims it (`fedEdit` → `__socOff`) without touching the pick list, so its row sits
 * past the last marker — visible in the panel, absent from the email. Before the
 * pristine snapshot existed this was unreachable anyway (the cut could only shrink);
 * now the last section simply takes the extra rows it needs.
 *
 * Deliberately claimed-only. A FEED row past the pick must not grow the table — that
 * is the fallback Jack killed on 2026-08-28 ("show only what I picked"), and it is
 * the difference between this and re-introducing the bug the cut exists to fix.
 */
export function echRowsForOwned(m: DynBlock, patch: Record<string, string>): void {
  const val = (k: string) => String(patch[k] !== undefined ? patch[k] : ((m.data || {})[k] ?? "")).trim();
  const rowCap = Math.max(1, Object.keys(echMeta(String(m.type || ""))?.declared || {})
    .filter((k) => /^home[0-9]+Homesite$/.test(k)).length || 10);
  let owned = 0;
  for (let n = 1; n <= rowCap; n++) {
    if (!val("home" + n + "Homesite") && !val("home" + n + "Plan")) continue;
    if (socIsOff(m, "home" + n + "Homesite") || socIsOff(m, "home" + n + "Plan")) owned = n;
  }
  if (!owned) return;
  const cur = String(patch.__html !== undefined ? patch.__html : ((m.data || {}).__html || ""));
  const drawn = htmlRowNumbers(cur);
  if (!drawn.length || drawn.length >= owned) return;   /* already reaches it */
  const spans = echGroupSpans(cur, rowCap).map((s) => s.length).filter((n) => n > 0);
  if (!spans.length) return;
  /* the shortfall goes to the LAST section — the same convention every other deal in
     this file uses, and the section a requester adds into is the one they are in */
  spans[spans.length - 1] += owned - drawn.length;
  echCutListing(m, patch, spans.length, spans);
}

/**
 * THE LAST WORD ON THE ROWS: a row the markup does not draw holds no data.
 *
 * Runs after every other writer, against the markup this pass ends with. Row data
 * for a marker that is not in `__html` is cleared — it can never ship, and leaving
 * it is what let the Inspector list rows the canvas had already dropped, duplicate
 * included.
 *
 * A CLAIMED ROW IS NEVER CLEARED (2026-08-28). `__socOff` means the requester typed
 * that row or added it by hand — "+ Add a home" inside a section, or an inline edit.
 * Deleting it here would make this trim the third mechanism in this file to throw
 * someone's typing away, and it would do it silently. `echRowsForOwned` widens the
 * markup to cover such a row instead, so the two renderings still agree; if the
 * widening cannot reach it the value survives unshipped, which is recoverable where
 * a deletion is not.
 */
export function echTrimToMarkup(m: DynBlock, patch: Record<string, string>): void {
  const html = String(patch.__html !== undefined ? patch.__html : ((m.data || {}).__html || ""));
  const drawn = htmlRowNumbers(html);
  if (!drawn.length) return;               /* no markers to trust — leave the data alone */
  const declared = echMeta(String(m.type || ""))?.declared || {};
  for (const k of Object.keys(declared)) {
    const mm = /^home([0-9]+)(Homesite|Plan|SqFt|Beds|Baths|Price|Url)$/.exec(k);
    if (!mm || drawn.indexOf(Number(mm[1])) > -1) continue;
    if (socIsOff(m, k)) continue;          /* the requester's own row — see above */
    const cur = patch[k] !== undefined ? patch[k] : String((m.data || {})[k] ?? "");
    if (String(cur)) patch[k] = "";
    else if (patch[k] !== undefined && patch[k] === "" && !String((m.data || {})[k] ?? "")) delete patch[k];
  }
}

/**
 * WHERE THE BLOCK PUTS ITS HEADINGS — read off the markup, not guessed. 2026-08-22.
 *
 * Jack, twice: "did I not say to make it more than two collections?" and then, on
 * the same table, "still showing two." Measured in his own four-pick case
 * (Hardwick Farms + Kings Creek + Saddle Oaks + Shearwater, JAX): three parents had
 * live inventory, the fill split ten rows 4/3/3 and wrote three headings — and the
 * email rendered TWO headings, with a Shearwater home sitting under "Saddle Oaks"
 * and Hardwick Farms' single home under "Shearwater". The rows were right; the
 * LABELS were wrong, which is worse than featuring fewer communities.
 *
 * The cause is geometry. ECH_ListingTable's markup interleaves its tokens
 *   collection1Name > home1..home5 > collection2Name > home6..home10
 * so heading 2 is welded to row 6: a group boundary anywhere else cannot be drawn,
 * and an undeclared `collection3Name` is not rendered at all — so that group's rows
 * flow up underneath the previous community's name.
 *
 * So the spans are PARSED from the block's own html — each `collection<N>Name` owns
 * the `home<N>Homesite` tokens that follow it. Today that is 2 × 5. The day Layne
 * ships five headings of two rows this returns 5 × 2 and the fill features five
 * communities with no change here, which is the behaviour Jack asked for, arrived at
 * through the block rather than over the top of it.
 */
export function echGroupSpans(html: string, rowCap: number): number[][] {
  const seq = String(html || "").match(/collection[0-9]+Name|home[0-9]+Homesite/g) || [];
  const spans: number[][] = [];
  let cur: number[] | null = null;
  const seen: Record<string, boolean> = {};
  seq.forEach((tok) => {
    if (seen[tok]) return;
    seen[tok] = true;
    if (/^collection/.test(tok)) { cur = []; spans.push(cur); return; }
    const n = Number((tok.match(/[0-9]+/) || ["0"])[0]);
    if (!n || n > rowCap) return;
    if (!cur) { cur = []; spans.push(cur); }   /* rows before any heading — one unnamed group */
    cur.push(n);
  });
  return spans.filter((g) => g.length);
}

/** the multi-community listing rows — exported for tests, called by echDynPatch */
export function echGroupedListingPatch(m: DynBlock, ctx: EchCtx, patch: Record<string, string>): boolean {
  const withHomes = (ctx.collPacks || []).filter((p) => p && p.C && (p.C.homesites || []).length);
  if (!withHomes.length) return false;
  /* ── AS MANY COMMUNITIES AS THE BLOCK CAN LABEL (Jack, 2026-08-22) ────────────
     "should try to diversify — say there's 5 communities, try to do like 2 each so
     it all fits; if there's 3, focus on evenly spacing them out." So the shape is
     no longer hard-coded at two groups of five: it is READ OFF THE BLOCK. However
     many `collection<N>Name` headers and `home<N>*` rows Layne declares, that is
     how many communities get featured and how many rows each one gets. Two headers
     and ten rows (today) → 2 × 5, exactly as before; five headers and ten rows →
     5 × 2, with no code change here.
     DIVERSITY FIRST: one group per distinct parent community, ranked by live
     inventory, before a second collection of a community already shown. A single
     community's own collections still share the table — that is the Edenbrooke
     case, and right. */
  const meta = echMeta(String(m.type || ""));
  const declared = (meta && meta.declared) || {};
  const countKeys = (re: RegExp): number => Object.keys(declared).filter((k) => re.test(k)).length;
  /* ── THE MOCKUP'S TWO HEADERS ARE NOT A GUARDRAIL (Jack, 2026-08-22) ──────────
     "that is just a mockup — should be able to add more unless there are guardrails
     stating otherwise." The live block declares `collection1Name` and
     `collection2Name` today, but that is the block Layne is still shaping, not a
     rule about how many communities an email may feature. So the fill writes up to
     ECH_GROUP_TARGET headings; the ones the current block does not declare are
     ignored by the renderer and light up the moment it ships them. If the block ever
     declares MORE than the target, the block wins — that IS a stated shape. */
  const declaredGroups = countKeys(/^collection[0-9]+Name$/);
  const groupCap = Math.max(1, declaredGroups > ECH_GROUP_TARGET ? declaredGroups : ECH_GROUP_TARGET);
  const rowCap = Math.max(1, countKeys(/^home[0-9]+Homesite$/) || 10);

  /* ── ONE GROUP PER COMMUNITY (Jack, 2026-08-22: "like one per community") ─────
     A masterplan's 50s and 60s are two SFMC collections but ONE community to a
     reader, and a three-community request was rendering Edenbrooke 50s + Saddle
     Oaks 60s while Hardwick Farms never appeared. So the packs are POOLED by parent
     community first — heading = the community, rows = every collection's homes
     merged, each home keeping its OWN collection code so the advertised homesite
     number still shortens correctly — and the groups are those communities, ranked
     by pooled inventory, ties keeping the order Step 1 named them in. */
  const parentOf = (p: Pack): string => echParentCommunity(p.name || p.C.community?.name || "");
  type GroupRow = { h: HomesiteInfo & { lot?: string }; code: string };
  type Group = { name: string; rows: GroupRow[]; at: number };
  const byParent: Record<string, Group> = {};
  const order: string[] = [];
  withHomes.forEach((p, i) => {
    const par = parentOf(p);
    const key = par.toLowerCase();
    if (!byParent[key]) { byParent[key] = { name: par, rows: [], at: i }; order.push(key); }
    (p.C.homesites || []).forEach((h) => {
      byParent[key].rows.push({ h: h as HomesiteInfo & { lot?: string }, code: p.code });
    });
  });
  /* the pooled list is re-ordered the way a single pack already arrives:
     move-in-ready first, oldest first inside that (services/echOrder) */
  order.forEach((k) => {
    const g = byParent[k];
    const sorted = orderHomesites(g.rows.map((r) => r.h));
    const seen = new Set<HomesiteInfo>();
    g.rows = sorted.map((h) => {
      const hit = g.rows.find((r) => r.h === h && !seen.has(r.h));
      if (hit) seen.add(hit.h);
      return hit as GroupRow;
    }).filter(Boolean);
  });
  const groups: Group[] = order
    .map((k) => byParent[k])
    .sort((a, b) => b.rows.length - a.rows.length || a.at - b.at)
    .slice(0, groupCap);

  const d = m.data || {};
  const set = (k: string, v: string) => {
    if (socIsOff(m, k)) return;
    /* the per-field loop may have written an authoritative-empty for this key
       (a masterplan's own pack has no homes) — the grouped answer replaces it,
       and a value the data already holds must also CLEAR the loop's entry or
       the two writers oscillate forever */
    if (String(d[k] ?? "") !== v) patch[k] = v; else delete patch[k];
  };

  /* ── THE ROWS DIVIDE BY THE COMMUNITIES ACTUALLY SHOWN (Jack, 2026-08-22) ─────
     "say there's 5 communities, try to do like 2 each so it all fits; if there's 3,
     focus on evenly spacing them out." So the split is rowCap ÷ groups, remainder to
     the earlier groups: one community takes all ten rows, two take 5/5, three take
     4/3/3, five take 2 each. Headings are written for every group shown — the block
     ignores the ones it does not declare yet, and lights them up when it does. */
  /* ── THE BLOCK GROWS A SECTION PER COMMUNITY (Jack, 2026-08-22) ───────────────
     "why can't you add a 3rd collection/section like I've been asking" — because
     every earlier pass treated Layne's two heading rows as the ceiling. His ruling:
     "that is just a mockup … not your role to be concerned." So when the request has
     more communities than the markup draws headings for, the table is RE-CUT — her
     heading row cloned and her ten rows dealt across the sections (services/echExpand).
     The row count is still hers: ten rows means at most ten sections, and three
     communities means 4/3/3. */
  const wanted = Math.max(1, Math.min(groups.length, groupCap));
  /* from the PRISTINE markup, so a feed fill can widen a table a pick narrowed */
  echCutListing(m, patch, wanted);
  const html1 = String(patch.__html !== undefined ? patch.__html : ((m.data || {}).__html || ""));
  const spans = echGroupSpans(html1, rowCap);
  const rowsOf: number[][] = spans.length ? spans.slice(0, groupCap) : (() => {
    /* no markup to read (a bare instance, or a unit test) — fall back to an even split */
    const G0 = Math.max(1, Math.min(groups.length, groupCap));
    const base = Math.floor(rowCap / G0), rem = rowCap - base * G0;
    const out: number[][] = [];
    for (let g = 0, at = 1; g < G0; g++) {
      const n = base + (g < rem ? 1 : 0);
      out.push(Array.from({ length: n }, (_, i) => at + i));
      at += n;
    }
    return out;
  })();
  /* a group with no heading of its own would render under the PREVIOUS community's
     name, so the number of communities featured is the number of headings available */
  const G = Math.max(1, Math.min(groups.length, rowsOf.length));

  const lots: Record<string, string> = {};
  let cleared = 0;
  for (let g = 0; g < groupCap; g++) {
    const grp = g < G ? groups[g] : undefined;
    /* a heading only names a group that exists; the rest go blank and the renderer
       drops their row, which is what lets the rows above flow on */
    set("collection" + (g + 1) + "Name", grp ? grp.name : "");
    if (!grp) continue;
    const rows = rowsOf[g] || [];
    /* the pooled homes arrive move-in-ready first and oldest-first inside that
       (services/echOrder), so slicing the top of the list IS the serving order */
    for (let r = 0; r < rows.length; r++) {
      const n = rows[r];
      if (n > rowCap) break;
      const cell = grp.rows[r];
      const row = cell ? cell.h : undefined;
      set("home" + n + "Homesite", row ? echShortLot(row.lot, cell.code) : "");
      set("home" + n + "Plan", row ? String(row.plan || "") : "");
      set("home" + n + "SqFt", row ? echSqft(row.sqft) : "");
      set("home" + n + "Beds", row ? echBeds(row.beds) : "");
      set("home" + n + "Baths", row ? echBaths(row.baths) : "");
      set("home" + n + "Price", row ? echMoney(row.price) : "");
      set("home" + n + "Url", row ? String(row.url || "") : "");
      if (row && row.lot) lots[String(n)] = String(row.lot);
      if (row) cleared = Math.max(cleared, n);
    }
  }
  /* any row past the last group's span is cleared rather than left holding sample copy */
  for (let n = cleared + 1; n <= rowCap; n++) ROW_FIELDS_ECH.forEach((sfx) => set("home" + n + sfx, ""));

  const j = JSON.stringify(lots);
  if (String(d.__lots || "") !== j && (Object.keys(lots).length || d.__lots)) patch.__lots = j;
  /* …and clear the POSITIONAL capture's stale entry when the data is already right —
     at community level (a masterplan) both writers run, and the positional one reads
     the masterplan's own empty pack (same oscillation the row `set()` guards) */
  else if (patch.__lots !== undefined) delete patch.__lots;
  /* this writer cut the shape above — the caller must not re-cut it (see echDynPatch) */
  return true;
}

/**
 * ONE PICK FROM THE STEPPED WIZARD → ONE PATCH FOR AN ECH BLOCK. 2026-08-20.
 *
 * THE FAULT THIS CLOSES (the second of three). `Inspector.tsx`'s `liveFill`
 * callback (L1082–1091) writes `{items}` for the legacy featured block and
 * `{rows, __title, __cols}` for the legacy listing table — the keys those two
 * hand-written RENDERERS consume (registry-extended.ts:375 / :417). An ECH block
 * has no renderer of ours at all: it emits LAYNE'S OWN MARKUP with its declared
 * tokens substituted. So a pick on ECH_Featured1CTA has to land on `PlanName` /
 * `PlanImage` / `PlanImageAlt` / `SqFt` / `Beds` / `Baths` / `FloorplanUrl`, and a
 * pick on ECH_ListingTable on the `home<N>*` family. `items` would be inert data
 * on a block that never reads it — the pick would appear to work and change
 * nothing, which is exactly the symptom Jack reported.
 *
 * IT REUSES `echDynPatch`, IT DOES NOT MAP AGAIN. There is already exactly one
 * place that knows which of Layne's fields a plan or a home fills — `ECH_DYN` plus
 * `echDynPatch` above. A second mapper here would be a second thing to keep in
 * step with Content Builder, and the first field Layne renamed would break the one
 * nobody remembered. So this writes the pick into a CLONE of the block and asks
 * `echDynPatch` the same question it always answers.
 *
 * WHY A CLONE AND A SUBSTITUTED DOCUMENT. React owns block data; mutating `m.data`
 * to make the fill see the pick would be a state write behind React's back. And
 * `echPlanIdx` compares by REFERENCE (`b === m`, L609), so handing it the clone
 * against the original array would silently make every Featured block look like
 * the first one — a second Featured block with no pick of its own would revert to
 * plan 1 the moment its neighbour was picked. The clone therefore stands in the
 * document in the block's own place.
 *
 * AN EMPTY PICK IS A REAL ANSWER, NOT A NO-OP. A wizard run that ends with nothing
 * chosen at that level CLEARS the stored pick, so the selected-cards panel and the
 * canvas cannot end up disagreeing about which floorplan this block shows.
 */
export function echPickPatch(
  m: DynBlock,
  ctx: EchCtx,
  doc: DynBlock[],
  picks: { planPick?: string; homePick?: string; featLevel?: string },
): Record<string, string> {
  const name = echNameOf(m.type);
  if (!name) return {};
  const keys: Record<string, string> = {};
  /**
   * THE LEVEL RIDES IN THE SAME PATCH AS THE PICK (2026-08-20).
   *
   * It has to: the clone below is what `echDynPatch` reads the level off, so writing
   * the level in a second `onData` would fill the block at the OLD level first and
   * only correct itself on the next render — one frame of a floorplan on a card the
   * requester just switched to Homes, and a real half-applied state if anything threw
   * in between. One patch, one state update, no window where the stored level and the
   * filled fields disagree.
   *
   * `undefined` leaves the block where it is, exactly like the two pick keys — that is
   * what a caller with no level to report (the Listing table, which has only one) says.
   * An unreadable value is not stored blindly either: `echFeatOf` refuses it on the way
   * back out and falls back, so the worst a bad write can do is nothing.
   */
  if (picks.featLevel !== undefined) keys[ECH_FEAT_KEY] = picks.featLevel;
  if (picks.planPick !== undefined) keys[ECH_PLAN_PICK] = picks.planPick;
  if (picks.homePick !== undefined) keys[ECH_HOME_PICK] = picks.homePick;

  /* ── CHANGING LEVEL DROPS THE OTHER LEVEL'S PICK (2026-08-28) ────────────────
     Measured: Homesites → Floorplans → Community, Done, and the table came back a
     header row and nothing. Community level writes no pick keys at all — its
     `echFeatPickKind` is "", so both ternaries in the caller pass `undefined`, which
     means "leave alone" — so the Homesites pick was still sitting on the block. The
     fill then read it and took the pick-driven branch (`__homePick` non-empty),
     which is gated OFF for the comparison rows the requester had just asked for.
     Nothing anywhere cleared a pick on a level change; `echFeatRestore` restores
     declared defaults but is registered for the Featured blocks only.

     A pick belongs to the level it was made at. Moving the level therefore drops the
     picks the new level cannot read, releases their row claims (a claim exists to
     stop the feed refilling a row the requester emptied — it is not a reason to hold
     the OLD level's rows empty), and drops the send-time lot map, which reprices a
     comparison row as if it were a homesite. An explicit pick in the same patch
     always wins: these only fire where the caller passed `undefined`. */
  const lvlWas = String(m.data[ECH_FEAT_KEY] ?? "").trim();
  const lvlNow = picks.featLevel === undefined ? lvlWas : String(picks.featLevel).trim();
  const lvlChanged = !!lvlWas && lvlNow !== lvlWas;
  if (lvlChanged) {
    /* ── THE PICKS STAY (2026-08-28, corrected the same day) ────────────────────
       The first version of this cleared `__homePick` and `__planPick`, because a
       retained home pick was what stopped the community fill running. That gate is
       now level-aware (`homePickHere` in echDynPatch), so the picks can stay — and
       they MUST, because Jack asked for the opposite of clearing them: "yes but if
       i toggle back they should show again" (#69, 2026-08-27). Each level keeps its
       own selection and refills from it on return.

       ── AND ONLY AN EMPTY ROW'S CLAIM IS RELEASED ──────────────────────────────
       This released EVERY row claim, which ate any row the requester had typed by
       hand — caught by the combination matrix, three cases red ("claimed row
       survives a switch to comms / homes / plans"). It also defeated the trim fix
       from earlier the same day, which skips claimed rows: strip the claim first
       and there is nothing left to skip.
       A claim on an EMPTY row is a deletion decision that belonged to the old
       level, so it goes. A claim on a row with CONTENT is the requester's typing,
       and nothing here is entitled to it. */
    const wasClaimed = String(m.data.__socOff || "").split(",").filter(Boolean);
    const held = wasClaimed.filter((k) => !(ROW_KEY_RE.test(k) && rowIsEmpty(m, k)));
    if (held.join(",") !== wasClaimed.join(",")) keys.__socOff = held.join(",");
    /* the send-time lot map is per-level: a comparison row is not a homesite */
    if (String(m.data.__lots ?? "").trim()) keys.__lots = "";
  }
  /* ── CHOOSING FROM INVENTORY RELEASES THE ROWS (2026-08-22) ───────────────────
     A deleted row keeps its claim so the feed cannot put the home back (Jack:
     "can't delete homes, you keep re-adding them"). But "Choose homes from
     inventory" is the requester ASKING to be refilled, and without this the
     claimed slots stayed blank and the picker looked broken — the same defect one
     turn later, in the opposite direction. Found by a risk probe before it shipped.
     Only the ROW keys are released; a claimed heading or a typed price on another
     block is untouched. */
  /* ── …BUT ONLY THE ROWS THE REQUESTER EMPTIED (2026-08-28) ────────────────────
     This released EVERY row claim on any write to `homePick`, which is right for
     the claims it was written for — slots the requester had ✕'d, which otherwise
     stay stubbornly blank when they ask to be refilled — and wrong for a row they
     TYPED. Both are just keys in `__socOff`; only the row's content tells them
     apart.
     Found by the combination matrix, traced with `echClaimTrace`: seeding a
     hand-typed row 5 and then picking two homes left `__socOff` empty and row 5
     blank at stage 1, before any level change and before any fill. The same path
     serves "+ Add a home", so adding ONE home silently wiped every hand-typed row
     in the table — a bigger defect than the level-switch case that led here, and
     older than any of this work. */
  if (picks.homePick !== undefined) {
    const was = String(m.data.__socOff || "").split(",").filter(Boolean);
    const kept = was.filter((k) => !(ROW_KEY_RE.test(k) && rowIsEmpty(m, k)));
    if (kept.join(",") !== was.join(",")) keys.__socOff = kept.join(",");
  }
  /* ── PICKING A DIFFERENT PLAN MAKES THE CARD THAT PLAN — TYPED NAME INCLUDED (2026-09-15)
     Driven on dev: the requester renamed a Featured card on the canvas ("Halle II Canvas
     Edit"), then chose Edison from inventory. The image and the three specs became
     Edison's; the name stayed "Halle II Canvas Edit", because the inline edit had
     claimed `PlanName` and the fill honours claims. Two renderings of one card
     disagreeing — a hand edit is protected against the FEED, not against the requester
     asking for another plan. So a plan pick that CHANGES releases the claims on this
     card's `plan.self.*` fields (name, specs, photo, plan link); a re-pick of the same
     plan, and every other claim on the block, are untouched. The table's rows read
     `plan.<n>`, not `plan.self`, so this never touches them. */
  const planWas = String(m.data[ECH_PLAN_PICK] ?? "").trim();
  if (picks.planPick !== undefined && String(picks.planPick).trim() && String(picks.planPick).trim() !== planWas) {
    const lvl = String(keys[ECH_FEAT_KEY] ?? m.data[ECH_FEAT_KEY] ?? "").trim();
    const map = echDynFor(name, (lvl === "plans" || lvl === "homes" || lvl === "comms" ? lvl : echFeatOf(m)) as EchFeat) || {};
    const selfKeys = Object.keys(map).filter((k) => /^plan\.self\./.test(echSrcOf(map[k]).src));
    if (selfKeys.length) {
      const was = String(keys.__socOff !== undefined ? keys.__socOff : (m.data.__socOff || "")).split(",").filter(Boolean);
      const kept = was.filter((k) => selfKeys.indexOf(k) < 0);
      if (kept.join(",") !== was.join(",")) keys.__socOff = kept.join(",");
    }
  }
  const clone: DynBlock = { ...m, data: { ...m.data, ...keys } };
  const doc2 = doc.map((b) => (b.id === m.id ? clone : b));
  const patch = echDynPatch(clone, name, ctx, doc2);

  /* ── DROPPING THE LAST PICK PUTS THE TEMPLATE'S OWN CONTENT BACK (2026-08-20) ────
     THE DEFECT, caught in Chrome on the finished build: Step 1 on Market with no
     community, pick Blakely Manor → Hopewell (canvas correctly reads "Hopewell · 4069
     sq ft · 5 beds"), then click the tile's ×. The pane went to "· 0 selected" and the
     CANVAS STAYED ON HOPEWELL — two renderings of one pick disagreeing, for the fourth
     time in this feature.
     WHY. With the pick gone, `echBlockLevel` has nothing to read a community off, so
     the level falls back to Step 1's — "market" — and every `plan.self.*` entry
     declares `levels:["community"]`, so `echDynPatch` SKIPS all seven fields and the
     removed plan's values simply stay. Blanking is not available either: the block's
     own guardrail says the spec lines are locked, so an empty one is a visibly broken
     card. The declared DEFAULT is the one value that is neither stale nor invented —
     the same answer, and the same reasoning, as `echFeatRestore` one axis over.
     NARROW ON PURPOSE. Only on an actual clear (non-empty → empty), only for fields
     the level cannot source, only where the fill had no answer of its own, and never
     over a field the requester took over. At COMMUNITY scope `echSrcOk` is true, the
     fill writes the positional plan, and this whole branch is inert — which is the
     pre-existing behaviour it must not disturb. */
  const cleared = Object.keys(keys).some((k) => (
    (k === ECH_PLAN_PICK || k === ECH_HOME_PICK)
    && !String(keys[k] || "").trim()
    && !!String(m.data[k] == null ? "" : m.data[k]).trim()));
  if (cleared) {
    const lvl = echBlockLevel(clone, ctx);
    const map = echDynFor(name, echFeatOf(clone)) || {};
    const declared = echMeta(m.type)?.declared || {};
    for (const k of Object.keys(map)) {
      if (patch[k] !== undefined) continue;      /* the fill had a real answer */
      if (socIsOff(clone, k)) continue;          /* the requester took this field over */
      /* ── NEVER PUT THE SAMPLE ROWS BACK (2026-08-28, widened 2026-09-22) ─────────
         The clear above is also what a level change looks like, and a listing
         row's declared default is Layne's placeholder — "101 / Plan Name /
         $399,990". Restoring those is right for a Featured card's locked spec line
         and flatly wrong for a table row, which ships empty and is dropped.
         This used to fire only on a level CHANGE. Pilot #127 (Kailey White, NEV,
         2026-09-21, "when you remove all the homes from the listing table, it
         crashes"): driven on prod 09-22 — Market focus, Plan cards → Listing table,
         Choose homes → deselect every home → Done — and the table came back as TEN
         placeholder rows, panel and canvas alike. Same level, so this guard was
         skipped; at Market scope `echSrcOk` is false for every `home<n>` source, so
         all seventy row fields took their sample defaults. A row never restores. */
      if (ROW_KEY_RE.test(k)) continue;
      /* …unless the level sources it but the SCOPE cannot answer it — a cleared plan
         card with no Step-1 community. `echSrcOk` went true for `plan.*` at every level
         on 2026-08-24, which silently made this whole branch inert exactly where it was
         doing its job, so the declared default is restored here too. */
      if (echSrcOk(map[k], lvl) && !(echPlanDry(clone, ctx, doc2) && /^plan\./.test(echSrcOf(map[k]).src))) continue;
      const def = String(declared[k]?.default ?? "");
      if (String(m.data[k] == null ? "" : m.data[k]) !== def) patch[k] = def;
    }
  }

  /* ── CLEARING THE LAST HOME EMPTIES THE TABLE, AND IT STAYS EMPTY (2026-09-22, #127) ──
     The picker's Done with nothing selected writes `homePick: ""`. Before this, that
     clear left the rows to whatever the fill answered: at Market scope nothing (the
     sample rows came back through the branch above), at Community scope the
     POSITIONAL fallback — the feed's first ten homes, i.e. exactly the homes the
     requester had just removed. Neither is "no homes".
     So an explicit clear on a block with home rows does what the row ✕ does, for
     every row at once: blank the row and CLAIM it (`__socOff`), so the next
     document-wide fill pass leaves the table empty; the section headings the grouped
     fill wrote go too, and the send-time lot map with them. A row the requester typed
     by hand is already claimed and is left alone — the picker replaces PICKS, not
     typing. Choosing homes again releases the empty rows' claims (above), so the
     table refills on the next pick. */
  const homeCleared = keys[ECH_HOME_PICK] !== undefined
    && !String(keys[ECH_HOME_PICK]).trim()
    && !!String(m.data[ECH_HOME_PICK] ?? "").trim();
  const declaredAll = echMeta(m.type)?.declared || {};
  if (homeCleared && declaredAll.home1Homesite) {
    const claimed = String(keys.__socOff !== undefined ? keys.__socOff : (m.data.__socOff || "")).split(",").filter(Boolean);
    for (const k of Object.keys(declaredAll)) {
      const isRow = ROW_KEY_RE.test(k);
      if (!isRow && !/^collection[0-9]+Name$/.test(k)) continue;
      if (socIsOff(clone, k) && !rowIsEmpty(clone, k) && isRow) continue;   /* typed by hand — theirs */
      if (String(m.data[k] ?? "")) patch[k] = ""; else delete patch[k];
      if (isRow && claimed.indexOf(k) < 0) claimed.push(k);
    }
    keys.__socOff = claimed.join(",");
    if (String(m.data.__lots ?? "").trim()) patch.__lots = "";
  }

  /* the pick keys ride along in the same patch, so the block's choice and the
     fields it produced land in ONE state update and can never half-apply */
  for (const k of Object.keys(keys)) if (m.data[k] !== keys[k]) patch[k] = keys[k];
  return patch;
}

/**
 * THE WHOLE DOCUMENT, not just the open panel (audit row 19).
 *
 * Walks every block and fills the dynamic ones, so picking the initiative /
 * community on Step 1 populates the email immediately. Returns one patch per
 * block that changed, keyed by block id — the caller applies them in a single
 * state update.
 *
 * The division's own Contact Us markup swap (`/sfmc-isc`) rides along here, as it
 * does in the monolith: it is an `__html` concern rather than a field fill, but it
 * is driven by exactly the same Step-1 change and has to land in the same pass or
 * the canvas shows the generic cream fallback until something else re-renders.
 */
export function echFillAll(doc: DynBlock[], ctx: EchCtx): Record<string, Record<string, string>> {
  const out: Record<string, Record<string, string>> = {};
  for (const m of doc) {
    const name = echNameOf(m.type);
    if (!name) continue;                 /* not an ECH block */
    /* ── the division's own Contact Us block replaces the generic one, ISC photo
       and all (monolith L11796–11808). Takes this branch and no other, exactly as
       the monolith `return`s here — a Contact Us block WITHOUT a data source has no
       ECH_DYN entry, so there is no field fill to fall through to anyway.
       ── A CONTACT US BLOCK THAT DECLARES A DATA SOURCE FALLS THROUGH (2026-09-02) ──
       `echIscPatch` already stands down for it (returns null), but this `continue`
       was still skipping the field fill below — so the declared `de:` feed, correct in
       every unit test, never ran on the one block that declares it. The same shape
       as the pattern fix of 2026-09-01: right function, wrong call path. The block
       now takes the ordinary fed-block route, and its image treatment is applied in
       `echContactPatch` right after the fields land. */
    if (ECH_ISC_RE.test(name) && !echDataSource(name)) {
      const p = echIscPatch(m, ctx);
      if (p) out[m.id] = p;
      continue;
    }
    /* ── AMENITY BLOCKS PRESELECT FROM THE COMMUNITY (audit row 17, monolith
       L11798–11811) ────────────────────────────────────────────────────────────
       Only when the user hasn't chosen for themselves: `__amenTouched` is set by
       the picker, so a hand-picked set is never overwritten by a later refill —
       the same ownership idea as `__socOff` on a URL. Amenities are per-community,
       so a multi-community or market/division send has no correct answer and the
       block's own defaults are left alone. Like the monolith, an amenity block
       takes THIS branch and no other: it never falls through to the field fill. */
    const amPairs = amenPairs(echMeta(m.type)?.fields);
    if (amPairs.length) {
      /* SCOPE: EXACTLY ONE COMMUNITY, AND THE GATE BELONGS IN THE `if` (2026-08-21).
         Amenities are per-community, so a market, division or multi-community send has
         no correct answer — and the state before any community is picked is "not known
         yet", not "none". Both keep the block's own defaults; only a community that
         STATES none gets blanked.
         ⚠️ This gate has now been reverted TWICE by a concurrent session committing a
         shared working tree that still held an early draft without it. If it is missing
         again, `echDyn.test.ts`'s "a multi-community send is LEFT ALONE" is the test
         that catches it — do not "fix" that test, restore this line. */
      if (!m.data.__amenTouched && ctx.picks.length === 1) {
        const lib = echAmenLib();
        /* the floor is tested on what the COMMUNITY claims, before the block's cell
           count trims it — monolith `amen.length>=AMEN_MIN` then `.slice()` */
        const amen = amenFromAirtable(ctx.picks[0].amenities, lib);
        /* BLANK, NOT THE BLOCK'S PLACEHOLDERS (2026-08-21, Jack: "if no amenities
           leave as blank"). A community whose Airtable amenities are None, TBD, empty
           or unmappable — 990 selections in the live feed say None or TBD alone — used
           to keep the block's shipped placeholders, so the email went out with generic
           icons under the literal word "Amenity". Same for a community mapping to
           fewer than AMEN_MIN, since one icon in a grid reads as a mistake. `amenTrimHtml` already drops an empty
           cell and removes a row left with nothing, so blanking the data is all
           that is needed for the icons to disappear cleanly. The block's own
           "Amenities" bar is left alone; a division that wants no amenity section
           deletes the block. */
        const want = amen.length >= AMEN_MIN ? amen.slice(0, amPairs.length) : [];
        const cur = amenPicked(m.data, amPairs).map((x) => x.label).join("|");
        if (cur !== want.join("|")) {
          const full = amenApply(amPairs, lib, want);
          const patch: Record<string, string> = {};
          for (const k of Object.keys(full)) if (m.data[k] !== full[k]) patch[k] = full[k];
          if (Object.keys(patch).length) out[m.id] = patch;
        }
      }
      continue;
    }
    if (!echDynFor(name)) continue;      /* no feed behind it */
    if (!echDynOn(m)) continue;          /* Manual blocks are left alone */
    const patch = echDynPatch(m, name, ctx, doc);
    if (ECH_ISC_RE.test(name)) echContactPatch(m, ctx, patch);
    if (Object.keys(patch).length) out[m.id] = patch;
  }
  return out;
}

/** the block's declared field map (with `fromColumn`), or null — what `contactLookup` resolves through first */
function echDeclaredFields(name: string): Record<string, { fromColumn?: string }> | null {
  const meta = echByName(name);
  const f = meta && meta.full && meta.full.fields;
  return f ? (f as Record<string, { fromColumn?: string }>) : null;
}

/**
 * The DE row behind a data-source block, for the current division — `null`/`undefined`
 * as `ctx.deRows` has it.
 *
 * A FAILED LOOKUP IS REPORTED AS `undefined`, NOT `null` (2026-09-09). Callers treat
 * `undefined` as "the route has not answered, write nothing" and `null` as "answered,
 * no row for this division", and a failed fetch belongs to the first group: nothing is
 * known, so nothing should be decided. Collapsing it into `null` is what made an
 * outage look like 45 divisions with no Contact Us row.
 */
function echDeRowFor(name: string, ctx: EchCtx): Record<string, string> | null | undefined {
  const ds = echDataSource(name);
  if (!ds || !ctx.deRows) return undefined;
  const r = ctx.deRows[ds.externalKey];
  return deFailed(r) ? undefined : r;
}

/**
 * Apply Layne's image treatment to a data-source Contact Us block, on top of the field
 * patch the ordinary fill just produced (2026-09-02, MARKUP-CONTACT-US-TREATMENTS).
 *
 * Reads the values the block WILL hold — `m.data` with `patch` over it — so the slots
 * drawn match the fields the same pass wrote. Always re-cut from the pristine markup
 * (`__html0`, captured on first sight exactly as the listing table does), so a change
 * of treatment or division re-renders cleanly and a lazy `</table>` never closes on a
 * fragment this function itself inserted. Writes `__html` only when it differs, so a
 * settled block patches nothing and the fill effect cannot loop.
 *
 * WHILE THE ROW IS IN FLIGHT NOTHING IS WRITTEN: `deRows[key]` undefined means the
 * route has not answered, and cutting on a half-answered lookup would draw the icon,
 * then the headshots, on consecutive renders. `null` — no row for this division — is a
 * real answer and renders as `none`.
 */
export function echContactPatch(m: DynBlock, ctx: EchCtx, patch: Record<string, string>): void {
  const name = echNameOf(m.type);
  if (!echDataSource(name)) return;
  const row = echDeRowFor(name, ctx);
  if (row === undefined) return;
  const cur = String((m.data || {}).__html || "");
  if (!cur) return;
  /* an UNMARKED `__html` is pristine — first sight, or a fresher SFMC re-pull that
     `echRebuildKeepEdits` dropped in over the old cut — and becomes the base; a marked
     one is this function's own output and the stored base stands */
  let base = String((m.data || {})[ECH_HTML_BASE] || "");
  if (!contactTreated(cur) && cur !== base) { base = cur; patch[ECH_HTML_BASE] = cur; }
  if (!base) return;
  const data = { ...(m.data as Record<string, unknown>), ...patch };
  /* the requester's photo and button choices (2026-09-25) ride over the row's answers —
     see echContactChoices; with none made this is the row lookup unchanged */
  const get = cuLookup(data, contactLookup(data, row, echDeclaredFields(name)));
  const want = contactTreatmentHtml(base, get);
  if (want !== cur) patch.__html = want;
}

/**
 * Where a record-filled Contact Us block stands for the panel — does the division have
 * photos, what are its two buttons — or null when the block is not one, or the row has
 * not answered yet (nothing is known, so the panel offers nothing).
 */
export function echContactChoiceState(m: DynBlock, ctx: EchCtx | null | undefined): CuState | null {
  const name = echNameOf(m.type);
  if (!name || !ECH_ISC_RE.test(name) || !echDataSource(name) || !ctx) return null;
  const row = echDeRowFor(name, ctx);
  if (row === undefined) return null;
  const data = m.data as Record<string, unknown>;
  return cuState(data, contactLookup(data, row, echDeclaredFields(name)));
}

/**
 * The alt-text gaps every data-source Contact Us block in the document has, as
 * warnings for the Studio's block-wide list — Layne: "surface '3 headshots missing
 * alt text' in the block-wide warnings rather than holding the image back". A count
 * and a sentence, never a block on sending.
 */
export function echContactWarnings(doc: DynBlock[], ctx: EchCtx): { module: string; field: string; label: string; message: string }[] {
  const out: { module: string; field: string; label: string; message: string }[] = [];
  for (const m of doc) {
    const name = echNameOf(m.type);
    if (!name || !ECH_ISC_RE.test(name) || !echDataSource(name)) continue;
    const row = echDeRowFor(name, ctx);
    if (row === undefined) continue;
    const get = cuLookup(m.data as Record<string, unknown>, contactLookup(m.data as Record<string, unknown>, row, echDeclaredFields(name)));
    for (const msg of contactAltGaps(get)) out.push({ module: m.type, field: "alt", label: "Contact Us", message: msg });
  }
  return out;
}
